import { Formatters } from '../shared/lib/formatters';
import { LoadingSpinner } from '../shared/components';
import { emptyState } from '../shared/components/Narrative';
import { icon } from '../shared/components/icons';
import { openWoQuickView, WoQuickView } from '../shared/components/WoQuickView';
import { kebabHtml, bindKebabs, closeKebab, type KebabItem } from '../shared/components/Kebab';
import { stateLabel } from '../../../lib/guestRecovery.mjs';
import { followWarning } from '../../../lib/inspFollow.mjs';   // R114 review: say so when the inspection couldn't follow
import { arrivalsHandoff, STORE_KEYS as RT_KEYS } from '../../../lib/arrivalsDayView.mjs';
import {
  addDays as calAddDays, diffDays as calDiffDays, validDate as calValidDate, assignLanes, packLanes, windowQuery, windowColumns, navStep, earlierStep, canEarlier,
  jumpWindow, anchorColumn, resolveView, defaultViewKey, legacyFilterToStat, parseFocus, coreStatCounts, layerKey, forwardStart, FOCUS_KEY, FOCUS_EVENT,
  type CalFocus, type CoreStatCounts,
} from '../../../lib/calendarCore.mjs';
import type { AddForm, ArrivalCheck, Block, BlockInfo, CalData, Chan, CheckItem, Detail, Dismissed, EciLco, EciLcoFlag, EciLcoSide, Impossible, ItemState, Opp, PopConfirm, Property, Review, Stay, TurnCleaner, TurnDetail, TurnEv, TurnPop, WoEv } from './calendar/types';
import {
  registerLayer, registerStat, registerInspector, registerSection, registerAction, registerChipKind,
  getLayer, allLayers, getStat, getRail, getInspector, sectionsFor, actionsFor, getChipKind, getView, viewsFor, anyBusy, allCss, splitActions,
  type CalendarCtx, type EntityRef, type ChipSpec, type BadgeSpec, type Layer, type StatSpec, type StatValue, type RailItem, type RailProvider, type Inspector, type Section, type Action,
  type DateCell, type View, type LayerState, type PostResult, type ChipKind, type StayDecoration,
} from './calendar/registry';
import { registerPresets } from './calendar/presets';
import { normalizeCal, normTurn, normArrivalCheck, normalizeDetail, initials as initialsOf, bizToday as bizTodayL, DONE_STATUSES, ITEM_STATES, type CalWindowInfo } from './calendar/normalize';
import type { StayInspectorData, BlockInspectorData } from './calendar/contract-ext';
import { HUB_CSS } from './calendar/hub-css';
// R115: feature modules (guest / maint / mtr / ops) self-register on import —
// AFTER the registry module and BEFORE the page boots, so every core
// registration below is "only when that key is still free" (a feature that
// replaces a core kind — maint's 'wo' editor, guest's 'message' action — wins).
import './calendar/features';

// R110 #1 — Calendar (stays timeline). Jesse, on HostBuddy's calendar: "I love
// this calendar view… exactly the sort of thing I'm looking to create on
// dash." Every reservation across the portfolio on one horizontal timeline
// (hotel style: a bar runs from mid check-in day to mid checkout day), a
// sticky property column with a % booked meter, and a right panel per stay:
// the guest, how they feel (the classified message ticket) and what we could
// offer them (early check-in / gap nights after / late checkout).
// Data comes from /api/stays (server/stays.js) — lane stacking, geometry and
// the local search debounce are the only things computed here.
//
// R111 (Jesse's "Calendar features" list): filter chips, an Ops lane per
// property (housekeeping turns + scheduled work orders, drag-to-day),
// negative-experience styling, the review status + review text, the Arrivals
// verification rollup, clickable blocks, a collapsed Details section and
// "+ Add" (hold nights / manual reservation — the one thing here that WRITES
// to Hostaway, behind a summary + confirm step). Every new payload field is
// optional: an older server still renders.
//
// R112 (contract r112/calendar_contract2.md): the turn chip carries its
// cleaners (initials discs), an at-risk ring, photo / moved badges and is
// draggable to another day; the turn view (GET /api/stays/turn/:id) has
// cleaner selects (assign, with the capacity 409 handshake inline), a
// clean-day input (move) and "Open in Housekeeping"; inquiries that can no
// longer happen (or were dismissed) are hidden with a "show hidden" toggle and
// a panel reason + Dismiss / Restore (6 s undo); the stay panel has the
// Arrival checklist and ECI / LCO switch rows.
//
// R115 (Jesse: "if we aren't smart about how we implement all these features,
// we're going to overwhelm folks"): the Calendar becomes the HUB — ONE
// timeline with five VIEWS (presets over shared LAYERS, calendar/presets.ts),
// ONE inspector panel for every entity with a back stack, ONE create chooser,
// a "📥 To do" worklist rail. Feature builders register through
// calendar/registry.ts (CALENDAR_CONTRACT.md); this file is the shell that
// implements CalendarCtx and draws what is registered:
//   · View picker (desktop seg-tabs, Operations ▾ shortcuts; phone "View ▾"
//     bottom sheet + "Make this my default"); per tab (sessionStorage
//     stc_view), cross-session only by the explicit default
//     (localStorage stc_default_view:<uid>); a deep link's view is transient.
//   · "Show ▾" replaces the legend: one row per layer (swatch = legend, count,
//     On / Dim / Off), per-view overrides in sessionStorage stc_layers:<view>.
//   · Stats = KPI cards that are the ONE active filter (stc_stat:<view>).
//   · B9: the past is lazy — "‹ Earlier" refetches with back=30 keeping the
//     visual position; ‹ › scroll a week inside the loaded range and fetch
//     only at its edge.
//   · Inspector: push layout ≥1200px, overlay 900–1199, full sheet <900.
//   · Phone: one toolbar row, comfortable geometry on pointer:coarse.

const API = '/api/stays';
const WO_API = '/api/assetops/work-orders';
const TURN_API = '/api/assetops/turns';
const ECI_LCO_API = '/api/assetops/eci-lco';
const RT_API = '/api/restracker';
const TURN_HORIZON_DAYS = 7;   // the server flags `unassigned` only for clean days today..today+7
const UNDO_MS = 6000;
// Geometry. R115: on a coarse pointer (phones / tablets) the comfortable set —
// JS values, not CSS: they feed inline geometry, row heights and click / drop math.
const COL_W = 64, LANE_H = 40, OPS_H = 24;          // px per day column / stacked lane / ops sub-lane
const COL_W_COARSE = 56, LANE_H_COARSE = 44, OPS_H_COARSE = 32;
const ROW_PAD = 8;         // px above/below the lanes in a property row
const MIN_ROW_H = 64;      // a row never gets shorter than its label needs
const MAX_NIGHTS = 90;     // the server refuses longer holds / manual reservations
const WINS = [14, 28, 56];
const CORE_LAYER_IDS = ['stays', 'inquiries', 'blocks', 'rates', 'turns', 'wos'];
const CORE_LEGEND: Record<string, string> = { stays: 'stc-lg-upcoming', inquiries: 'stc-lg-inq', blocks: 'stc-lg-block', rates: '', turns: '', wos: '' };
const ITEM_PILL: Record<ItemState, { label: string; cls: string }> = {
  pending: { label: 'Pending', cls: 'pend' },
  done: { label: 'Done', cls: 'ok' },
  waived: { label: 'Waived', cls: 'none' },
  na: { label: 'N/A', cls: 'none' },
  failed: { label: 'Failed', cls: 'bad' },
};
const ECI_LCO_SRC: Record<string, string> = { manual: 'approved by hand', calendar: 'offered from calendar', auto: 'detected from Hostaway times' };
const VER_PILL: Record<string, { ic: string; label: string; cls: string }> = {
  verified: { ic: '✅', label: 'Verified', cls: 'ok' },
  pending: { ic: '◐', label: 'Pending', cls: 'pend' },
  at_risk: { ic: '⚠', label: 'At risk', cls: 'warn' },
  blocked: { ic: '⛔', label: 'Blocked', cls: 'bad' },
  not_generated: { ic: '—', label: 'Not generated', cls: 'none' },
};
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

type CalD = CalData & CalWindowInfo;
/** One inspector entity on the back stack (data = what its load resolved; core kinds keep their state on the page). */
interface InspEntry { ref: EntityRef; data: unknown; loading: boolean; err: string | null; ctrl: AbortController | null; seq: number }
/** A chip in a row's ops sub-lanes: the built-in turn / WO chips keep their R112 renderers, module chips draw from ChipSpec. */
interface OpsChip { spec: ChipSpec; core: { kind: 'turn'; turn: TurnEv } | { kind: 'wo'; wo: WoEv } | null; dim: boolean; hl: boolean; start: string; end: string; lane: number }

const ss = {
  get(k: string): string | null { try { return sessionStorage.getItem(k); } catch { return null; } },
  set(k: string, v: string): void { try { sessionStorage.setItem(k, v); } catch { /* private mode */ } },
  del(k: string): void { try { sessionStorage.removeItem(k); } catch { /* private mode */ } },
};
const ls = {
  get(k: string): string | null { try { return localStorage.getItem(k); } catch { return null; } },
  set(k: string, v: string): void { try { localStorage.setItem(k, v); } catch { /* private mode */ } },
  del(k: string): void { try { localStorage.removeItem(k); } catch { /* private mode */ } },
};
/** A feature module's hook never breaks the calendar: a throw logs and falls back. */
function safe<T>(fn: () => T, fb: T): T {
  try { const v = fn(); return v === undefined || v === null ? fb : v; } catch (e) { console.warn('[calendar] a module hook failed:', e); return fb; }
}
const SAFE_CSS_RE = /^[#(),.%\s\w-]+$/;
/** Layer.swatch is a CSS background (colour / var(--token) / gradient); anything else → a neutral swatch. */
const cssBg = (v: unknown): string => (typeof v === 'string' && v.length <= 240 && SAFE_CSS_RE.test(v) ? v : 'var(--border)');
const sameRef = (a: EntityRef, b: EntityRef) => a.kind === b.kind && a.id === b.id && (a.listing_id || '') === (b.listing_id || '') && (a.date || '') === (b.date || '');
const TONES = new Set(['info', 'ok', 'warn', 'bad', 'muted', 'accent']);
const toneOf = (t: unknown) => (typeof t === 'string' && TONES.has(t) ? t : 'muted');
const clsSafe = (c: unknown) => (typeof c === 'string' ? c.replace(/[^\w -]/g, '').slice(0, 80) : '');
/** An icon name from the shared set renders as SVG; anything else (an emoji) as text. */
const glyph = (name: unknown, size = 15): string => { const n = String(name || ''); return icon(n, size) || Formatters.escapeHtml(n); };

export class StaysCalendarPage {
  private container: HTMLElement;
  private rootEl: HTMLElement | null = null;
  private data: CalD | null = null;
  private loading = true;
  private error = '';
  private forbidden = false;
  // ── window (R115 B9: the past is LAZY — `extra` past columns load only on "‹ Earlier" / ‹ at the left edge) ──
  private from = '';            // '' = anchored on today (the server picks the day before today)
  private days = 28;            // forward columns (the 2 / 4 / 8 wk chips)
  private extra = 0;            // past columns: 0 | 30 | 60 (back + forward ≤ 120)
  private pendingScroll: { left: number } | null = null;   // the scrollLeft a NEW window lands on (keeps the visual position)
  private q = '';
  private qTimer: ReturnType<typeof setTimeout> | null = null;
  private reqSeq = 0;
  // ── density: comfortable on a coarse pointer (JS geometry) ──
  private colW = COL_W;
  private laneH = LANE_H;
  private opsH = OPS_H;
  private coarse = false;
  // ── view / stat / layers ──
  private view = 'stays';
  private viewTransient = false;   // a deep link's view — never written to stc_view
  private stat = '';               // the ONE active stat (per view, sessionStorage stc_stat:<view>)
  private showAllRows = false;     // "Show all" in the corner: the view's row filter is lifted until the view changes
  private ovCache: { view: string; map: Record<string, LayerState> } | null = null;
  private layerCache = new Map<string, { data: unknown; err: string | null }>();   // layerKey(id, from, to) → data / error
  private layerInflight = new Set<string>();
  private layerCtrls: AbortController[] = [];
  private layerGen = 0;
  private layerSeqs = new Map<string, number>();            // layerKey → the request that owns it (a newer refetch supersedes an older one)
  private layerWaits = new Map<string, Promise<void>>();    // layerKey → settles when its in-flight request lands
  private layersReady: Promise<void> = Promise.resolve();   // the refetch the last base load started
  private roster: Property[] | null = null;                 // every home from the last load with no search (the "Which home?" pickers)
  private drawQueued = false;
  private countsMemo: { d: CalD | null; c: CoreStatCounts } | null = null;
  // ── inspector (the #stc-panel host with a back stack) ──
  private stack: InspEntry[] = [];
  private secOpen = new Map<string, boolean>();   // `${kind}|${section}` → the user opened / closed it
  private inspSeq = 0;
  // ── form sheet / quick popover / day pick / rail / search ──
  private sheetOpen = false;
  private sheetDirty = false;
  private sheetOnClose: (() => void) | null = null;
  private modPopOpen = false;
  private modPopOnClose: (() => void) | null = null;
  private showMenuOpen = false;
  private picking = false;
  private dragChip: { ref: EntityRef; listing_id: string; kind: string } | null = null;
  private dragExt: { ref: EntityRef; listing_id: string; kind: string } | null = null;   // R115 integ: a rail / bucket row dragged onto the timeline ('application/x-stc-entity')
  private dragWrongRow = false;   // R115 integ fix: the last dragover was another home's row (dropEffect 'none' → the browser never fires drop)
  private railQ = '';
  private railCollapsed = StaysCalendarPage.readRailCollapsed();
  private searchOpen = false;
  private chipIndex = new Map<string, ChipSpec>();
  private ctx: CalendarCtx;
  // ── the stay / block / turn / WO inspectors' own state (synced from the top of the stack) ──
  private selectedId: string | null = null;
  private detail: Detail | null = null;
  private detailFor: string | null = null;
  private detailLoading = false;
  private applying = new Set<string>();
  private applyNote = '';
  private loadedAt: Date | null = null;
  private tick: ReturnType<typeof setInterval> | null = null;
  private keyBound = false;
  private drawKey = '';         // `${from}|${days}` of the last timeline draw — same key → keep the scroll position
  private detailsOpen = StaysCalendarPage.readDetailsOpen();
  private selectedBlock: { listing_id: string; start: string; end: string } | null = null;   // the panel's "Blocked nights" variant
  private blockInfo: BlockInfo | null = null;
  private blockLoading = false;
  private popWo: string | null = null;    // the work order whose (lite) inspector is open
  private popErr = '';
  private popBusy = false;
  private dragWo: { id: string; listing_id: string; date: string; title: string; due_only?: boolean } | null = null;
  private addOpen = false;
  private addStep: 'form' | 'summary' = 'form';
  private addForm: AddForm = StaysCalendarPage.blankAdd();
  private addPropQ = '';
  private addErr = '';
  private addConflicts: { date: string; why: string }[] = [];
  private addBusy = false;
  private toastTimer: ReturnType<typeof setTimeout> | null = null;
  // R112
  private popTurn: string | null = null;           // the turn whose inspector is open
  private popTurnData: TurnPop | null = null;      // GET /api/stays/turn/:id (cleaner roster + flags); null while loading / on an older server
  private popTurnLoading = false;
  private popConfirm: PopConfirm | null = null;    // the move ask, or a 409 needs_confirm waiting for "Assign anyway" / "Move anyway"
  private popExisting: { id: string; status: string; date: string } | null = null;   // a 409 "already has a clean on <date>" from move (id = THAT clean, for the HK hop)
  private turnAsk: ((ok: boolean) => void) | null = null;   // R115: the inline "… anyway" answer the shared 409 helper waits for
  private dragTurn: { id: string; listing_id: string; date: string } | null = null;
  private showHiddenInq = StaysCalendarPage.readShowHiddenInq();   // per tab: impossible + dismissed inquiries stay hidden (and off the wire) unless asked
  private undoFn: (() => void) | null = null;      // the 6 s undo toast (dismiss / restore an inquiry)
  private inqBusy = false;
  private checkBusy = new Set<string>();           // checklist item keys with a POST in flight
  private genBusy = false;                         // POST /api/restracker/generate (a cold Hostaway fetch can take seconds)
  private photoBusy: string | null = null;
  private eciBusy = new Set<string>();             // 'eci' | 'lco' with a POST in flight
  private eciNote = '';

  // The page instance the core registrations talk to (App keeps ONE persistent instance; tests may build several — the latest wins).
  private static live: StaysCalendarPage | null = null;
  private static coreDone = false;
  private static coreKinds = new Map<string, Inspector>();
  private static coreChipKinds = new Map<string, ChipKind>();

  constructor(container: HTMLElement) {
    this.container = container;
    StaysCalendarPage.live = this;
    this.ctx = this.makeCtx();
    registerPresets();
    StaysCalendarPage.registerCore();
    StaysCalendarPage.migrateLegacy();
    // R115: a deep link while the Calendar is ALREADY open (App.navigate is a no-op on the same page) — link.ts dispatches this
    document.addEventListener(FOCUS_EVENT, () => {
      if (this.rootEl && this.rootEl.isConnected && StaysCalendarPage.live === this) void this.takeFocusNow();
    });
  }
  private esc(s: unknown): string { return Formatters.escapeHtml(s == null ? '' : String(s)); }

  // App's 15-min auto-refresh asks first — re-rendering would close the stay
  // the user is reading (the R37 data-loss class), drop a half-typed hold, or
  // yank the chip out from under a drag. The ↻ button is one click.
  // R112: the turn view, a drag, an open cleaner select, a pending undo toast
  // and any checklist / ECI write in flight veto it too.
  // R115: an open inspector (any entity on the stack), the form sheet (open or
  // dirty), a quick popover, a day pick, a module drag, every registered
  // busy() (registry anyBusy, inspectors, the open sections) veto it as well.
  suspendAutoRefresh(): boolean {
    if (WoQuickView.isOpen()) return true;   // the shared WO card is open on top of the page
    if (this.stack.length || this.addOpen || this.sheetOpen || this.sheetDirty || this.modPopOpen || this.picking) return true;
    if (this.dragWo || this.dragTurn || this.dragChip || this.dragExt || this.undoFn) return true;
    if (this.genBusy || this.inqBusy || this.checkBusy.size > 0 || this.eciBusy.size > 0 || this.photoBusy || this.popBusy) return true;
    if (anyBusy()) return true;
    for (const e of this.stack) {
      const ins = getInspector(e.ref.kind);
      if (ins && ins.busy && safe(() => ins.busy!(), false)) return true;
      if (sectionsFor(e.ref.kind).some(s => s.busy && safe(() => s.busy!(), false))) return true;
    }
    const a = document.activeElement as HTMLElement | null;   // a cleaner select open under the mouse
    return !!(a && a.tagName === 'SELECT' && a.closest('#stc-root'));
  }

  // ── dates (calendar dates, never instants: UTC in, UTC out) ──────────
  private addDays(s: string, n: number): string { return calAddDays(s, n); }
  private diffDays(a: string, b: string): number { return calDiffDays(a, b); }
  private fmt(s: string, opts: Intl.DateTimeFormatOptions): string { try { return new Date(s + 'T00:00:00Z').toLocaleDateString('en-US', { ...opts, timeZone: 'UTC' }); } catch { return s; } }
  private fmtMD(s: string): string { return this.fmt(s, { month: 'short', day: 'numeric' }); }
  private fmtWMD(s: string): string { return this.fmt(s, { weekday: 'short', month: 'short', day: 'numeric' }); }
  private dow(s: string): number { return new Date(s + 'T00:00:00Z').getUTCDay(); }
  private validDate(s: unknown): s is string { return calValidDate(s); }
  /** Today in the business timezone — only a fallback; the server's as_of is the truth whenever a payload is loaded. */
  private bizToday(): string { return bizTodayL(); }
  /** Listing thumbnail with an initials fallback — Airbnb CDN URLs go stale, and a broken-image icon is worse than no photo. */
  private avatarHtml(url: string | null | undefined, name: string, lazy: boolean): string {
    const ini = `<span class="stc-pav">${this.esc(this.initials(name))}</span>`;
    if (!url) return ini;
    return `<img class="stc-pav" src="${this.esc(url)}" alt=""${lazy ? ' loading="lazy"' : ''} onerror="var t=this.nextElementSibling;this.outerHTML=t.innerHTML;t.remove()"><template>${ini}</template>`;
  }

  private static readDetailsOpen(): boolean { return ls.get('stc_details_open') === '1'; }
  /** R115 integ: the drag format a rail / bucket row carries onto the timeline — {kind, id, listing_id}. */
  private static readonly EXT_TYPE = 'application/x-stc-entity';
  private static hasExtType(de: DragEvent): boolean { try { return !!de.dataTransfer && Array.from(de.dataTransfer.types || []).includes(StaysCalendarPage.EXT_TYPE); } catch { return false; } }
  /** A dragged entity, validated (it came through the DOM — never trusted as-is). */
  private static parseExt(raw: string): { ref: EntityRef; listing_id: string; kind: string } | null {
    if (!raw || raw.length > 600) return null;
    let o: any = null;
    try { o = JSON.parse(raw); } catch { return null; }
    if (!o || typeof o !== 'object') return null;
    const kind = typeof o.kind === 'string' ? o.kind : '', id = o.id == null ? '' : String(o.id), lid = o.listing_id == null ? '' : String(o.listing_id);
    if (!/^[a-z][\w.-]{0,40}$/i.test(kind) || !id || id.length > 120 || lid.length > 64 || /[<>"'`]/.test(id + lid)) return null;
    return { ref: { kind, id, listing_id: lid || undefined }, listing_id: lid, kind };
  }
  private static readShowHiddenInq(): boolean { return ss.get('stc_show_hidden_inq') === '1'; }
  private static readRailCollapsed(): boolean { return ls.get('stc_rail_collapsed') === '1'; }
  private static blankAdd(): AddForm { return { listing_id: '', kind: 'block', check_in: '', check_out: '', note: '', guest_name: '', guest_email: '', guest_phone: '', guests: '', total_price: '' }; }
  private static actorName(): string {
    try { const p = JSON.parse(sessionStorage.getItem('userProfile') || '{}'); return String(p.full_name || p.email || sessionStorage.getItem('userRole') || '').trim(); }
    catch { return ''; }
  }
  private static profile(): Record<string, unknown> { try { return JSON.parse(sessionStorage.getItem('userProfile') || '{}') || {}; } catch { return {}; } }
  /** R115 one-time migrations of the pre-hub prefs: the one-chip stc_filter → the Reservations stat; the legend's inquiries / ops toggles → this tab's Reservations layers. */
  private static migrateLegacy(): void {
    const f = ss.get('stc_filter');
    if (f != null) {
      const st = legacyFilterToStat(f);
      if (st && ss.get('stc_stat:stays') == null) ss.set('stc_stat:stays', st);
      ss.del('stc_filter');
    }
    if (ss.get('stc_layers:stays') == null) {
      const ov: Record<string, LayerState> = {};
      if (ls.get('stc_show_inquiries') === '0') ov.inquiries = 'off';
      if (ls.get('stc_show_ops') === '0') { ov.turns = 'off'; ov.wos = 'off'; }
      if (Object.keys(ov).length) ss.set('stc_layers:stays', JSON.stringify(ov));
    }
  }

  private initials(name: string): string { return initialsOf(name); }
  // R122 review 4: no userRole (a cleared or broken session) is the LEAST privilege, never admin: no role-gated layer,
  // action or control (Open lease nights, money) is drawn until the boot stamps the real role
  private role(): string { return ss.get('userRole') || ''; }

  // ── CalendarCtx — what feature modules may use (calendar/registry.ts) ──
  private makeCtx(): CalendarCtx {
    const page = this;
    return {
      get data() { return page.data || page.emptyData(); },
      get view() { return page.view; },
      get role() { return page.role(); },
      get asOf() { return page.data ? page.data.as_of : page.bizToday(); },
      get from() { return page.data ? page.data.from : ''; },
      get to() { return page.data ? page.data.to : ''; },
      layer: (id: string) => page.layerData(id) as any,
      layerError: (id: string) => page.layerErr(id),
      ensureLayer: (id: string) => page.ensureLayer(id) as Promise<any>,
      allProperties: () => page.allProperties(),
      property: (id: string) => page.propOf(id),
      staysOf: (id: string) => (page.data ? page.data.stays.filter(s => s.listing_id === id) : []),
      nightTaken: (id: string, date: string) => page.nightTaken(id, date),
      reload: (o) => page.reloadFor(o),
      toast: (m: string) => page.toast(m),
      toastUndo: (m: string, u: () => void) => page.toastUndo(m, u),
      openInspector: (ref: EntityRef) => page.pushEntry(ref),
      back: () => page.backEntry(),
      closeInspector: () => page.closeInspectorAll(),
      openPop: (anchor, html, o) => page.openModPop(anchor, html, o),
      closePop: () => page.closeModPop(),
      openSheet: (html, o) => page.openSheet(html, o),
      closeSheet: () => { page.closeSheet(true); },
      markDirty: (d: boolean) => { page.sheetDirty = !!d && page.sheetOpen; },
      openChooser: (lid: string, date: string) => page.pushEntry(page.dateRef(lid, date)),
      pickDay: (o) => page.pickDay(o),
      focus: (o) => page.focusOn(o),
      go: (p: string, h?: string) => page.go(p, h),
      actorName: () => StaysCalendarPage.actorName(),
      esc: (s: unknown) => page.esc(s),
      postWithOverride: (url, body, confirmText) => page.postOverride(url, body, { text: confirmText }),
      fmt: { md: (d: string) => page.fmtMD(d), wmd: (d: string) => page.fmtWMD(d), addDays: calAddDays, diffDays: calDiffDays },
    };
  }
  private emptyData(): CalD {
    const t = this.bizToday();
    return { as_of: t, generated_at: '', from: t, to: t, days: 0, q: '', properties: [], stays: [], blocks: [], rates: {}, kpis: { check_ins_today: 0, check_outs_today: 0, staying_tonight: 0, opportunities_in_view: 0 }, events: { turns: [], work_orders: [] }, filters: {}, anchor: null, back: 0 };
  }

  // ── the core's registrations (CALENDAR_CONTRACT ownership table: core rows) ──
  // Registered once, each ONLY when its key is still free — feature modules
  // were imported first, so a feature that replaces a core kind wins.
  private static registerCore(): void {
    if (StaysCalendarPage.coreDone) return;
    StaysCalendarPage.coreDone = true;
    const P = () => StaysCalendarPage.live!;
    const lay = (l: Layer) => { if (!getLayer(l.id)) registerLayer(l); };
    const stat = (s: StatSpec) => { if (!getStat(s.key)) registerStat(s); };
    const insp = (i: Inspector) => { if (!getInspector(i.kind)) registerInspector(i); StaysCalendarPage.coreKinds.set(i.kind, i); };
    const sec = (s: Section) => { if (!sectionsFor(s.inspector).some(x => x.key === s.key)) registerSection(s); };
    const act = (a: Action) => { if (!actionsFor(a.inspector, 'dev').some(x => x.key === a.key)) registerAction(a); };
    const chip = (k: ChipKind) => { if (!getChipKind(k.kind)) registerChipKind(k); StaysCalendarPage.coreChipKinds.set(k.kind, k); };
    const ALL_VIEWS: any[] = ['stays', 'ops', 'guests', 'mtr', 'health'];

    // layers drawn from the base payload
    lay({ id: 'stays', label: 'Stays & owner stays', swatch: 'var(--info-soft)', scope: 'stay', count: (ctx) => ctx.data.stays.filter(s => s.kind !== 'inquiry').length });
    lay({ id: 'inquiries', label: 'Inquiries', swatch: 'var(--card)', scope: 'stay', count: (ctx) => ctx.data.stays.filter(s => s.kind === 'inquiry').length });
    lay({ id: 'blocks', label: 'Blocked & held nights', swatch: 'repeating-linear-gradient(135deg, var(--bg-subtle) 0 4px, var(--border) 4px 6px)', scope: 'stay', count: (ctx) => ctx.data.blocks.length });
    lay({ id: 'rates', label: 'Nightly rates (free nights)', swatch: 'var(--bg-subtle)', scope: 'day', count: () => null });
    lay({ id: 'turns', label: 'Cleans (turnovers)', swatch: 'var(--warning-soft)', scope: 'day', count: (ctx) => ctx.data.events.turns.length });
    lay({ id: 'wos', label: 'Work orders', swatch: 'var(--info-soft)', scope: 'day', count: (ctx) => ctx.data.events.work_orders.length });

    // Reservations stats (today-based KPIs + forward-window counts; lib/calendarCore coreStatCounts mirrors the server)
    const today = (ctx: CalendarCtx) => ctx.asOf;
    stat({ key: 'arriving_today', label: 'Arriving today', icon: 'home', compute: () => ({ value: P().counts().arriving_today }), match: { stays: (s, ctx) => s.kind === 'stay' && s.check_in === today(ctx) } });
    stat({ key: 'checkouts_today', label: 'Checkouts today', icon: 'flag', compute: () => ({ value: P().counts().checkouts_today }), match: { stays: (s, ctx) => s.kind !== 'inquiry' && s.check_out === today(ctx) } });
    stat({
      key: 'in_house', label: 'Staying tonight', icon: 'building',
      compute: () => { const c = P().counts(); return { value: c.in_house, tone: c.negative_in_house > 0 ? 'warn' : undefined, sub: c.negative_in_house > 0 ? `😟 ${c.negative_in_house} having a negative experience` : undefined }; },
      match: { stays: (s, ctx) => s.kind !== 'inquiry' && s.check_in <= today(ctx) && s.check_out > today(ctx) },
    });
    stat({ key: 'vacant_tonight', label: 'Vacant tonight', icon: 'moon', compute: () => ({ value: P().counts().vacant_tonight }), match: { rows: (p) => p.vacant_tonight === true } });
    stat({ key: 'negative', label: 'Negative experience', icon: 'heart', compute: () => { const n = P().counts().negative; return { value: n, tone: n > 0 ? 'bad' : undefined }; }, match: { stays: (s, ctx) => s.negative === true && s.check_out >= forwardStart(ctx.from, ctx.to, ctx.asOf) && s.check_in <= ctx.to } });   // R115 review: the forward window the card counts (‹ Earlier never changes it)
    stat({ key: 'open_weekend', label: 'Open weekend', icon: 'calendar', compute: () => ({ value: P().counts().open_weekend }), match: { rows: (p) => !!(p.open_weekend && p.open_weekend.fri) } });

    // inspectors (core kinds; data shapes in calendar/contract-ext.ts)
    insp({ kind: 'stay', title: (ref) => P().stayTitle(ref.id), sub: (ref) => P().staySub(ref.id) });
    insp({ kind: 'block', title: () => (P().blockKind() === 'held' ? 'Held nights' : 'Blocked nights'), sub: (ref) => P().propName(ref.listing_id || ref.id.split('|')[0]) });
    insp({ kind: 'listing', title: (ref) => P().propName(ref.listing_id || ref.id), sub: (ref) => P().listingSub(ref.listing_id || ref.id) });
    insp({ kind: 'turn', title: (ref) => P().turnTitle(ref.id), sub: (ref) => P().turnSub(ref.id) });
    insp({ kind: 'wo', title: (ref) => P().woTitle(ref.id), sub: (ref) => P().propName((P().woOf(ref.id) || { listing_id: ref.listing_id || '' }).listing_id) });
    insp({ kind: 'date', title: (ref) => (ref.listing_id ? P().propName(ref.listing_id) : 'Add to the calendar'), sub: (ref) => P().dateSub(ref) });
    insp({ kind: 'rail', title: () => 'To do', sub: () => `${P().activeView().label} · ${P().railCount()} item${P().railCount() === 1 ? '' : 's'}` });

    // stay sections — today's stay panel, moved (not rewritten); the view decides which open first
    const stayOf = (ref: EntityRef) => P().findStay(ref.id);
    sec({ inspector: 'stay', key: 'stay', title: '', order: 10, openIn: ALL_VIEWS, when: (ref) => !!stayOf(ref), render: (ref) => P().secStayCard(stayOf(ref)!) });
    sec({ inspector: 'stay', key: 'inquiry', title: 'Inquiry', order: 15, openIn: ALL_VIEWS, when: (ref) => !!stayOf(ref) && stayOf(ref)!.kind === 'inquiry', render: (ref) => P().secInquiry(stayOf(ref)!) });
    sec({ inspector: 'stay', key: 'opportunities', title: 'Opportunities', order: 20, openIn: ['stays'], when: (ref) => !!stayOf(ref), render: (ref) => P().secOpps(stayOf(ref)!) });
    sec({ inspector: 'stay', key: 'sentiment', title: 'Guest sentiment', order: 30, openIn: ['stays', 'guests'], when: (ref) => !!stayOf(ref), render: (ref) => P().secSentiment(stayOf(ref)!) });
    sec({ inspector: 'stay', key: 'review', title: 'Review', order: 40, openIn: ['stays', 'guests', 'health'], when: (ref) => !!stayOf(ref) && !!P().secReview(stayOf(ref)!), render: (ref) => P().secReview(stayOf(ref)!) });
    sec({ inspector: 'stay', key: 'arrival_checklist', title: 'Arrival checklist', order: 50, openIn: ['stays'], when: (ref) => !!stayOf(ref) && !!P().secChecklist(stayOf(ref)!), render: (ref) => P().secChecklist(stayOf(ref)!) });
    sec({ inspector: 'stay', key: 'ops_around', title: 'Ops around this stay', order: 60, openIn: ['stays', 'ops'], when: () => !!P().detailOpsHtml(P().detailForSelected()), render: () => P().detailOpsHtml(P().detailForSelected()) });
    sec({ inspector: 'stay', key: 'details', title: 'Details', order: 90, when: (ref) => !!stayOf(ref) && !!P().detailForSelected(), render: (ref) => P().detailsHtml(P().detailForSelected(), stayOf(ref)!) });
    // the other core kinds
    sec({ inspector: 'block', key: 'block', title: '', order: 10, openIn: ALL_VIEWS, when: () => true, render: () => P().secBlock() });
    sec({ inspector: 'listing', key: 'listing', title: '', order: 10, openIn: ALL_VIEWS, when: (ref) => !!P().propOf(ref.listing_id || ref.id), render: (ref) => P().secListing(ref.listing_id || ref.id) });
    sec({ inspector: 'turn', key: 'turn', title: '', order: 10, openIn: ALL_VIEWS, when: () => P().isCoreKind('turn'), render: () => P().secTurn() });
    sec({ inspector: 'wo', key: 'wo', title: '', order: 10, openIn: ALL_VIEWS, when: () => P().isCoreKind('wo'), render: () => P().secWo() });
    sec({ inspector: 'date', key: 'date', title: '', order: 10, openIn: ALL_VIEWS, when: () => P().isCoreKind('date'), render: (ref) => P().secChooser(ref) });
    sec({ inspector: 'rail', key: 'rail', title: '', order: 10, openIn: ALL_VIEWS, when: () => true, render: () => P().railListHtml(true) });

    // actions — one blue primary per inspector (the view's primaryIn), ≤ 2 secondary, the rest behind ⋯
    act({ inspector: 'stay', key: 'message', label: 'Open conversation', icon: '💬', order: 10, primaryIn: ALL_VIEWS, when: () => true, run: () => P().openConversation() });
    act({ inspector: 'stay', key: 'issues', label: 'View on Issues board', icon: '⚑', order: 60, when: (ref) => !!P().flagOf(ref.id), run: () => P().openIssue() });
    act({ inspector: 'stay', key: 'hostaway', label: 'Open in Hostaway', icon: '↗', order: 80, when: () => !!P().hostawayUrl(), run: () => { const u = P().hostawayUrl(); if (u) window.open(u, '_blank', 'noopener'); } });
    act({ inspector: 'stay', key: 'add', label: 'Add on these dates…', icon: '＋', order: 90, when: (ref) => !!stayOf(ref) && stayOf(ref)!.kind !== 'inquiry', run: (ref, _d, ctx) => { const s = stayOf(ref)!; ctx.openChooser(s.listing_id, s.check_in > ctx.asOf ? s.check_in : ctx.asOf); } });
    act({ inspector: 'block', key: 'blocks', label: 'Open Calendar blocks', icon: '📅', order: 10, when: () => true, run: () => P().go('portfolio', 'calendar-blocks') });
    // R115 integ: the listing's blue primary is a NAVIGATION action (Property Details — the guest builder's
    // "Go to" section keeps every other link); money-adjacent "+ Resolution" / "+ Claim" sit behind it.
    act({ inspector: 'listing', key: 'details', label: 'Property details', icon: '🏠', order: 5, primaryIn: ALL_VIEWS, when: (ref) => !!(ref.listing_id || ref.id), run: (ref) => P().openPropertyDetails(ref.listing_id || ref.id) });
    act({ inspector: 'listing', key: 'add', label: 'Add for this home…', icon: '＋', order: 50, when: () => true,   // R115 review: before + Resolution / + Claim (order 60 / 61) — the first visible secondary everywhere
      run: (ref, _d, ctx) => ctx.openChooser(ref.listing_id || ref.id, ctx.asOf) });
    act({ inspector: 'turn', key: 'move', label: 'Move to another day…', icon: '📅', order: 10, when: () => P().isCoreKind('turn') && P().turnMovable(), run: () => P().pickTurnDay() });
    act({ inspector: 'turn', key: 'hk', label: 'Open in Housekeeping', icon: '🛏', order: 20, when: () => P().isCoreKind('turn'), run: () => P().openHousekeeping(P().popTurnId()) });
    act({ inspector: 'wo', key: 'book', label: 'Book for its due day', icon: '📌', order: 5, when: (ref) => P().isCoreKind('wo') && !!(P().woOf(ref.id) || { due_only: false }).due_only, run: (ref) => { const w = P().woOf(ref.id); if (w) void P().rescheduleWo(w.id, w.title, w.date); } });
    act({ inspector: 'wo', key: 'pick', label: 'Book / move to a day…', icon: '📅', order: 10, when: (ref) => P().isCoreKind('wo') && !!P().woOf(ref.id), run: (ref) => P().pickWoDay(ref.id) });
    act({ inspector: 'wo', key: 'maintenance', label: 'Open in Maintenance', icon: '🔧', order: 20, when: () => P().isCoreKind('wo'), run: () => P().openMaintenance() });
    act({ inspector: 'wo', key: 'details', label: 'Full work-order card', icon: '🗂', order: 30, when: (ref) => P().isCoreKind('wo') && !!P().woOf(ref.id), run: (ref) => { const w = P().woOf(ref.id); if (w) openWoQuickView(w.id, { listingId: w.listing_id, onChange: () => { void P().reloadFor({ base: true }); } }); } });
    act({ inspector: 'date', key: 'hold', label: 'Hold or reserve…', icon: '⊘', order: 10, primaryIn: ['stays'], when: (ref, data) => { const c = data as DateCell; return !!c && !c.past && !c.taken; }, run: (ref, data) => { const c = data as DateCell; P().openAdd({ listing_id: c.listing_id || undefined, check_in: c.date }); } });

    // chip kinds — the built-in turn / WO chips keep their R112 renderers; open = the inspector, move = drag (desktop) / pickDay
    chip({ kind: 'turn', legend: 'Clean', open: (ref) => P().pushEntry({ kind: 'turn', id: ref.id, listing_id: ref.listing_id, date: ref.date }),
      move: { canDrop: (ref, date, ctx) => date >= ctx.asOf && !P().turnDone(ref.id), onDrop: (ref, date) => { void P().moveTurn(ref.id, date, { confirm: true }); } } });
    chip({ kind: 'wo', legend: 'Work order', open: (ref) => P().pushEntry({ kind: 'wo', id: ref.id, listing_id: ref.listing_id, date: ref.date }),
      move: { canDrop: () => true, onDrop: (ref, date) => { const w = P().woOf(ref.id); if (w) void P().rescheduleWo(w.id, w.title, date); } } });
  }
  /** True while the core's own implementation of an inspector kind is the registered one (maint replaces 'wo'). */
  private isCoreKind(kind: string): boolean { const c = StaysCalendarPage.coreKinds.get(kind); return !!c && getInspector(kind) === c; }

  // ── lifecycle ──────────────────────────────────────────────────────────
  async render(): Promise<void> {
    StaysCalendarPage.live = this;
    registerPresets(); StaysCalendarPage.registerCore();
    this.applyDensity();
    const focus = this.consumeFocus();
    this.resolveViewNow(focus);
    if (focus) this.focusWindow(focus);
    this.container.innerHTML = `
      <div class="page-content stc-page${this.coarse ? ' stc-coarse' : ''}" id="stc-root" style="--stc-col:${this.colW}px;">
        <style>${this.css()}${HUB_CSS}${allCss()}</style>
        <div class="stc-top">
          <div class="stc-titlerow"><h1 class="stc-h1">Calendar</h1><div class="stc-views" id="stc-views"></div></div>
          <div class="stc-blurb" id="stc-blurb"></div>
        </div>
        <div class="stc-toolbar" id="stc-toolbar">
          <button type="button" class="btn btn-secondary stc-viewbtn stc-phone" data-stc-viewsheet aria-haspopup="dialog"></button>
          <label class="stc-search">
            ${icon('search', 15)}
            <input class="input" id="stc-q" type="search" placeholder="Search property or guest" autocomplete="off" value="${this.esc(this.q)}">
          </label>
          <span class="stc-updated stc-desk" id="stc-updated">Loading…</span>
          <button type="button" class="btn btn-secondary stc-refresh stc-desk" data-stc-refresh title="Refresh">↻</button>
          <span class="stc-spacer"></span>
          <div class="stc-nav">
            <button type="button" class="btn btn-secondary" data-stc-nav="prev" title="Previous week (←)" aria-label="Previous week">‹</button>
            <button type="button" class="btn btn-secondary" data-stc-nav="today" title="Jump to today (T)">Today</button>
            <button type="button" class="btn btn-secondary" data-stc-nav="next" title="Next week (→)" aria-label="Next week">›</button>
          </div>
          <div class="stc-wins stc-desk">
            ${WINS.map(w => `<button type="button" class="chip ${this.days === w ? 'active' : ''}" data-stc-win="${w}">${w / 7} wk</button>`).join('')}
          </div>
          <button type="button" class="btn btn-secondary stc-desk" data-stc-show title="Layers on the calendar — and what each colour means">◧ Show ▾</button>
          <button type="button" class="btn btn-secondary stc-railbtn" data-stc-rail-btn hidden title="To do — items with no day yet or needing attention">📥 <span class="stc-desk">To do</span> <b data-stc-rail-n>0</b></button>
          <button type="button" class="btn btn-secondary stc-phone stc-qbtn" data-stc-qtoggle aria-label="Search" title="Search">${icon('search', 15)}</button>
          <button type="button" class="btn btn-primary stc-add-btn" data-stc-add title="Add to the calendar">+ <span class="stc-add-lbl">Add</span></button>
          <span class="stc-more" id="stc-more"></span>
        </div>
        <div class="stc-stats" id="stc-stats"></div>
        <div class="stc-body">
          <div class="stc-card">
            <div id="stc-timeline"><div style="padding:40px; text-align:center;">${LoadingSpinner.render()}</div></div>
          </div>
          <aside class="stc-rail" id="stc-rail" hidden aria-label="To do"></aside>
        </div>
        <div class="stc-panel" id="stc-panel" aria-live="polite"></div>
        <div id="stc-pop"></div>
        <div id="stc-sheet"></div>
        <div id="stc-modal"></div>
        <div class="stc-toast" id="stc-toast" role="status" aria-live="polite"></div>
      </div>`;
    this.rootEl = document.getElementById('stc-root');
    closeKebab();
    // R112/R115: nothing transient survives a re-render — popovers, the sheet, a drag, the Add modal, a pending undo (a ghost veto would park auto-refresh)
    this.dragWo = null; this.dragTurn = null; this.dragChip = null; this.picking = false;
    this.popConfirm = null; this.popExisting = null; this.answerTurnAsk(false); this.popBusy = false; this.popErr = '';
    this.clearUndo(); this.inqBusy = false; this.checkBusy.clear(); this.genBusy = false; this.photoBusy = null; this.eciBusy.clear(); this.eciNote = '';
    this.addOpen = false; this.addBusy = false; document.body.classList.remove('stc-modal-open');
    // R115 review: a discarded sheet / quick card still runs its onClose, or a module's dirty flag would veto auto-refresh forever
    const lostSheet = this.sheetOnClose, lostPop = this.modPopOnClose;
    this.sheetOpen = false; this.sheetDirty = false; this.sheetOnClose = null; this.picking = false;
    this.modPopOpen = false; this.modPopOnClose = null; this.showMenuOpen = false;
    for (const f of [lostSheet, lostPop]) if (f) safe(() => { f(); return true; }, true);
    for (const ins of [...new Set(this.stack.map(e => getInspector(e.ref.kind)).filter(Boolean))]) if (ins && ins.reset) safe(() => { ins.reset!(); return true; }, true);
    document.body.classList.toggle('stc-panel-open', this.stack.length > 0);
    this.bindChrome();
    this.drawChrome();
    this.syncLegacy();
    await this.load(false, true);   // a (re)render never trusts a detail cached before it
    if (focus) this.focusAfterLoad(focus);
    this.startTick();
  }
  /** pointer:coarse → the comfortable geometry (JS values: inline geometry, row heights, click / drop math). */
  private applyDensity(): void {
    let coarse = false;
    try { coarse = window.matchMedia('(pointer: coarse)').matches; } catch { coarse = false; }
    this.coarse = coarse;
    this.colW = coarse ? COL_W_COARSE : COL_W;
    this.laneH = coarse ? LANE_H_COARSE : LANE_H;
    this.opsH = coarse ? OPS_H_COARSE : OPS_H;
  }

  /** layers: 'refresh' (a write, ↻) refetches the cached layers of the same window but keeps showing them until the
   *  fresh ones land; 'keep' (a search keystroke, the hidden-inquiries toggle — layers don't depend on either) reuses them.
   *  A new window (from / to / as_of) always starts clean. */
  private async load(quiet = false, refetchDetail = false, layers: 'refresh' | 'keep' = 'refresh'): Promise<void> {
    const seq = ++this.reqSeq;
    let refreshIds: string[] | null = null;   // null = a new window (fetch what the view needs); [] = keep; ids = refetch those
    let moduleTop: InspEntry | null = null;
    if (!quiet) { this.loading = true; this.drawTimeline(); }
    const upd = document.getElementById('stc-updated');
    if (upd) upd.textContent = 'Loading…';
    const win = { from: this.from, days: this.days, extra: this.extra };
    const wq = windowQuery(win);
    const qs = new URLSearchParams();
    if (wq.from) qs.set('from', wq.from); else qs.set('back', String(wq.back));
    qs.set('days', String(wq.days));
    qs.set('q', this.q);
    if (this.showHiddenInq) qs.set('hidden', '1');
    let j: any = null, status = 0;
    try {
      const r = await fetch(`${API}/calendar?${qs.toString()}`);
      status = r.status;
      j = await r.json().catch(() => null);
    } catch { j = null; }
    if (seq !== this.reqSeq) return;   // a newer request owns the screen
    this.loading = false;
    this.forbidden = status === 403;
    if (!j || status !== 200) { this.error = (j && j.error) || (status ? `HTTP ${status}` : 'Could not reach the server'); }
    else {
      const prev = this.data;
      this.error = ''; this.data = normalizeCal(j, windowColumns(win)); this.loadedAt = new Date();
      if (!this.data.q) this.roster = this.data.properties;
      // R115 review: stale-while-revalidate — the same window keeps its layers on screen while they refetch (no
      // collapsing stat filters, MTR rows or Health sort, no lost scroll); a different window starts clean.
      const sameWin = !!prev && prev.from === this.data.from && prev.to === this.data.to && prev.as_of === this.data.as_of;
      if (!sameWin) this.clearLayers();
      else refreshIds = layers === 'refresh' ? this.cachedLayerIds() : [];
      // ↻ / Apply / 409: the open panel must not keep yesterday's opportunities or phone.
      // R112: the detail for the SAME stay stays on screen while its refetch is in flight.
      if (refetchDetail) {
        if (this.selectedId) {
          if (this.detailFor !== this.selectedId) this.detail = null;
          this.detailFor = this.selectedId; this.detailLoading = true; void this.fetchDetail(this.selectedId);
        } else { this.detail = null; this.detailFor = null; }
        const top = this.top();
        if (top && !this.isCoreKind(top.ref.kind)) moduleTop = top;   // a module inspector refetches once its layers are fresh; the core turn / block views patch in place (R112)
      }
      // an entity that left the window (done / moved out) closes its inspector — the R111 popover rule
      const top = this.top();
      if (top && top.ref.kind === 'wo' && this.isCoreKind('wo') && !this.woOf(top.ref.id)) this.closeTop();
      else if (top && top.ref.kind === 'turn' && !this.data.events.turns.some(t => t.id === top.ref.id)) this.closeTop();
    }
    this.draw();
    this.pendingScroll = null;
    this.layersReady = this.fetchLayers(refreshIds && refreshIds.length ? refreshIds : undefined);
    if (moduleTop) { const t = moduleTop; void this.layersReady.then(() => { if (this.top() === t) this.loadTop(true); }); }
  }

  /** After a write from the panel (checklist tick, ECI/LCO, generate): fresh detail first (no flicker), then the list so bars, KPIs and the turn chips agree. */
  private async refreshAfterWrite(id: string): Promise<void> {
    if (this.selectedId === id) await this.fetchDetail(id);
    await this.load(true, this.selectedId !== id);
  }
  /** ctx.reload: base (+ its layers, + the open inspector) by default; or just some layers; or just the inspector. */
  private async reloadFor(o?: { base?: boolean; layers?: string[]; inspector?: boolean }): Promise<void> {
    const opts = o || { base: true, inspector: true };
    // R115 review: resolves only once the base AND its layers are fresh, so a module that reads ctx.layer() or
    // opens an inspector right after `await ctx.reload(…)` sees what it just wrote.
    // a layer request that never answers must not hang the module awaiting this (it carries on after 8 s; the layer
    // still lands and redraws when it does)
    const capped = (p: Promise<void>) => Promise.race([p, new Promise<void>(r => setTimeout(r, 8000))]);
    if (opts.base) {
      // R122 review 3: the base request is capped like the layers (a slow / hung /api/stays/calendar must not hold a
      // module's sheet with a disabled submit; it still lands and redraws when it answers)
      await capped(this.load(true, opts.inspector !== false));
      if (this.error && this.data && !this.forbidden) {
        // the base fetch FAILED right after a write: the page keeps the last calendar ("Not updated"), but the layers
        // the module named and its open inspector come from their own endpoints: refresh them anyway
        if (opts.layers && opts.layers.length) await capped(this.fetchLayers(opts.layers.filter(id => !this.layerPending(id)), true));
        if (opts.inspector !== false) this.loadTop(true);
        return;
      }
      if (opts.layers && opts.layers.length) await capped(this.fetchLayers(opts.layers.filter(id => !this.layerPending(id))));
      await capped(this.layersReady);
      return;
    }
    if (opts.layers && opts.layers.length) await capped(this.fetchLayers(opts.layers));
    if (opts.inspector) {
      this.loadTop(true);
      // R115 review: a core kind drawn from the base payload (listing / date / lite WO) has nothing to fetch — redraw it
      // so a section that changed a setting (Health's "excl. guest messages") shows the new numbers
      const top = this.top();
      if (top && this.isCoreKind(top.ref.kind) && !['stay', 'turn', 'block'].includes(top.ref.kind)) this.drawPanel();
    }
  }
  /** ctx.ensureLayer: a registered layer for the loaded window even when this view doesn't draw it (cached). */
  private async ensureLayer(id: string): Promise<unknown> {
    const d = this.data;
    if (!d) return null;
    const key = layerKey(id, d.from, d.to);
    if (this.layerInflight.has(key)) await (this.layerWaits.get(key) || Promise.resolve());
    else if (!this.layerCache.has(key)) await this.fetchLayers([id]);
    return this.layerData(id);   // the current window's copy (a same-window base reload keeps it)
  }
  /** ctx.allProperties: every home on the calendar, the search box ignored (falls back to what is loaded). */
  private allProperties(): Property[] { return this.roster && this.roster.length ? this.roster : (this.data ? this.data.properties : []); }
  private cachedLayerIds(): string[] {
    const d = this.data; if (!d) return [];
    const ids = new Set<string>();
    for (const k of this.layerCache.keys()) { const id = k.split('|')[0]; if (layerKey(id, d.from, d.to) === k) ids.add(id); }
    return [...ids];
  }

  // ── layers: one request per window for the view's server layers, cached, cleared on every base reload ──
  private layerState(id: string): LayerState {
    const ov = this.overrides()[id];
    if (ov === 'on' || ov === 'dim' || ov === 'off') return ov;
    const st = this.activeView().layers[id];
    return st === 'on' || st === 'dim' || st === 'off' ? st : 'off';
  }
  private overrides(): Record<string, LayerState> {
    if (this.ovCache && this.ovCache.view === this.view) return this.ovCache.map;
    let map: Record<string, LayerState> = {};
    try {
      const raw = JSON.parse(ss.get(`stc_layers:${this.view}`) || '{}');
      if (raw && typeof raw === 'object' && !Array.isArray(raw)) for (const [k, v] of Object.entries(raw)) if (v === 'on' || v === 'dim' || v === 'off') map[k] = v;
    } catch { map = {}; }
    this.ovCache = { view: this.view, map };
    return map;
  }
  private setLayerState(id: string, st: LayerState): void {
    const map = { ...this.overrides() };
    const preset = this.activeView().layers[id] || 'off';
    if (st === preset) delete map[id]; else map[id] = st;
    if (Object.keys(map).length) ss.set(`stc_layers:${this.view}`, JSON.stringify(map)); else ss.del(`stc_layers:${this.view}`);
    this.ovCache = { view: this.view, map };
  }
  private layersAllowed(): Layer[] { return allLayers(this.role()); }
  /** Layers whose data this view needs: shown (on / dim) + every stat's / rail provider's own layer. */
  private neededLayers(): Layer[] {
    const ids = new Set<string>();
    for (const l of this.layersAllowed()) if (this.layerState(l.id) !== 'off') ids.add(l.id);
    for (const s of this.viewStats()) if (s.layer) ids.add(s.layer);
    for (const r of this.viewRails()) if (r.layer) ids.add(r.layer);
    const allowed = new Set(this.layersAllowed().map(l => l.id));
    return [...ids].map(id => getLayer(id)).filter((l): l is Layer => !!l && allowed.has(l.id) && (!!l.server || typeof l.load === 'function'));
  }
  private layerData(id: string): unknown {
    const d = this.data; if (!d) return null;
    const c = this.layerCache.get(layerKey(id, d.from, d.to));
    return c && !c.err ? c.data : null;
  }
  private layerErr(id: string): string | null {
    const d = this.data; if (!d) return null;
    const c = this.layerCache.get(layerKey(id, d.from, d.to));
    return c ? c.err : null;
  }
  private layerPending(id: string): boolean { const d = this.data; return !!d && this.layerInflight.has(layerKey(id, d.from, d.to)); }
  private clearLayers(): void {
    this.layerGen++;
    for (const c of this.layerCtrls) { try { c.abort(); } catch { /* gone */ } }
    this.layerCtrls = []; this.layerCache.clear(); this.layerInflight.clear(); this.layerSeqs.clear(); this.layerWaits.clear();
  }
  /** Fetch what the view needs and is not cached for this window; `force` refetches those ids (any registered layer the
   *  role may read, drawn by this view or not — ctx.reload({layers}) / ensureLayer). Server layers share ONE request.
   *  Resolves when every requested layer has landed. A newer request for a layer supersedes an older one in flight. */
  private fetchLayers(force?: string[], baseFailed = false): Promise<void> {
    const d = this.data;
    // baseFailed: reloadFor after a write whose base refetch failed: the named layers of the window on screen still refresh
    if (!d || this.forbidden || (this.error && !baseFailed)) return Promise.resolve();
    const gen = this.layerGen;
    const q = { from: d.from, to: d.to, as_of: d.as_of };
    const allowed = new Set(this.layersAllowed().map(l => l.id));
    const want = new Map<string, Layer>();
    for (const l of this.neededLayers()) want.set(l.id, l);
    for (const id of force || []) {
      const l = getLayer(id);
      if (l && allowed.has(l.id) && (!!l.server || typeof l.load === 'function')) want.set(l.id, l);
    }
    const waits: Promise<void>[] = [];
    const todo: Layer[] = [];
    for (const l of want.values()) {
      const key = layerKey(l.id, d.from, d.to);
      if ((force && force.includes(l.id)) || (!this.layerCache.has(key) && !this.layerInflight.has(key))) todo.push(l);
      else if (this.layerInflight.has(key)) { const w = this.layerWaits.get(key); if (w) waits.push(w); }
    }
    if (!todo.length) return Promise.all(waits).then(() => undefined);
    const seqOf = new Map<string, number>();
    for (const l of todo) {
      const key = layerKey(l.id, d.from, d.to);
      const n = (this.layerSeqs.get(key) || 0) + 1;
      this.layerSeqs.set(key, n); seqOf.set(key, n);
      this.layerInflight.add(key);
    }
    const done = (l: Layer, data: unknown, err: string | null) => {
      if (gen !== this.layerGen) return;
      const key = layerKey(l.id, d.from, d.to);
      if (this.layerSeqs.get(key) !== seqOf.get(key)) return;   // a newer request owns this layer
      this.layerInflight.delete(key); this.layerWaits.delete(key);
      // a failed REFRESH keeps the data already on screen; a failed first fetch records the error
      const prev = this.layerCache.get(key);
      if (err && prev && !prev.err) { this.queueDraw(); return; }
      this.layerCache.set(key, { data: err ? null : data, err });
      this.queueDraw();
    };
    const started: Promise<void>[] = [];
    const srv = todo.filter(l => !!l.server);
    if (srv.length) {
      const ids = [...new Set(srv.map(l => String(l.server)))];
      const ctrl = new AbortController(); this.layerCtrls.push(ctrl);
      const qs = `l=${encodeURIComponent(ids.join(','))}&from=${encodeURIComponent(d.from)}&to=${encodeURIComponent(d.to)}`;
      const p = fetch(`${API}/calendar/layers?${qs}`, { signal: ctrl.signal })
        .then(async r => ({ r, j: await r.json().catch(() => null) }))
        .then(({ r, j }) => {
          for (const l of srv) {
            const sid = String(l.server);
            if (!r.ok || !j) done(l, null, r.status === 404 ? 'this server has no calendar layers yet (redeploy)' : `HTTP ${r.status}`);
            else if (j.errors && j.errors[sid]) done(l, null, String(j.errors[sid]));
            else if (j.layers && Object.prototype.hasOwnProperty.call(j.layers, sid)) done(l, j.layers[sid], null);
            else done(l, null, 'not on this server yet');
          }
        })
        .catch(e => { if (ctrl.signal.aborted) return; for (const l of srv) done(l, null, (e && e.message) || 'no connection'); });
      for (const l of srv) this.layerWaits.set(layerKey(l.id, d.from, d.to), p);
      started.push(p);
    }
    for (const l of todo.filter(x => !x.server && typeof x.load === 'function')) {
      const ctrl = new AbortController(); this.layerCtrls.push(ctrl);
      const p = Promise.resolve().then(() => l.load!(q, ctrl.signal))
        .then(data => done(l, data, null))
        .catch(e => { if (ctrl.signal.aborted) return; done(l, null, (e && e.message) || 'failed'); });
      this.layerWaits.set(layerKey(l.id, d.from, d.to), p);
      started.push(p);
    }
    this.queueDraw();
    return Promise.all([...waits, ...started]).then(() => undefined);
  }
  /** Layer data landed: redraw what depends on it (stats, timeline, rail; the panel only when nobody is typing in it). */
  private queueDraw(): void {
    if (this.drawQueued) return;
    this.drawQueued = true;
    setTimeout(() => {
      this.drawQueued = false;
      if (!this.rootEl || !this.rootEl.isConnected) return;
      this.drawStats(); this.drawTimeline(); this.drawRail(); this.drawToolbarBits(false);
      const a = document.activeElement as HTMLElement | null;
      const typing = !!(a && a.closest('#stc-panel') && (a.tagName === 'INPUT' || a.tagName === 'TEXTAREA' || a.tagName === 'SELECT'));
      if (this.stack.length && !typing) this.drawPanel();
      if (this.showMenuOpen) this.drawShowMenu();
    }, 0);
  }

  // ── view / stat / rail ─────────────────────────────────────────────────
  private availableViews(): View[] { return viewsFor(this.role()); }
  private activeView(): View {
    return getView(this.view) || getView('stays') || { id: 'stays', label: 'Reservations', short: 'Stays', icon: '🗓', blurb: '', order: 10, roles: [], layers: { stays: 'on', inquiries: 'on', blocks: 'on', rates: 'on', turns: 'dim', wos: 'dim' }, stats: [] };
  }
  /** Resolve the view at render: deep link (transient) → this tab → the explicit default → Reservations. */
  private resolveViewNow(focus: CalFocus | null): void {
    const avail = this.availableViews().map(v => String(v.id));
    const r = resolveView({ focusView: focus ? focus.view : null, tabView: ss.get('stc_view'), savedView: ls.get(defaultViewKey(StaysCalendarPage.profile())), available: avail });
    this.view = r.view;
    this.viewTransient = r.source === 'focus';
    this.ovCache = null;
    this.stat = this.readStat();
  }
  private readStat(): string {
    const v = ss.get(`stc_stat:${this.view}`) || '';
    return v && this.activeView().stats.includes(v) ? v : '';
  }
  private setView(id: string): void {
    if (!getView(id) || !this.availableViews().some(v => v.id === id)) return;
    if (id === this.view && !this.viewTransient) return;
    this.view = id; this.viewTransient = false;
    ss.set('stc_view', id);
    this.ovCache = null; this.stat = this.readStat(); this.showAllRows = false; this.secOpen.clear();
    this.closeModPop();
    this.drawChrome(); this.drawTimeline(); this.drawRail(); this.drawPanel();
    this.fetchLayers();
  }
  private viewStats(): StatSpec[] { return this.activeView().stats.map(k => getStat(k)).filter((s): s is StatSpec => !!s).slice(0, 6); }
  private activeStat(): StatSpec | null { return this.stat ? (this.viewStats().find(s => s.key === this.stat) || null) : null; }
  private setStat(key: string): void {
    this.stat = key && key !== this.stat ? key : '';
    if (this.stat) ss.set(`stc_stat:${this.view}`, this.stat); else ss.del(`stc_stat:${this.view}`);
    if (this.stat) this.fetchLayers();
    this.drawStats(); this.drawTimeline();
  }
  private counts(): CoreStatCounts {
    const d = this.data || this.emptyData();
    if (this.countsMemo && this.countsMemo.d === this.data) return this.countsMemo.c;
    const c = coreStatCounts(d);
    this.countsMemo = { d: this.data, c };
    return c;
  }
  private viewRails(): RailProvider[] { return (this.activeView().rail || []).map(k => getRail(k)).filter((r): r is RailProvider => !!r); }
  private railItems(): Array<RailItem & { group: string }> {
    const out: Array<RailItem & { group: string }> = [];
    for (const r of this.viewRails()) for (const it of safe(() => r.items(this.ctx), [] as RailItem[])) if (it && it.entity && it.title) out.push({ ...it, group: r.label });
    return out;
  }
  /** The 📥 count: to-dos only — a row that opens a list (RailItem.meta, e.g. maint's "All 80 unscheduled") is not one. */
  private railCount(): number { return this.railItems().filter(it => !it.meta).length; }

  // ── window navigation (R115 B9) ──────────────────────────────────────
  private scroller(): HTMLElement | null { return document.querySelector<HTMLElement>('#stc-timeline .stc-scroller'); }
  private labelW(): number { const c = document.querySelector<HTMLElement>('#stc-timeline .stc-corner'); return c ? c.offsetWidth : 0; }
  private loadedWin(): { from: string; to: string; days: number; anchor: string | null } | null {
    const d = this.data; return d ? { from: d.from, to: d.to, days: d.days, anchor: d.anchor } : null;
  }
  private navWindow(dir: 'prev' | 'next' | 'today'): void {
    if (this.pendingScroll) return;   // R115 review: a window load is in flight — its scroll math would use the old grid
    const sc = this.scroller();
    const r = navStep({ dir, win: { from: this.from, days: this.days, extra: this.extra }, loaded: this.loadedWin(), scrollLeft: sc ? sc.scrollLeft : 0, viewport: sc ? sc.clientWidth - this.labelW() : 0, colW: this.colW });
    if (r.kind === 'scroll') { if (sc) sc.scrollLeft = r.left; return; }
    this.setWindow(r.win, r.left);
  }
  private setWindow(w: { from: string; days: number; extra: number }, left: number | null): void {
    this.from = w.from; this.days = w.days; this.extra = w.extra;
    this.pendingScroll = left == null ? null : { left };
    this.drawKey = '';
    this.closeModPop();
    this.drawToolbarBits();
    void this.load(true);
  }
  /** "‹ Earlier": 30 more past days, the visible dates stay put. */
  private earlier(): void {
    if (this.pendingScroll) return;   // R115 review: a second press before the first window lands would double-shift
    const sc = this.scroller();
    const r = earlierStep({ win: { from: this.from, days: this.days, extra: this.extra }, loaded: this.loadedWin(), scrollLeft: sc ? sc.scrollLeft : 0, colW: this.colW });
    if (!r) { this.toast('The calendar already shows the earliest days it can load (60 days back).'); return; }
    this.setWindow(r.win, r.left);
  }
  /** Returns true when it started a window load (that load lands on the day before `date`). */
  private jumpTo(date: string): boolean {
    if (!this.validDate(date)) return false;
    const r = jumpWindow({ date, win: { from: this.from, days: this.days, extra: this.extra }, loaded: this.loadedWin(), colW: this.colW });
    if (!r) return false;
    if (r.kind === 'scroll') { const sc = this.scroller(); if (sc) sc.scrollLeft = r.left; return false; }
    this.setWindow(r.win, r.left);
    return true;
  }

  // ── deep links (calendar/link.ts → sessionStorage stc_focus + 'stc:focus') ──
  private consumeFocus(): CalFocus | null {
    const raw = ss.get(FOCUS_KEY);
    if (raw == null) return null;
    ss.del(FOCUS_KEY);
    return parseFocus(raw);
  }
  /** Before the load: a window that contains the focus date (the anchored one when it already does). */
  private focusWindow(f: CalFocus): void {
    if (!f.date) return;
    const anchor = this.addDays(this.bizToday(), -1);
    const start = this.from || this.addDays(anchor, -this.extra);
    const end = this.addDays(start, windowColumns({ from: this.from, days: this.days, extra: this.extra }) - 1);
    if (f.date >= start && f.date <= end) return;
    this.from = this.addDays(f.date, -1); this.pendingScroll = { left: 0 };
  }
  /** After the load: scroll to the date, outline the row, open the entity. */
  private focusAfterLoad(f: CalFocus): void {
    if (f.date || f.listing_id) this.focusOn({ date: f.date, listing_id: f.listing_id });
    const lid = f.listing_id;
    if (f.stay_id) this.openRoot({ kind: 'stay', id: f.stay_id, listing_id: lid });
    else if (f.turn_id) this.openRoot({ kind: 'turn', id: f.turn_id, listing_id: lid });
    else if (f.wo_id) this.openRoot({ kind: 'wo', id: f.wo_id, listing_id: lid });
  }
  /** 'stc:focus' while the page is already open. */
  private async takeFocusNow(): Promise<void> {
    const f = this.consumeFocus();
    if (!f) return;
    if (f.view && f.view !== this.view && this.availableViews().some(v => v.id === f.view)) {
      // R115 review: the same switch as setView (rail, panel sections, quick card) — minus the stc_view write
      this.view = f.view; this.viewTransient = true; this.ovCache = null; this.stat = this.readStat(); this.showAllRows = false;
      this.secOpen.clear(); this.closeModPop();
      this.drawChrome(); this.drawRail(); this.drawPanel();
    }
    const d = this.data;
    if (f.date && (!d || f.date < d.from || f.date > d.to)) {
      this.from = this.addDays(f.date, -1); this.pendingScroll = { left: 0 }; this.drawKey = '';
      await this.load(true);
    } else { this.drawTimeline(); this.fetchLayers(); }
    this.focusAfterLoad(f);
  }
  /** ctx.focus: scroll the date column / the listing's row into view and outline the row for a moment. */
  private focusOn(o: { date?: string; listing_id?: string }): void {
    const d = this.data; const sc = this.scroller();
    if (!d || !sc) return;
    if (o.date && this.validDate(o.date)) {
      if (o.date < d.from || o.date > d.to) { this.jumpTo(o.date); return; }
      sc.scrollLeft = Math.max(0, this.diffDays(d.from, o.date) - 1) * this.colW;
    }
    if (o.listing_id) {
      const row = sc.querySelector<HTMLElement>(`[data-stc-row="${CSS.escape(o.listing_id)}"]`);
      if (row) {
        const hdr = sc.querySelector<HTMLElement>('.stc-hdr');
        sc.scrollTop = Math.max(0, row.offsetTop - (hdr ? hdr.offsetHeight : 0) - 8);
        sc.querySelectorAll('.stc-row-focus').forEach(x => x.classList.remove('stc-row-focus'));
        row.classList.add('stc-row-focus');
        setTimeout(() => row.classList.remove('stc-row-focus'), 2600);
      }
    }
  }

  private startTick(): void {
    if (this.tick) clearInterval(this.tick);
    this.tick = setInterval(() => {
      const root = document.getElementById('stc-root');
      if (!root || !root.isConnected) { if (this.tick) clearInterval(this.tick); this.tick = null; return; }
      this.drawUpdated();
    }, 30000);
  }
  private drawUpdated(): void {
    const el = document.getElementById('stc-updated');
    if (!el) return;
    if (this.error) { el.textContent = 'Not updated'; return; }
    if (!this.loadedAt) { el.textContent = ''; return; }
    const m = Math.round((Date.now() - this.loadedAt.getTime()) / 60000);
    el.textContent = m < 1 ? 'Updated just now' : `Updated ${m} min ago`;
  }

  private toast(msg: string): void {
    const el = document.getElementById('stc-toast');
    if (!el) return;
    this.undoFn = null;
    el.textContent = msg; el.classList.add('show'); el.classList.remove('stc-toast-act');
    if (this.toastTimer) clearTimeout(this.toastTimer);
    this.toastTimer = setTimeout(() => el.classList.remove('show'), 3500);
  }
  /** R112: a toast with an Undo button that lives UNDO_MS (6 s); the pending undo vetoes auto-refresh. */
  private toastUndo(msg: string, undo: () => void): void {
    const el = document.getElementById('stc-toast');
    if (!el) return;
    this.undoFn = undo;
    el.innerHTML = `<span>${this.esc(msg)}</span><button type="button" class="stc-toast-btn" data-stc-undo>Undo</button>`;
    el.classList.add('show', 'stc-toast-act');
    if (this.toastTimer) clearTimeout(this.toastTimer);
    this.toastTimer = setTimeout(() => this.clearUndo(), UNDO_MS);
  }
  private clearUndo(): void {
    this.undoFn = null;
    if (this.toastTimer) { clearTimeout(this.toastTimer); this.toastTimer = null; }
    const el = document.getElementById('stc-toast');
    if (el) { el.classList.remove('show', 'stc-toast-act'); }
  }
  private go(page: string, hubTab?: string): void {
    try { if (hubTab) sessionStorage.setItem('hub_tab', hubTab); } catch { /* private mode */ }
    document.dispatchEvent(new CustomEvent('app:navigate', { detail: page }));
  }
  // R114 review: Arrivals now lands on its Day view — hand it this stay's
  // arrival day (a future one; today follows today), make sure arrivals are
  // listed, and open that stay's checklist (lib/arrivalsDayView arrivalsHandoff).
  private openArrivals(): void {
    const s = this.findStay(this.selectedId);
    const today = this.data && this.validDate(this.data.as_of) ? this.data.as_of : this.bizToday();
    const ac = this.detail && s && this.detail.stay.id === s.id ? this.detail.arrival_check : null;
    try {
      const h = arrivalsHandoff(s && s.kind === 'stay' ? s.check_in : null, today, sessionStorage.getItem(RT_KEYS.mode), ac && ac.generated ? ac.id : null);
      for (const [k, v] of Object.entries(h.set)) sessionStorage.setItem(k, v);
      for (const k of h.remove) sessionStorage.removeItem(k);
    } catch { /* private mode: Arrivals opens on today */ }
    this.go('remote-ops', 'arrivals');
  }
  /** True while the user is typing somewhere — the single-key shortcuts stay out of the way. */
  private isTyping(): boolean {
    const a = document.activeElement as HTMLElement | null;
    if (!a) return false;
    const tag = a.tagName;
    return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || a.isContentEditable;
  }

  // ── chrome events (bound once per render; elements are recreated each render) ──
  private bindChrome(): void {
    const root = document.getElementById('stc-root');
    if (!root) return;
    const input = root.querySelector<HTMLInputElement>('#stc-q');
    input?.addEventListener('input', () => {
      if (this.qTimer) clearTimeout(this.qTimer);
      this.qTimer = setTimeout(() => { this.q = input.value.trim(); void this.load(true, false, 'keep'); }, 350);   // layers don't depend on the search
    });
    root.querySelector('[data-stc-refresh]')?.addEventListener('click', () => void this.load(true, true));
    root.querySelector('[data-stc-add]')?.addEventListener('click', () => this.addFromToolbar());
    root.querySelector('[data-stc-show]')?.addEventListener('click', (e) => { e.stopPropagation(); this.toggleShowMenu(e.currentTarget as HTMLElement); });
    root.querySelector('[data-stc-viewsheet]')?.addEventListener('click', () => this.openViewSheet());
    root.querySelector('[data-stc-qtoggle]')?.addEventListener('click', () => {
      this.searchOpen = !this.searchOpen;
      root.classList.toggle('stc-q-open', this.searchOpen);
      if (this.searchOpen) root.querySelector<HTMLInputElement>('#stc-q')?.focus();
    });
    root.querySelector('[data-stc-rail-btn]')?.addEventListener('click', () => this.railButton());
    root.querySelectorAll<HTMLElement>('[data-stc-nav]').forEach(b => b.addEventListener('click', () => this.navWindow(b.dataset.stcNav as 'prev' | 'next' | 'today')));
    root.querySelectorAll<HTMLElement>('[data-stc-win]').forEach(b => b.addEventListener('click', () => this.setDays(Number(b.dataset.stcWin))));
    bindKebabs(root.querySelector<HTMLElement>('#stc-toolbar')!);
    bindKebabs(root.querySelector<HTMLElement>('#stc-views')!);
    bindKebabs(root.querySelector<HTMLElement>('#stc-panel')!);
    // view tabs (delegated — redrawn on every view switch; the Operations ▾ shortcuts sit in a kebab bound per element)
    root.querySelector('#stc-views')?.addEventListener('click', (e) => {
      const b = (e.target as HTMLElement).closest<HTMLElement>('[data-stc-view]');
      if (b && !b.closest('[data-kebab-pop]')) this.setView(String(b.dataset.stcView || ''));
    });
    // stats: the KPI cards / chips ARE the one active filter
    root.querySelector('#stc-stats')?.addEventListener('click', (e) => {
      const t = e.target as HTMLElement;
      const b = t.closest<HTMLElement>('[data-stc-stat]');
      if (b) { this.setStat(String(b.dataset.stcStat || '')); return; }
      if (t.closest('[data-stc-layers-retry]')) this.fetchLayers(this.neededLayers().filter(l => !!this.layerErr(l.id)).map(l => l.id));
    });
    // the rail column (search + items)
    const rail = root.querySelector<HTMLElement>('#stc-rail');
    rail?.addEventListener('click', (e) => this.railClick(e, false));
    rail?.addEventListener('dragstart', (e) => this.railDragStart(e as DragEvent));
    rail?.addEventListener('input', (e) => { const t = e.target as HTMLInputElement; if (t.matches('[data-stc-rail-q]')) { this.railQ = t.value; this.patchRailList(rail); } });
    // timeline clicks (delegated — the grid is redrawn often)
    const tl = root.querySelector<HTMLElement>('#stc-timeline');
    tl?.addEventListener('click', (e) => this.timelineClick(e as MouseEvent));
    tl?.addEventListener('keydown', (e) => {   // bars / chips / labels are role=button — Enter / Space must open them
      const ke = e as KeyboardEvent;
      if (ke.key !== 'Enter' && ke.key !== ' ') return;
      const el = (ke.target as HTMLElement).closest<HTMLElement>('[data-stc-stay],[data-stc-block],[data-stc-wo],[data-stc-turn],[data-stc-chip],[data-stc-prop],[data-stc-date],[data-stc-rb]');
      if (!el) return;
      ke.preventDefault();
      el.click();
    });
    tl?.addEventListener('scroll', () => { if (this.modPopOpen && !this.showMenuOpen) this.closeModPop(); }, true);   // the anchor moved under a fixed popover — drop it
    // drag a work-order chip (R111), a not-done turn chip (R112) or a movable module chip (R115) to another day of the same row — desktop; phones use pickDay
    tl?.addEventListener('dragstart', (e) => {
      const de = e as DragEvent;
      const tgt = de.target as HTMLElement;
      const chip = tgt.closest<HTMLElement>('[data-stc-wo]');
      const wo = chip && this.data ? this.data.events.work_orders.find(w => w.id === chip.dataset.stcWo) : null;
      const tchip = chip ? null : tgt.closest<HTMLElement>('[data-stc-turn]');
      const turn = tchip && this.data ? this.data.events.turns.find(t => t.id === tchip.dataset.stcTurn) : null;
      const mchip = chip || tchip ? null : tgt.closest<HTMLElement>('[data-stc-chip-key]');
      const spec = mchip ? this.chipIndex.get(String(mchip.dataset.stcChipKey || '')) : null;
      if (wo) this.dragWo = { id: wo.id, listing_id: wo.listing_id, date: wo.date, title: wo.title, due_only: !!wo.due_only };
      else if (turn && !turn.done) this.dragTurn = { id: turn.id, listing_id: turn.listing_id, date: turn.date };
      else if (spec && spec.movable) this.dragChip = { ref: spec.entity, listing_id: spec.listing_id, kind: spec.entity.kind };
      else { if (turn || mchip) de.preventDefault(); return; }   // a done clean never moves (the server refuses too)
      this.closeModPop();
      try { de.dataTransfer?.setData('text/plain', wo ? wo.id : turn ? turn.id : spec!.entity.id); if (de.dataTransfer) de.dataTransfer.effectAllowed = 'move'; } catch { /* some engines refuse */ }
      root.classList.add('stc-dragging');
    });
    tl?.addEventListener('dragover', (e) => {
      const de = e as DragEvent;
      // R115 integ: a row dragged from the 📥 rail / the WO bucket carries 'application/x-stc-entity' (stashed at its dragstart)
      const ext = !this.dragWo && !this.dragTurn && !this.dragChip && !this.dragExt && StaysCalendarPage.hasExtType(de);
      const drag = this.dragWo || this.dragTurn || this.dragChip || this.dragExt;
      if (!drag && !ext) return;
      const lanes = (de.target as HTMLElement).closest<HTMLElement>('.stc-lanes');
      if (!lanes) return;
      de.preventDefault();
      if (de.dataTransfer) de.dataTransfer.dropEffect = !drag || !drag.listing_id || lanes.dataset.stcListing === drag.listing_id ? 'move' : 'none';
      this.dragWrongRow = !!drag && !!drag.listing_id && lanes.dataset.stcListing !== drag.listing_id;
      this.showDropCol(lanes, de.clientX);
    });
    tl?.addEventListener('drop', (e) => {
      const de = e as DragEvent;
      this.dragWrongRow = false;   // a drop happened — it answers for itself
      const drag = this.dragWo, dragT = this.dragTurn, dragC = this.dragChip;
      let dragE = this.dragExt;
      if (!drag && !dragT && !dragC && !dragE && StaysCalendarPage.hasExtType(de)) { try { dragE = StaysCalendarPage.parseExt(de.dataTransfer ? de.dataTransfer.getData(StaysCalendarPage.EXT_TYPE) : ''); } catch { dragE = null; } }
      const lanes = (de.target as HTMLElement).closest<HTMLElement>('.stc-lanes');
      if ((!drag && !dragT && !dragC && !dragE) || !lanes || !this.data) { this.endDrag(); return; }
      de.preventDefault();
      const col = this.showDropCol(lanes, de.clientX);
      const date = this.addDays(this.data.from, col);
      if (dragE) {   // R115 integ: rail / bucket → timeline: the entity kind's ChipKind.move decides (maint: book the WO that day)
        this.endDrag();
        const k = getChipKind(dragE.kind);
        if (!k || !k.move) { this.toast('That item can\'t be dropped on the calendar — open it to pick a day.'); return; }
        if (dragE.listing_id && lanes.dataset.stcListing !== dragE.listing_id) { this.toast('Drop it on its own home\'s row.'); return; }
        const ref: EntityRef = { ...dragE.ref, listing_id: dragE.listing_id || lanes.dataset.stcListing || undefined };
        if (!safe(() => k.move!.canDrop(ref, date, this.ctx), false)) { this.toast('That day is not allowed for this item.'); return; }
        void safe(() => k.move!.onDrop(ref, date, this.ctx), undefined);
        return;
      }
      const sameRow = lanes.dataset.stcListing === (drag ? drag.listing_id : dragT ? dragT.listing_id : dragC!.listing_id);
      this.endDrag();
      if (dragC) {
        const k = getChipKind(dragC.kind);
        if (!sameRow) { this.toast('Drop on the same property\'s row.'); return; }
        if (!k || !k.move) return;
        if (!safe(() => k.move!.canDrop(dragC.ref, date, this.ctx), false)) { this.toast('That day is not allowed for this item.'); return; }
        void safe(() => k.move!.onDrop(dragC.ref, date, this.ctx), undefined);
        return;
      }
      if (dragT) {
        if (!sameRow) { this.toast('Drop on the same property\'s row to move the clean.'); return; }
        if (date < this.data.as_of) { this.toast('Cleans can\'t move into the past.'); return; }
        if (date === dragT.date) return;
        const k = getChipKind('turn');
        const ref: EntityRef = { kind: 'turn', id: dragT.id, listing_id: dragT.listing_id, date: dragT.date };
        if (k && k.move && k !== StaysCalendarPage.coreChipKinds.get('turn')) { if (safe(() => k.move!.canDrop(ref, date, this.ctx), false)) void safe(() => k.move!.onDrop(ref, date, this.ctx), undefined); return; }
        void this.moveTurn(dragT.id, date, { confirm: true });
        return;
      }
      if (!drag) return;
      if (!sameRow) { this.toast('Drop on the same property\'s row to move the booked day.'); return; }
      if (date === drag.date && !drag.due_only) return;   // dropping a due-only chip on its own day BOOKS that day
      const k = getChipKind('wo');
      const ref: EntityRef = { kind: 'wo', id: drag.id, listing_id: drag.listing_id, date: drag.date };
      if (k && k.move && k !== StaysCalendarPage.coreChipKinds.get('wo')) { if (safe(() => k.move!.canDrop(ref, date, this.ctx), false)) void safe(() => k.move!.onDrop(ref, date, this.ctx), undefined); return; }
      void this.rescheduleWo(drag.id, drag.title, date);
    });
    tl?.addEventListener('dragend', (e) => this.dragEnded(e as DragEvent));
    // the quick popover host (ctx.openPop / the Show menu)
    const pop = root.querySelector<HTMLElement>('#stc-pop');
    pop?.addEventListener('click', (e) => {
      const t = e.target as HTMLElement;
      if (t.closest('[data-stc-pop-close]')) { this.closeModPop(); return; }
      const tri = t.closest<HTMLElement>('[data-stc-layer]');
      if (tri) { this.toggleLayer(String(tri.dataset.stcLayer || ''), String(tri.dataset.stcState || 'on') as LayerState); return; }
      if (t.closest('[data-stc-show-reset]')) { ss.del(`stc_layers:${this.view}`); this.ovCache = null; this.afterLayerChange(); return; }
    });
    pop?.addEventListener('change', (e) => {
      const t = e.target as HTMLInputElement;
      if (t.matches('[data-stc-hiddeninq]')) this.setShowHidden(t.checked);
    });
    // the Add modal
    const modal = root.querySelector<HTMLElement>('#stc-modal');
    modal?.addEventListener('click', (e) => {
      const t = e.target as HTMLElement;
      if (t.classList.contains('stc-ovl') || t.closest('[data-stc-add-close]')) { if (!this.addBusy) this.closeAdd(); return; }
      const kind = t.closest<HTMLElement>('[data-stc-add-kind]');
      if (kind) { this.syncAddForm(); this.addForm.kind = kind.dataset.stcAddKind === 'reservation' ? 'reservation' : 'block'; this.addErr = ''; this.drawAdd(); return; }
      const opt = t.closest<HTMLElement>('[data-stc-add-prop]');
      if (opt) { this.syncAddForm(); this.addForm.listing_id = opt.dataset.stcAddProp || ''; this.addPropQ = ''; this.drawAdd(); return; }
      if (t.closest('[data-stc-add-clearprop]')) { this.syncAddForm(); this.addForm.listing_id = ''; this.addPropQ = ''; this.drawAdd(); root.querySelector<HTMLInputElement>('[data-stc-add-propq]')?.focus(); return; }
      if (t.closest('[data-stc-add-review]')) { this.reviewAdd(); return; }
      if (t.closest('[data-stc-add-back]')) { if (!this.addBusy) { this.addStep = 'form'; this.addErr = ''; this.addConflicts = []; this.drawAdd(); } return; }
      if (t.closest('[data-stc-add-confirm]')) { void this.submitAdd(); return; }
    });
    modal?.addEventListener('input', (e) => {
      const t = e.target as HTMLInputElement;
      if (t.matches('[data-stc-add-propq]')) {
        this.addPropQ = t.value; this.addForm.listing_id = '';
        const list = modal.querySelector<HTMLElement>('[data-stc-add-proplist]');
        if (list) list.innerHTML = this.addPropListHtml();
        return;
      }
      if (t.dataset.stcAddF === 'check_in' || t.dataset.stcAddF === 'check_out') {
        this.syncAddForm();
        const n = modal.querySelector<HTMLElement>('[data-stc-add-nights]');
        if (n) n.textContent = this.addNightsLabel();
      }
    });
    // the form sheet host
    const sheet = root.querySelector<HTMLElement>('#stc-sheet');
    sheet?.addEventListener('click', (e) => {
      const t = e.target as HTMLElement;
      if (t.closest('[data-stc-sheet-close]') || t.classList.contains('stc-sheet-ovl')) { this.closeSheet(false); return; }
    });
    // the inspector panel (delegated: the core sections; module sections bind themselves, kebab items are bound per element)
    const panel = root.querySelector<HTMLElement>('#stc-panel');
    panel?.addEventListener('click', (e) => this.panelClick(e as MouseEvent));
    panel?.addEventListener('change', (e) => this.panelChange(e));
    panel?.addEventListener('dragstart', (e) => this.railDragStart(e as DragEvent));   // the To do list in the panel (900–1279px)
    panel?.addEventListener('focusout', (e) => {   // leaving a half-typed / past clean day snaps it back
      const inp = (e.target as HTMLElement).closest<HTMLInputElement>('.stc-pop-turn [data-stc-pop-date]');
      if (!inp || !this.popTurn || !this.data) return;
      const turn = this.data.events.turns.find(t => t.id === this.popTurn);
      if (!turn) return;
      const v = inp.value;
      if (this.validDate(v) && v >= this.data.as_of) return;
      const pending = this.popConfirm && this.popConfirm.kind === 'move' ? String(this.popConfirm.body.date || '') : '';
      inp.value = this.validDate(pending) ? pending : turn.date;
      if (this.popErr === 'Cleans can\'t move into the past.') { this.popErr = ''; this.patchTurnAsk(); }
    });
    panel?.addEventListener('input', (e) => { const t = e.target as HTMLInputElement; if (t.matches('[data-stc-rail-q]')) { this.railQ = t.value; this.patchRailList(panel); } });
    root.querySelector('#stc-toast')?.addEventListener('click', (e) => {
      if (!(e.target as HTMLElement).closest('[data-stc-undo]')) return;
      const fn = this.undoFn;
      this.clearUndo();
      if (fn) fn();
    });
    if (!this.keyBound) {
      this.keyBound = true;
      // R115 integ fix (D3): the inspector's action row is measured — re-fit it when the window (so the panel) resizes
      let fitT: ReturnType<typeof setTimeout> | null = null;
      window.addEventListener('resize', () => {
        const r = this.rootEl;
        if (!r || !r.isConnected) return;
        if (fitT) clearTimeout(fitT);
        fitT = setTimeout(() => { fitT = null; this.refitActions(); }, 120);
      });
      document.addEventListener('keydown', (e) => {
        const r = this.rootEl;
        if (!r || !r.isConnected) return;
        if (e.key === 'Escape') {
          if (document.querySelector('[data-kebab-pop]')) return;   // the ⋯ menu closes itself first
          if (this.modPopOpen) { this.closeModPop(); return; }
          if (this.sheetOpen) { this.closeSheet(false); return; }
          if (this.addOpen) { if (!this.addBusy) this.closeAdd(); return; }
          if (this.stack.length) this.backEntry();
          return;
        }
        if (this.addOpen || this.sheetOpen || e.metaKey || e.ctrlKey || e.altKey || this.isTyping()) return;
        if (e.key === 'ArrowLeft') { e.preventDefault(); this.navWindow('prev'); }
        else if (e.key === 'ArrowRight') { e.preventDefault(); this.navWindow('next'); }
        else if (e.key === 't' || e.key === 'T') { this.navWindow('today'); }
      });
      // R115 integ: a drag that starts OUTSIDE the timeline (a 📥 rail row, the WO bucket in the panel) and
      // carries 'application/x-stc-entity' {kind,id,listing_id} — stash it so dragover can show the
      // right / wrong row (getData is unreadable during dragover) and drop can route it to ChipKind.move
      document.addEventListener('dragstart', (e) => {
        const r = this.rootEl;
        if (!r || !r.isConnected) return;
        const t = e.target as HTMLElement | null;
        if (t && t.closest && t.closest('#stc-timeline')) return;   // the timeline's own chips
        let raw = '';
        try { raw = (e as DragEvent).dataTransfer ? (e as DragEvent).dataTransfer!.getData(StaysCalendarPage.EXT_TYPE) : ''; } catch { raw = ''; }
        this.dragExt = raw ? StaysCalendarPage.parseExt(raw) : null;
        if (this.dragExt) { this.closeModPop(); r.classList.add('stc-dragging'); }
      });
      document.addEventListener('dragend', (e) => { if (this.dragExt) this.dragEnded(e as DragEvent); });
      // a click anywhere outside the quick popover (and off its anchor) closes it — never a click in a ⋯ menu
      // (Kebab moves menus to <body>), the lightbox or the form sheet
      document.addEventListener('click', (e) => {
        if (!this.modPopOpen || this.modPopSkip) return;
        const t = e.target as HTMLElement;
        if (!t || !t.isConnected) return;
        if (t.closest('#stc-pop') || t.closest('[data-kebab-pop]') || t.closest('.sx-lb') || t.closest('#stc-sheet') || t.closest('[data-stc-show]') || t.closest('[data-stc-chip]')) return;
        this.closeModPop();
      });
    }
  }
  private setDays(w: number): void {
    if (!WINS.includes(w) || w === this.days) return;
    const sc = this.scroller();
    this.setWindow({ from: this.from, days: w, extra: this.extra }, this.from ? (sc ? sc.scrollLeft : 0) : null);
  }

  /** Timeline click → the entity it hit opens in the inspector (a FRESH stack); an empty cell → the chooser (Reservations: one-tap hold). */
  private timelineClick(e: MouseEvent): void {
    const t = e.target as HTMLElement;
    if (t.closest('[data-stc-earlier]')) { this.earlier(); return; }
    if (t.closest('[data-stc-showall]')) { this.showAllRows = true; this.stat = ''; ss.del(`stc_stat:${this.view}`); this.drawStats(); this.drawTimeline(); return; }
    const rb = t.closest<HTMLElement>('[data-stc-rb]');
    if (rb) { const [kind, id, lid] = String(rb.dataset.stcRb || '').split('|'); if (kind && id) this.openRoot({ kind, id, listing_id: lid || undefined }); return; }
    const day = t.closest<HTMLElement>('[data-stc-date]');
    if (day && getInspector('day')) { const dt = String(day.dataset.stcDate || ''); if (this.validDate(dt)) this.openRoot({ kind: 'day', id: dt, date: dt }); return; }
    const lbl = t.closest<HTMLElement>('[data-stc-prop]');
    if (lbl) { const id = String(lbl.dataset.stcProp || ''); if (id) this.openRoot({ kind: 'listing', id, listing_id: id }); return; }
    const bar = t.closest<HTMLElement>('[data-stc-stay]');
    if (bar) { const s = this.findStay(bar.dataset.stcStay || null); if (s) this.openRoot({ kind: 'stay', id: s.id, listing_id: s.listing_id, date: s.check_in }); return; }
    const wo = t.closest<HTMLElement>('[data-stc-wo]');
    if (wo) { this.openChip('wo', String(wo.dataset.stcWo || ''), wo); return; }
    const turn = t.closest<HTMLElement>('[data-stc-turn]');
    if (turn) { this.openChip('turn', String(turn.dataset.stcTurn || ''), turn); return; }
    const mchip = t.closest<HTMLElement>('[data-stc-chip-key]');
    if (mchip) {
      const spec = this.chipIndex.get(String(mchip.dataset.stcChipKey || ''));
      if (spec) {
        if (!this.replaceStack()) return;
        const k = getChipKind(spec.entity.kind);   // contract-ext 4: the chip's entity kind picks its ChipKind (MTR / ops / maint kinds included)
        if (k) safe(() => { k.open(spec.entity, mchip, this.ctx); return true; }, true); else this.pushEntry(spec.entity);
        if (!this.stack.length) this.panelClosed();   // a quick card only (e.g. MTR's try-MTR / tour cards): the panel must not keep the old entity
      }
      return;
    }
    const blk = t.closest<HTMLElement>('[data-stc-block]');
    if (blk) { const key = String(blk.dataset.stcBlock || ''); const [lid] = key.split('|'); this.openRoot({ kind: 'block', id: key, listing_id: lid }); return; }
    if (t.closest('.stc-dropcol') || t.closest('.stc-ops-chip')) return;
    const lanes = t.closest<HTMLElement>('.stc-lanes');
    if (lanes) this.vacantClick(lanes, e);
  }
  /** The built-in turn / WO chips open through their chip kind (a feature may have replaced it — maint's WO editor). */
  private openChip(kind: 'turn' | 'wo', id: string, anchor: HTMLElement): void {
    const d = this.data; if (!d || !id) return;
    const ev = kind === 'turn' ? d.events.turns.find(x => x.id === id) : d.events.work_orders.find(x => x.id === id);
    if (!ev) return;
    const ref: EntityRef = { kind, id, listing_id: ev.listing_id, date: ev.date };
    const top = this.top();
    if (top && this.stack.length === 1 && sameRef(top.ref, ref)) { this.closeInspectorAll(); return; }   // a second click on the open chip closes it (the old popover toggle)
    if (!this.replaceStack()) return;
    const k = getChipKind(kind);   // maint re-registers 'wo' (its full editor + the book move); core's turn kind stays
    if (k) safe(() => { k.open(ref, anchor, this.ctx); return true; }, true); else this.pushEntry(ref);
    if (!this.stack.length) this.panelClosed();
  }
  /** "+ Add": the chooser — or, in Reservations when holding is the only thing that can be added, today's Add modal straight away. */
  private addFromToolbar(): void {
    const d = this.data;
    const cell = this.cellOf('', d ? d.as_of : this.bizToday());
    const acts = this.dateActions(cell);
    if (this.view === 'stays' && acts.length === 1 && acts[0].key === 'hold') { this.openAdd(); return; }
    this.openRoot(this.dateRef('', cell.date));
  }

  // ── draw ───────────────────────────────────────────────────────────────
  private draw(): void {
    this.drawChrome();
    this.drawTimeline();
    this.drawRail();
    this.syncLegacy();
    this.drawPanel();
    this.drawUpdated();
  }
  /** Everything above the grid that depends on the view: tabs, blurb, the phone View button, the ⋯ menu, stats. */
  private drawChrome(): void {
    this.drawViews();
    this.drawToolbarBits();
    this.drawStats();
  }
  private drawViews(): void {
    const v = this.activeView();
    const el = document.getElementById('stc-views');
    const views = this.availableViews();
    if (el) {
      const top = views.filter(x => !x.parent);
      el.innerHTML = `<div class="seg-tabs" role="tablist" aria-label="Calendar views">${top.map(t => {
        const kids = views.filter(x => x.parent === t.id);
        const on = v.id === t.id || v.parent === t.id;
        const cur = on && v.parent === t.id ? v : t;
        const label = cur === t ? t.label : `${t.label} · ${cur.label}`;
        const main = `<button type="button" class="seg-tab${on ? ' active' : ''}" role="tab" aria-selected="${on ? 'true' : 'false'}" data-stc-view="${this.esc(t.id)}" title="${this.esc(t.blurb)}"><span class="stc-vic">${this.esc(cur.icon)}</span>${this.esc(label)}</button>`;
        if (!kids.length) return main;
        const items: KebabItem[] = [t, ...kids].map(x => ({ label: x === t ? `All ${t.label.toLowerCase()}` : x.label, icon: x.icon, title: x.blurb, attrs: { 'data-stc-view': String(x.id), 'data-stc-vpick': '1' } }));
        return `<span class="stc-vtab-wrap${on ? ' active' : ''}">${main}${kebabHtml(items, { label: '▾', tip: `${t.label} shortcuts`, cls: 'stc-vcaret', title: `${t.label} views` })}</span>`;
      }).join('')}</div>${this.subViewsHtml(v, views)}`;
      el.querySelectorAll<HTMLElement>('[data-stc-vpick]').forEach(b => b.addEventListener('click', () => this.setView(String(b.dataset.stcView || ''))));
    }
    const blurb = document.getElementById('stc-blurb');
    if (blurb) blurb.textContent = v.blurb || '';
    const vb = document.querySelector<HTMLElement>('[data-stc-viewsheet]');
    if (vb) vb.innerHTML = `<span class="stc-vlbl">${this.esc(v.short || v.label)}</span>&nbsp;▾`;
  }
  /** R115 review (A2 / A3): when Operations or one of its views is on, its views are a visible chip row on desktop
   *  ("All operations · 🧹 Cleans & Inspections · 🔧 Maintenance") — not only an 11px ▾. Phones use the View sheet. */
  private subViewsHtml(v: View, views: View[]): string {
    const parentId = v.parent || v.id;
    const kids = views.filter(x => x.parent === parentId);
    const parent = views.find(x => x.id === parentId);
    if (!kids.length || !parent) return '';
    return `<div class="stc-subviews stc-desk" role="tablist" aria-label="${this.esc(parent.label)} views">${[parent, ...kids].map(x => {
      const on = x.id === v.id;
      return `<button type="button" class="chip${on ? ' active' : ''}" role="tab" aria-selected="${on ? 'true' : 'false'}" data-stc-view="${this.esc(x.id)}" title="${this.esc(x.blurb)}">${this.esc(x.icon)} ${this.esc(x === parent ? `All ${parent.label.toLowerCase()}` : x.label)}</button>`;
    }).join('')}</div>`;
  }
  /** The toolbar's live bits: the window chips, the rail count, and (menu = true) the ⋯ menu. */
  private drawToolbarBits(menu = true): void {
    document.querySelectorAll<HTMLElement>('#stc-root [data-stc-win]').forEach(x => x.classList.toggle('active', Number(x.dataset.stcWin) === this.days));
    const rails = this.viewRails();
    const rb = document.querySelector<HTMLElement>('[data-stc-rail-btn]');
    if (rb) {
      rb.hidden = !rails.length;
      const n = rails.length ? this.railCount() : 0;
      const nEl = rb.querySelector('[data-stc-rail-n]'); if (nEl) nEl.textContent = String(n);
      rb.classList.toggle('on', !this.railCollapsed && window.innerWidth >= 1280);
    }
    const more = document.getElementById('stc-more');
    if (more && menu) {
      const v = this.activeView();
      const key = defaultViewKey(StaysCalendarPage.profile());
      const isDefault = ls.get(key) === v.id;
      const earlierOk = canEarlier({ from: this.from, days: this.days, extra: this.extra });
      const items: KebabItem[] = [
        ...WINS.map(w => ({ label: `${w / 7} weeks${this.days === w ? ' ✓' : ''}`, icon: '▦', attrs: { 'data-stc-menu': `win:${w}` } })),
        { divider: true },
        { label: 'Refresh', icon: '↻', attrs: { 'data-stc-menu': 'refresh' } },
        { label: 'Show layers & key…', icon: '◧', attrs: { 'data-stc-menu': 'show' } },
        { label: 'Jump to date…', icon: '📅', attrs: { 'data-stc-menu': 'jump' } },
        { label: 'Earlier 30 days', icon: '‹', disabled: !earlierOk, attrs: { 'data-stc-menu': 'earlier' } },
        { divider: true },
        { label: isDefault ? `${v.label} is my default · clear` : `Make ${v.label} my default`, icon: isDefault ? '★' : '☆', attrs: { 'data-stc-menu': 'default' } },
      ];
      more.innerHTML = kebabHtml(items, { tip: 'More', title: 'Calendar' });
      more.querySelectorAll<HTMLElement>('[data-stc-menu]').forEach(b => b.addEventListener('click', () => this.menuPick(String(b.dataset.stcMenu || ''), more)));
    }
  }
  private menuPick(cmd: string, anchor: HTMLElement): void {
    if (cmd.startsWith('win:')) { this.setDays(Number(cmd.slice(4))); return; }
    if (cmd === 'refresh') { void this.load(true, true); return; }
    if (cmd === 'show') { this.toggleShowMenu(anchor); return; }
    if (cmd === 'earlier') { this.earlier(); return; }
    if (cmd === 'default') { this.toggleDefault(); return; }
    if (cmd === 'jump') {
      const d = this.data;
      const body = this.openSheet(`<label class="stc-field" style="display:flex;flex-direction:column;gap:6px;"><span class="stc-dl">Go to a day</span><input type="date" class="input" data-stc-jump value="${this.esc(d ? d.as_of : this.bizToday())}"></label>
        <div class="stc-fine">Up to 60 days back loads on demand; any later day re-centres the calendar.</div>
        <div class="stc-sheet-foot"><button type="button" class="btn btn-secondary" data-stc-sheet-close>Cancel</button><button type="button" class="btn btn-primary" data-stc-jump-go>Go</button></div>`, { title: 'Jump to date' });
      body.querySelector('[data-stc-jump-go]')?.addEventListener('click', () => {
        const v = (body.querySelector<HTMLInputElement>('[data-stc-jump]') || { value: '' }).value;
        if (!this.validDate(v)) { this.toast('Pick a date first.'); return; }
        this.closeSheet(true); if (!this.jumpTo(v)) setTimeout(() => this.focusOn({ date: v }), 60);   // a fetch already lands on the day before it
      });
    }
  }
  private toggleDefault(): void {
    const key = defaultViewKey(StaysCalendarPage.profile());
    const v = this.activeView();
    if (ls.get(key) === v.id) { ls.del(key); this.toast('Cleared — the calendar opens on Reservations in a new tab.'); }
    else { ls.set(key, String(v.id)); this.toast(`${v.label} is now your default view.`); }
    this.drawToolbarBits();
    if (this.sheetOpen && document.querySelector('[data-stc-vsheet]')) this.openViewSheet();
  }
  /** Phone: "View ▾" → a bottom sheet with icon + label + one-line blurb + "Make this my default". */
  private openViewSheet(): void {
    const v = this.activeView();
    const views = this.availableViews();
    const isDefault = ls.get(defaultViewKey(StaysCalendarPage.profile())) === v.id;
    const rows = views.map(x => `<button type="button" class="stc-vrow${x.id === v.id ? ' on' : ''}${x.parent ? ' sub' : ''}" data-stc-vrow="${this.esc(x.id)}"><span class="stc-vrow-ic">${this.esc(x.icon)}</span><span class="stc-vrow-txt"><b>${this.esc(x.label)}</b><span>${this.esc(x.blurb)}</span></span>${x.id === v.id ? '<span class="stc-vrow-on">✓</span>' : ''}</button>`).join('');
    const body = this.openSheet(`<div class="stc-vlist" data-stc-vsheet>${rows}</div>
      <div class="stc-sheet-foot"><button type="button" class="btn btn-secondary" data-stc-vdefault>${isDefault ? `★ ${this.esc(v.label)} is my default · clear` : `☆ Make ${this.esc(v.label)} my default`}</button></div>`, { title: 'Choose a view' });
    body.querySelectorAll<HTMLElement>('[data-stc-vrow]').forEach(b => b.addEventListener('click', () => { this.closeSheet(true); this.setView(String(b.dataset.stcVrow || '')); }));
    body.querySelector('[data-stc-vdefault]')?.addEventListener('click', () => this.toggleDefault());
  }

  // ── stats: KPI cards (desktop, first 4) + chips (the rest; on phones ONE scrolling chip row) ──
  private statValue(s: StatSpec): { v: StatValue | null; pending: boolean } {
    if (s.layer && this.layerPending(s.layer) && this.layerData(s.layer) == null) return { v: null, pending: true };   // a refresh keeps the last value
    if (s.layer && this.layerErr(s.layer)) return { v: null, pending: false };
    return { v: safe<StatValue | null>(() => s.compute(this.ctx), null), pending: false };
  }
  private drawStats(): void {
    const el = document.getElementById('stc-stats');
    if (!el) return;
    const stats = this.viewStats();
    const act = this.activeStat();
    const valTxt = (x: { v: StatValue | null; pending: boolean }) => (x.pending ? '…' : x.v && x.v.value != null && x.v.value !== '' ? String(x.v.value) : '—');
    const card = (s: StatSpec) => {
      const x = this.statValue(s);
      const on = !!act && act.key === s.key;
      const tone = x.v && x.v.tone ? ` tone-${x.v.tone}` : '';
      return `<button type="button" class="kpi-card clickable stc-kpi${tone}${on ? ' stc-kpi-on' : ''}" data-stc-stat="${this.esc(s.key)}" aria-pressed="${on ? 'true' : 'false'}" title="${on ? 'Showing only these — click again for everything' : 'Show only these on the calendar'}">
        <div class="kpi-card-top"><span class="kpi-card-label">${this.esc(s.label)}</span><span class="kpi-card-ic">${glyph(s.icon, 17)}</span></div>
        <div class="kpi-card-value">${this.esc(valTxt(x))}</div>${x.v && x.v.sub ? `<div class="stc-kpi-sub${x.v.tone === 'warn' || x.v.tone === 'bad' ? ' stc-kpi-sub-bad' : ''}">${this.esc(x.v.sub)}</div>` : ''}
      </button>`;
    };
    const chip = (s: StatSpec) => {
      const x = this.statValue(s);
      const on = !!act && act.key === s.key;
      return `<button type="button" class="chip stc-statchip${on ? ' active' : ''}${x.v && x.v.tone ? ` tone-${x.v.tone}` : ''}" data-stc-stat="${this.esc(s.key)}" aria-pressed="${on ? 'true' : 'false'}"><span class="stc-sc-ic">${glyph(s.icon, 14)}</span>${this.esc(s.label)}<span class="chip-count">${this.esc(valTxt(x))}</span></button>`;
    };
    const errs = this.neededLayers().filter(l => !!this.layerErr(l.id));
    const banner = errs.length ? `<div class="stc-banner" role="status">⚠ ${this.esc(errs.map(l => l.label).join(', '))} ${errs.length === 1 ? 'is' : 'are'} unavailable right now — the calendar itself is fine. <button type="button" class="stc-link" data-stc-layers-retry>Try again</button></div>` : '';
    el.innerHTML = stats.length
      ? `<div class="kpi-grid stc-kpis">${stats.slice(0, 4).map(card).join('')}</div>
         <div class="stc-statchips stc-desk">${stats.slice(4).map(chip).join('')}</div>
         <div class="stc-statchips stc-phone">${stats.map(chip).join('')}</div>${banner}`
      : banner;
  }

  // ── the Show menu: one row per layer (swatch = the legend entry, count, On / Dim / Off) ──
  private toggleShowMenu(anchor: HTMLElement): void {
    if (this.showMenuOpen) { this.closeModPop(); return; }
    this.openModPop(anchor, this.showMenuHtml(), { width: 380, cls: 'stc-showpop', onClose: () => { this.showMenuOpen = false; } });
    this.showMenuOpen = true;
  }
  private drawShowMenu(): void {
    const el = document.querySelector<HTMLElement>('#stc-pop .stc-showpop');
    if (el) el.innerHTML = this.showMenuHtml();
  }
  private showMenuHtml(): string {
    const ov = this.overrides();
    const layers = this.layersAllowed();
    const order = (l: Layer) => { const i = CORE_LAYER_IDS.indexOf(l.id); return i >= 0 ? i : 100; };
    const rows = [...layers].sort((a, b) => order(a) - order(b)).map(l => {
      const st = this.layerState(l.id);
      const loaded = !(l.server || l.load) || (!!this.data && this.layerCache.has(layerKey(l.id, this.data.from, this.data.to)));
      const n = this.layerPending(l.id) && this.layerData(l.id) == null ? '…' : !loaded ? '' : (() => { const c = l.count ? safe<number | null>(() => l.count!(this.ctx), null) : null; return c == null ? '' : String(c); })();
      const coreCls = CORE_LEGEND[l.id];
      const sw = l.id === 'turns' ? '<span class="stc-ops-chip stc-ops-turn stc-ops-busy stc-lg-chip">◐</span>'
        : l.id === 'wos' ? '<span class="stc-ops-chip stc-ops-wo stc-ops-p-routine stc-lg-chip">🔧</span>'
        : l.id === 'rates' ? '<span class="stc-show-sw" style="background:var(--bg-subtle); border-color: var(--border);"></span>'
        : coreCls ? `<span class="stc-show-sw stc-lg-sw ${coreCls}"></span>` : `<span class="stc-show-sw" style="background:${this.esc(cssBg(l.swatch))}"></span>`;
      const btn = (s: LayerState, lbl: string) => `<button type="button" class="${st === s ? 'on' : ''}" data-stc-layer="${this.esc(l.id)}" data-stc-state="${s}" aria-pressed="${st === s ? 'true' : 'false'}">${lbl}</button>`;
      const err = this.layerErr(l.id);
      return `<div class="stc-show-row" data-stc-show-row="${this.esc(l.id)}">${sw}<span class="stc-show-lbl">${this.esc(l.label)}${err ? ' <span class="stc-muted">· unavailable</span>' : ''}</span><span class="stc-show-n">${this.esc(n)}</span><span class="stc-tri" role="group" aria-label="${this.esc(l.label)}">${btn('on', 'On')}${btn('dim', 'Dim')}${btn('off', 'Off')}</span></div>`;
    }).join('');
    const hidden = this.data && Number.isFinite(Number(this.data.filters.hidden_inquiries)) ? Number(this.data.filters.hidden_inquiries) : this.hiddenInqCount();
    const it = (sw: string, label: string) => `<span class="stc-lg"><span class="stc-lg-sw ${sw}"></span>${label}</span>`;
    const key = it('stc-lg-inhouse', 'In house') + it('stc-lg-upcoming', 'Upcoming') + it('stc-lg-out', 'Checked out') + it('stc-lg-inq', 'Inquiry') + it('stc-lg-owner', 'Owner stay') + it('stc-lg-block', 'Blocked') + it('stc-lg-neg', 'Guest unhappy')
      + `<span class="stc-lg"><span class="stc-badge stc-badge-opp stc-lg-badge">$</span>Opportunity</span><span class="stc-lg"><span class="stc-badge stc-badge-msg stc-lg-badge">💬</span>Flagged messages</span><span class="stc-lg"><span class="stc-badge stc-badge-rv stc-lg-badge">★</span>Review</span><span class="stc-lg"><span class="stc-turn stc-turn-ready stc-lg-badge"></span>Turnover</span>`;
    return `<div class="stc-show">
      <div class="stc-show-head"><b>Show on the calendar · ${this.esc(this.activeView().label)}</b>${Object.keys(ov).length ? `<button type="button" class="stc-link" data-stc-show-reset title="Back to what this view shows by default">Reset to view</button>` : ''}<button type="button" class="stc-close" data-stc-pop-close aria-label="Close">✕</button></div>
      ${rows}
      <label class="stc-show-opt"><input type="checkbox" data-stc-hiddeninq ${this.showHiddenInq ? 'checked' : ''}> Show inquiries that can't happen or were dismissed${hidden > 0 ? ` (${hidden})` : ''}</label>
      <div class="stc-show-key">${key}</div>
    </div>`;
  }
  private toggleLayer(id: string, st: LayerState): void {
    if (!getLayer(id) || !['on', 'dim', 'off'].includes(st)) return;
    this.setLayerState(id, st);
    this.afterLayerChange();
  }
  /** A layer toggle NEVER refetches the base payload — it redraws, and fetches a newly needed layer once for this window. */
  private afterLayerChange(): void {
    this.drawTimeline(); this.drawStats(); this.drawRail(); this.drawToolbarBits(false);
    if (this.showMenuOpen) this.drawShowMenu();
    this.fetchLayers();
  }
  private setShowHidden(on: boolean): void {
    this.showHiddenInq = on;
    ss.set('stc_show_hidden_inq', on ? '1' : '0');
    if (on && this.layerState('inquiries') === 'off') this.setLayerState('inquiries', 'on');
    void this.load(true, false, 'keep');   // hidden inquiries are off the wire unless asked (hidden=1); layers don't depend on them
  }
  private hiddenInqCount(): number { return this.data ? this.data.stays.filter(s => this.isHiddenInq(s)).length : 0; }
  /** Inquiries the server flagged as impossible, or that an office user dismissed — hidden unless "show hidden" is on. */
  private isHiddenInq(s: Stay): boolean { return s.kind === 'inquiry' && !!(s.impossible || s.dismissed); }

  // ── the "📥 To do" rail: in-flow ≥1280px (collapsible), else a toolbar count that opens it in the inspector ──
  private railButton(): void {
    if (window.innerWidth >= 1280 && !this.stack.length) {
      this.railCollapsed = !this.railCollapsed;
      ls.set('stc_rail_collapsed', this.railCollapsed ? '1' : '0');
      this.drawRail(); this.drawToolbarBits();
      return;
    }
    this.openRoot({ kind: 'rail', id: String(this.view) });
  }
  private drawRail(): void {
    const el = document.getElementById('stc-rail');
    if (!el) return;
    const show = this.viewRails().length > 0 && !this.railCollapsed;
    el.hidden = !show;
    if (!show) { el.innerHTML = ''; return; }
    const focusQ = document.activeElement && (document.activeElement as HTMLElement).matches && (document.activeElement as HTMLElement).matches('#stc-rail [data-stc-rail-q]');
    if (focusQ && el.querySelector('[data-stc-rail-list]')) { this.patchRailList(el); return; }
    el.innerHTML = `<div class="stc-rail-head">📥 To do <span class="stc-count">${this.railCount()}</span><button type="button" class="stc-close" data-stc-rail-close aria-label="Hide the To do list" title="Hide">✕</button></div>${this.railListHtml(false)}`;
  }
  private railListHtml(inPanel: boolean): string {
    const items = this.railItems();
    return `${items.length > 6 || this.railQ ? `<input class="input stc-rail-q" type="search" placeholder="Search the list" data-stc-rail-q value="${this.esc(this.railQ)}">` : ''}<div class="stc-rlist" data-stc-rail-list>${this.railRowsHtml(items)}</div>${inPanel ? '' : ''}`;
  }
  private railRowsHtml(items: Array<RailItem & { group: string }>): string {
    const q = this.railQ.trim().toLowerCase();
    const hit = (it: RailItem & { group: string }) => !q || [it.search || '', it.title, it.sub || '', it.listing_id ? this.propName(it.listing_id) : ''].join(' ').toLowerCase().includes(q);
    const shown = items.filter(hit);
    if (!items.length) return `<div class="stc-rempty">Nothing to do here right now.</div>`;
    if (!shown.length) return `<div class="stc-rempty">Nothing matches "${this.esc(this.railQ.trim())}".</div>`;
    const groups = new Map<string, Array<RailItem & { group: string }>>();
    for (const it of shown) { if (!groups.has(it.group)) groups.set(it.group, []); groups.get(it.group)!.push(it); }
    let i = 0;
    return [...groups.entries()].map(([g, list]) => `<div class="stc-rgroup"><span>${this.esc(g)}</span><span>${list.filter(x => !x.meta).length}</span></div>${list.slice(0, 200).map(it => {
      const idx = items.indexOf(it); i++;
      const drag = this.railDraggable(it) ? ' draggable="true" title="Click to open — or drag onto its home\'s row to pick the day"' : '';
      const home = it.listing_id ? this.propName(it.listing_id) : '';
      const sub = [home && !it.title.includes(home) && !(it.sub || '').includes(home) ? home : '', it.sub || ''].filter(Boolean).join(' · ');   // R115 integ: never the home twice
      return `<button type="button" class="stc-ritem" data-stc-ritem="${idx}"${it.meta ? ' data-stc-rmeta' : ''}${drag}><span class="stc-rdot tone-${toneOf(it.tone)}"></span><span class="stc-rtxt"><span class="stc-rtitle">${this.esc(it.title)}</span>${sub ? `<span class="stc-rsub">${this.esc(sub)}</span>` : ''}</span>${it.date && this.validDate(it.date) ? `<span class="stc-rdate">${this.esc(this.fmtMD(it.date))}</span>` : ''}</button>`;
    }).join('')}`).join('');
  }
  /** R115 integ: a rail item drags onto the timeline when its kind has a ChipKind.move and a home (fine pointers only — phones use pickDay). */
  private railDraggable(it: RailItem): boolean {
    if (this.coarse || !it.listing_id || !it.entity) return false;
    const k = getChipKind(it.entity.kind);
    return !!(k && k.move);
  }
  private railDragStart(e: DragEvent): void {
    const b = (e.target as HTMLElement).closest<HTMLElement>('[data-stc-ritem][draggable="true"]');
    if (!b || !e.dataTransfer) return;
    const it = this.railItems()[Number(b.dataset.stcRitem)];
    if (!it || !this.railDraggable(it)) { e.preventDefault(); return; }
    try {
      e.dataTransfer.setData(StaysCalendarPage.EXT_TYPE, JSON.stringify({ kind: it.entity.kind, id: it.entity.id, listing_id: it.listing_id }));
      e.dataTransfer.setData('text/plain', `${it.entity.kind}:${it.entity.id}`);
      e.dataTransfer.effectAllowed = 'move';
    } catch { /* some engines refuse */ }
  }
  private patchRailList(host: HTMLElement): void {
    const list = host.querySelector<HTMLElement>('[data-stc-rail-list]');
    if (list) list.innerHTML = this.railRowsHtml(this.railItems());
  }
  private railClick(e: Event, inPanel: boolean): void {
    const t = e.target as HTMLElement;
    if (t.closest('[data-stc-rail-close]')) { this.railCollapsed = true; ls.set('stc_rail_collapsed', '1'); this.drawRail(); this.drawToolbarBits(); return; }
    const b = t.closest<HTMLElement>('[data-stc-ritem]');
    if (!b) return;
    const it = this.railItems()[Number(b.dataset.stcRitem)];
    if (!it) return;
    this.focusOn({ date: it.date || undefined, listing_id: it.listing_id || undefined });
    if (inPanel) this.pushEntry(it.entity); else this.openRoot(it.entity);
  }

  // ── the timeline ───────────────────────────────────────────────────────
  // R115: rows = the view's rowFilter / the active stat / rowSort; stays,
  // inquiries, blocks, rates, turns and WOs honour their layer's On / Dim /
  // Off; module chips (Layer.chips) pack into the ops sub-lanes beside the
  // built-in turn / WO chips; row badges (Layer.badges) sit under the name;
  // bars take Layer.decorateStay; ONE shared shade layer (weekends / today)
  // for every row instead of per-row divs; "‹ Earlier" at the left edge.
  private turnSpec(t: TurnEv): ChipSpec {
    return { key: `turn:${t.id}`, entity: { kind: 'turn', id: t.id, listing_id: t.listing_id, date: t.date }, listing_id: t.listing_id, start: t.date,
      tone: t.at_risk ? 'bad' : t.unassigned ? 'warn' : t.done ? 'ok' : 'info', glyph: t.done ? '✓' : t.unassigned ? '⚠' : '◐', label: t.assignees.join(', ') || 'Unassigned',
      title: `Turnover ${t.date}`, initials: t.initials, ring: t.at_risk, dashed: t.unassigned, movable: !t.done };
  }
  private woSpec(w: WoEv): ChipSpec {
    const hot = w.priority === 'emergency' || w.priority === 'urgent';
    return { key: `wo:${w.id}`, entity: { kind: 'wo', id: w.id, listing_id: w.listing_id, date: w.date }, listing_id: w.listing_id, start: w.date, end: this.addDays(w.end || w.date, 1),
      tone: w.kind === 'pm' || w.priority === 'pm' ? 'accent' : hot ? 'bad' : 'info', glyph: '🔧', label: w.title, title: w.title, dashed: !!w.due_only, movable: true };
  }
  private drawTimeline(): void {
    const el = document.getElementById('stc-timeline');
    if (!el) return;
    if (this.loading && !this.data) { el.innerHTML = `<div style="padding:40px; text-align:center;">${LoadingSpinner.render()}</div>`; return; }
    if (this.forbidden) { el.innerHTML = `<div style="padding:48px; text-align:center; color:var(--text-muted);">🔒 The calendar is limited to office roles.</div>`; return; }
    if (this.error && !this.data) {
      el.innerHTML = `<div style="padding:40px; text-align:center; color:var(--text-muted);">Couldn't load the calendar — ${this.esc(this.error)}. <button type="button" class="btn btn-secondary" data-stc-refresh-inline>Try again</button></div>`;
      el.querySelector('[data-stc-refresh-inline]')?.addEventListener('click', () => void this.load());
      return;
    }
    const d = this.data;
    if (!d) return;
    if (!d.properties.length) {
      el.innerHTML = d.q
        ? emptyState('No matches', `No property or guest matches "${this.esc(d.q)}" in this window.`, '🔍')
        : emptyState('No properties on the calendar', 'Active listings show here once the Hostaway sync has run (listings hidden from the dashboard are left out).', '📅');
      return;
    }
    const ctx = this.ctx;
    const v = this.activeView();
    const COL = this.colW, LANE = this.laneH, OPSH = this.opsH;
    const days = d.days;
    const dates: string[] = [];
    for (let i = 0; i < days; i++) dates.push(this.addDays(d.from, i));
    const W = days * COL;
    const x = (date: string) => (this.diffDays(d.from, date) + 0.5) * COL;
    const col = (date: string) => this.diffDays(d.from, date);
    const dayInsp = !!getInspector('day');
    const earlierOk = canEarlier({ from: this.from, days: this.days, extra: this.extra });

    // header: month labels + day cells (+ "‹ Earlier" at the left edge — B9's lazy past)
    const months: { key: string; label: string; start: number; n: number }[] = [];
    dates.forEach((dt, i) => {
      const key = dt.slice(0, 7);
      const last = months[months.length - 1];
      if (last && last.key === key) last.n++;
      else months.push({ key, label: this.fmt(dt, { month: 'long', year: 'numeric' }), start: i, n: 1 });
    });
    // R115: the month name sticks beside the label column while its month is on screen (a window can now start a month back)
    const monthsHtml = months.map(m => `<span class="stc-month" style="left:${m.start * COL}px; width:${m.n * COL}px;"><span class="stc-month-t">${this.esc(m.n * COL >= 110 ? m.label : m.label.slice(0, 3))}</span></span>`).join('');
    const earlierBtn = earlierOk ? `<button type="button" class="stc-earlier" data-stc-earlier title="Load the 30 days before ${this.esc(this.fmtMD(d.from))} (the window keeps its place)">‹ Earlier</button>` : '';
    const daysHtml = dates.map((dt, i) => {
      const wd = this.dow(dt);
      const cls = ['stc-day', (wd === 0 || wd === 6) ? 'stc-wk' : '', dt === d.as_of ? 'stc-today' : ''].filter(Boolean).join(' ');
      return `<div class="${cls}" style="left:${i * COL}px; width:${COL}px;"${dayInsp ? ` data-stc-date="${dt}" role="button" tabindex="0" title="Everything on ${this.esc(this.fmtWMD(dt))}"` : ''}><span class="stc-dow">${this.fmt(dt, { weekday: 'short' })}</span><span class="stc-dnum">${Number(dt.slice(8, 10))}</span></div>`;
    }).join('');
    const shadeHtml = dates.map((dt, i) => {
      const wd = this.dow(dt);
      if (dt === d.as_of) return `<div class="stc-shade stc-shade-today" style="left:${i * COL}px; width:${COL}px;"></div>`;
      if (wd === 0 || wd === 6) return `<div class="stc-shade stc-shade-wk" style="left:${i * COL}px; width:${COL}px;"></div>`;
      return '';
    }).join('');

    const staysBy = new Map<string, Stay[]>();
    for (const s of d.stays) { if (!staysBy.has(s.listing_id)) staysBy.set(s.listing_id, []); staysBy.get(s.listing_id)!.push(s); }
    const blocksBy = new Map<string, Block[]>();
    for (const b of d.blocks) { if (!blocksBy.has(b.listing_id)) blocksBy.set(b.listing_id, []); blocksBy.get(b.listing_id)!.push(b); }
    const turnsBy = new Map<string, TurnEv[]>();
    for (const t of d.events.turns) { if (!turnsBy.has(t.listing_id)) turnsBy.set(t.listing_id, []); turnsBy.get(t.listing_id)!.push(t); }
    const wosBy = new Map<string, WoEv[]>();
    for (const w of d.events.work_orders) { if (!wosBy.has(w.listing_id)) wosBy.set(w.listing_id, []); wosBy.get(w.listing_id)!.push(w); }

    const LS = (id: string) => this.layerState(id);
    const stSt = LS('stays'), inqSt = LS('inquiries'), blkSt = LS('blocks'), rateSt = LS('rates'), turnSt = LS('turns'), woSt = LS('wos');
    const modLayers = this.layersAllowed().filter(l => !CORE_LAYER_IDS.includes(l.id));
    const shownMod = modLayers.filter(l => LS(l.id) !== 'off');
    const stat = this.activeStat();
    // R115 review: a stat whose layer has no data yet (first fetch in flight) or failed can't decide rows — show every
    // row instead of a false "Nothing matches" (the card reads … / unavailable meanwhile).
    const statL = stat && stat.layer ? getLayer(stat.layer) : null;   // core layers (stays, turns…) come with the base payload
    const statBlind = !!(statL && (statL.server || typeof statL.load === 'function') && this.layerData(statL.id) == null);
    const m = stat && stat.match && !statBlind ? stat.match : null;
    const forced = (layerId: string) => !!(m && m.chips) && (stat!.layer ? stat!.layer === layerId : (layerId === 'turns' || layerId === 'wos'));
    this.chipIndex.clear();
    const chipMemo = new Map<string, OpsChip[]>();
    const chipsFor = (p: Property): OpsChip[] => {
      const memo = chipMemo.get(p.listing_id);
      if (memo) return memo;
      const out: OpsChip[] = [];
      const take = (spec: ChipSpec, core: OpsChip['core'], st: LayerState, layerId: string) => {
        const start = spec.start;
        const end = this.validDate(spec.end) && spec.end > start ? spec.end : this.addDays(start, 1);
        if (!(end > d.from && start <= d.to)) return;
        const hl = !!(m && m.chips) && safe(() => m.chips!(spec, ctx), false);
        if (st === 'off' && !(hl && forced(layerId))) return;
        out.push({ spec, core, dim: st === 'dim' && !hl, hl, start, end, lane: 0 });
      };
      if (turnSt !== 'off' || forced('turns')) for (const t of turnsBy.get(p.listing_id) || []) take(this.turnSpec(t), { kind: 'turn', turn: t }, turnSt, 'turns');
      if (woSt !== 'off' || forced('wos')) for (const w of wosBy.get(p.listing_id) || []) take(this.woSpec(w), { kind: 'wo', wo: w }, woSt, 'wos');
      for (const l of modLayers) {
        const st = LS(l.id);
        if ((st === 'off' && !forced(l.id)) || !l.chips) continue;
        for (const spec of safe<ChipSpec[]>(() => l.chips!(p, ctx), [])) {
          if (!spec || !spec.entity || typeof spec.entity.kind !== 'string' || typeof spec.entity.id !== 'string' || !this.validDate(spec.start)) continue;
          take({ ...spec, listing_id: p.listing_id }, null, st, l.id);
        }
      }
      chipMemo.set(p.listing_id, out);
      return out;
    };

    // rows: the view's row filter (unless "Show all"), the active stat, the view's sort
    let props = d.properties;
    const total = props.length;
    if (v.rowFilter && !this.showAllRows) props = props.filter(p => safe(() => v.rowFilter!(p, ctx), true));
    // R115 integ fix (D1): match.rows decides which homes SHOW; match.chips / match.stays only decide what is
    // HIGHLIGHTED inside them (ringed chips, dimmed other bars) and never hide a home match.rows kept — an
    // unscheduled work order or an owing tenant whose lease is a stay bar has no chip. With no match.rows a
    // home shows when it has a matching stay OR a matching chip.
    const rowStatOk = (p: Property) => {
      if (!m) return true;
      if (m.rows) return safe(() => m.rows!(p, ctx), false);
      if (!m.stays && !m.chips) return true;
      if (m.stays && (staysBy.get(p.listing_id) || []).some(s => safe(() => m.stays!(s, ctx), false))) return true;
      return !!m.chips && chipsFor(p).some(c => c.hl);
    };
    props = props.filter(rowStatOk);
    if (v.rowSort) props = [...props].sort((a, b) => safe(() => v.rowSort!(a, b, ctx), 0));
    if (!props.length) {
      const what = stat ? stat.label : v.label;
      el.innerHTML = `${emptyState(`Nothing matches "${this.esc(what)}"`, `No property in this window matches ${stat ? `the ${this.esc(stat.label)} filter` : `the ${this.esc(v.label)} view`}${d.q ? ` and "${this.esc(d.q)}"` : ''}.`, '🔍')}<div style="text-align:center; padding-bottom:24px;"><button type="button" class="btn btn-secondary stc-empty-act" data-stc-showall>Show all ${total} properties</button></div>`;
      return;
    }

    const rows = props.map(p => {
      // inquiries: their layer; the impossible / dismissed ones stay hidden unless "show hidden" is on
      const stays = (staysBy.get(p.listing_id) || []).filter(s => (s.kind === 'inquiry' ? inqSt !== 'off' && (this.showHiddenInq || !this.isHiddenInq(s)) : stSt !== 'off'));
      const blocks = blkSt === 'off' ? [] : (blocksBy.get(p.listing_id) || []);
      const items = assignLanes(stays, blocks);
      const lanes = Math.max(1, ...items.map(it => it.lane + 1));
      const baseH = Math.max(MIN_ROW_H, lanes * LANE + ROW_PAD * 2);
      const ops = chipsFor(p).slice().sort((a, b) => a.start.localeCompare(b.start) || a.end.localeCompare(b.end));
      const opsLanes = ops.length ? packLanes(ops) : 0;
      const rbadges = this.rowBadgesHtml(p, shownMod);
      const vsub = v.rowSub ? safe(() => v.rowSub!(p, ctx), '') : '';
      const hasSub = !!vsub || (!!stat && ((stat.key === 'open_weekend' && !!p.open_weekend) || (stat.key === 'vacant_tonight' && !!p.vacant_tonight)));
      const rowH = Math.max(baseH + (opsLanes ? opsLanes * OPSH + 4 : 0), MIN_ROW_H + (rbadges ? 24 : 0) + (hasSub ? 16 : 0));
      // nights the home is actually taken (real stays + blocks) → the rest show the nightly rate (R115: rates ≥ today only, from the server)
      const taken = new Set<string>();
      for (const it of items) {
        if (it.kind === 'stay' && it.stay.kind === 'inquiry') continue;
        for (let dt = it.start; dt < it.end; dt = this.addDays(dt, 1)) taken.add(dt);
      }
      const rates = rateSt === 'off' ? {} : (d.rates[p.listing_id] || {});
      // a NIGHT runs mid-day → mid-day, exactly like the bars, so the number
      // sits in the gap between a checkout and the next check-in
      const ratesHtml = dates.map((dt, i) => {
        if (taken.has(dt) || rates[dt] == null) return '';
        const last = i === days - 1;
        return `<span class="stc-rate${rateSt === 'dim' ? ' stc-rate-dim' : ''}" style="left:${(i + 0.5) * COL}px; width:${last ? COL / 2 : COL}px; top:${ROW_PAD}px; height:${LANE}px;" title="Nightly rate ${this.fmtMD(dt)}">${this.esc(Math.round(rates[dt]))}</span>`;
      }).join('');
      const openHtml = stat && stat.key === 'open_weekend' && p.open_weekend
        ? [p.open_weekend.fri, p.open_weekend.sat].filter(dt => this.validDate(dt) && dt >= d.from && dt <= d.to).map(dt => `<div class="stc-shade stc-shade-open" style="left:${col(dt) * COL}px; width:${COL}px;" title="Open ${this.esc(this.fmtWMD(dt))}"></div>`).join('')
        : '';
      const barsHtml = items.map(it => {
        const x0 = x(it.start), x1 = x(it.end);
        const left = Math.max(0, x0), right = Math.min(W, x1);
        if (right - left < 4) return '';
        const w = right - left;
        const top = ROW_PAD + it.lane * LANE + 4;
        const clip = `${x0 < 0 ? ' stc-clip-l' : ''}${x1 > W ? ' stc-clip-r' : ''}`;
        const geo = `left:${left}px; width:${w}px; top:${top}px; height:${LANE - 8}px;`;
        if (it.kind === 'block') {
          const b = it.block;
          const held = b.kind === 'held';
          const key = `${b.listing_id}|${b.start}|${b.end}`;
          const sel = this.selectedBlock && this.selectedBlock.listing_id === b.listing_id && this.selectedBlock.start === b.start && this.selectedBlock.end === b.end ? ' stc-bar-sel' : '';
          const n = this.diffDays(b.start, b.end);
          const title = `${held ? 'Held' : 'Blocked'} ${this.fmtMD(b.start)} – ${this.fmtMD(this.addDays(b.end, -1))} · ${n} night${n === 1 ? '' : 's'}${b.note ? ` · ${b.note}` : ' · no note'}`;
          return `<div class="stc-bar stc-bar-block${held ? ' stc-bar-held' : ''}${clip}${sel}${blkSt === 'dim' ? ' stc-ldim' : ''}" style="${geo}" data-stc-block="${this.esc(key)}" title="${this.esc(title)}" role="button" tabindex="0"><span class="stc-bar-in"><span class="stc-blk-ic">${held ? '◔' : '⊘'}</span>${w >= 90 ? `<span class="stc-bname">${held ? 'Held' : 'Blocked'}${b.note && w >= 150 ? ` · ${this.esc(b.note)}` : ''}</span>` : ''}</span></div>`;
        }
        const s = it.stay;
        const vs = this.vstate(s, d.as_of);
        const cls = s.kind === 'owner' ? 'stc-bar-owner' : s.kind === 'inquiry' ? 'stc-bar-inq' : `stc-bar-${vs}`;
        const sel = this.selectedId === s.id ? ' stc-bar-sel' : '';
        const neg = s.negative ? ' stc-bar-neg' : '';
        const dim = m && m.stays && !safe(() => m.stays!(s, ctx), false) ? ' stc-bar-dim' : '';
        const ldim = (s.kind === 'inquiry' ? inqSt : stSt) === 'dim' ? ' stc-ldim' : '';
        const deco = this.decoFor(s, shownMod);
        const hasOpp = s.opportunities.some(o => !o.applied && !o.declined);
        const msg = !!(s.flagged && s.flagged.open);
        const av = s.kind === 'owner' ? '🏠' : this.esc(this.initials(s.guest_name));
        const name = w >= 96 ? `<span class="stc-bname">${this.esc(s.guest_name)}</span>` : '';
        const nights = w >= 170 ? `<span class="stc-bnights">${s.nights} night${s.nights === 1 ? '' : 's'}</span>` : '';
        const rv = s.review && s.review.status === 'received' && s.kind === 'stay' && (s.state === 'checked_out' || s.state === 'in_house');
        const rvBadge = rv ? `<span class="stc-badge stc-badge-rv" title="Review received${s.review!.rating != null ? ` · ${this.esc(s.review!.rating)} ★` : ''}">★${s.review!.rating != null && w >= 120 ? ` ${this.esc(s.review!.rating)}` : ''}</span>` : '';
        const badges = `${hasOpp ? '<span class="stc-badge stc-badge-opp" title="Opportunity">$</span>' : ''}${msg ? `<span class="stc-badge stc-badge-msg ${s.flagged!.sentiment === 'negative' ? 'stc-badge-neg' : ''}" style="${hasOpp ? 'right:12px;' : ''}" title="Flagged messages (${this.esc(s.flagged!.sentiment)})">${s.flagged!.sentiment === 'negative' ? '😟' : '💬'}</span>` : ''}${rvBadge}`;
        const turn = s.turnover ? `<span class="stc-turn stc-turn-${this.esc(s.turnover.status)}" title="Turnover before arrival: ${this.esc(s.turnover.status.replace('_', ' '))}${s.turnover.same_day ? ' (same day)' : ''}"></span>` : '';
        const vdot = s.kind === 'stay' && s.check_in === d.as_of && s.checks ? `<span class="stc-vdot stc-vdot-${this.esc(s.checks.rollup)}" title="Arrival checks: ${this.esc((VER_PILL[s.checks.rollup] || VER_PILL.pending).label)}"></span>` : '';
        const title = `${s.guest_name} · ${this.fmtMD(s.check_in)} → ${this.fmtMD(s.check_out)} · ${s.nights} night${s.nights === 1 ? '' : 's'} · ${this.stateLabel(s, d.as_of)}${s.negative ? ' · negative experience' : ''}${rv ? ` · review ${s.review!.rating != null ? s.review!.rating + '★' : 'received'}` : ''}${deco.titleSuffix}`;
        return `<div class="stc-bar ${cls}${clip}${sel}${neg}${dim}${ldim}${deco.cls}" style="${geo}" data-stc-stay="${this.esc(s.id)}" title="${this.esc(title)}" role="button" tabindex="0">
          <span class="stc-bar-in"><span class="stc-av">${av}</span>${name}${nights}${w >= 60 ? deco.html : ''}</span>${badges}${turn}${vdot}</div>`;
      }).join('');
      const opsHtml = ops.map(it => {
        const c0 = Math.max(0, col(it.start)), c1 = Math.min(days, col(it.end));
        if (c1 <= c0) return '';
        const left = c0 * COL + 3, w = (c1 - c0) * COL - 6;
        const top = baseH + 2 + it.lane * OPSH;
        const geo = `left:${left}px; width:${w}px; top:${top}px; height:${OPSH - 4}px;`;
        const hl = it.hl ? ' stc-ops-hl' : '';
        const ldim = it.dim ? ' stc-ldim' : '';
        if (it.core && it.core.kind === 'turn') {
          const t = it.core.turn;
          const st = t.status.toLowerCase();
          const tone = t.unassigned ? 'unassigned' : t.done ? 'done' : 'busy';
          const glyphT = tone === 'done' ? '✓' : tone === 'unassigned' ? '⚠' : '◐';
          const who = t.assignees.length ? t.assignees.join(', ') : 'unassigned';
          // R112: up to two initials discs (one when the chip is narrow), a danger ring when at risk, corner badges for photos / a moved clean
          const ini = t.initials.slice(0, w >= 44 ? 2 : 1);
          const discs = ini.length ? `<span class="stc-ops-avs">${ini.map(i => `<span class="stc-ops-av" title="${this.esc(who)}">${this.esc(i.slice(0, 2))}</span>`).join('')}</span>` : (w >= 44 ? '<span class="stc-ops-t">Turn</span>' : '');
          const badges = `${t.moved_from ? `<span class="stc-ops-cb stc-ops-cb-mv" title="Moved from ${this.esc(this.fmtMD(t.moved_from))}">↪</span>` : ''}${t.photos > 0 ? `<span class="stc-ops-cb stc-ops-cb-ph" title="${t.photos} photo${t.photos === 1 ? '' : 's'}">📷${t.photos > 1 ? t.photos : ''}</span>` : ''}`;
          const extra = `${t.at_risk ? ' · AT RISK: same-day and not done' : ''}${t.photos > 0 ? ` · ${t.photos} photo${t.photos === 1 ? '' : 's'}` : ''}${t.moved_from ? ` · moved from ${this.fmtMD(t.moved_from)}` : ''}`;
          const title = `Turnover ${this.fmtMD(t.date)} · ${st.replace('_', ' ') || 'unstarted'} · ${who}${t.same_day ? ' · same-day' : ''}${t.next_check_in ? ` · next arrival ${this.fmtMD(t.next_check_in)}` : ''}${extra} · click for the turn${t.done ? '' : ', drag to another day'}`;
          const cls = `stc-ops-chip stc-ops-turn stc-ops-${tone}${hl}${ldim}${t.at_risk ? ' stc-ops-risk' : ''}${t.done ? '' : ' stc-ops-drag'}${this.popTurn === t.id ? ' stc-ops-open' : ''}`;
          return `<div class="${cls}" style="${geo}" data-stc-turn="${this.esc(t.id)}"${t.done ? '' : ' draggable="true"'} title="${this.esc(title)}" role="button" tabindex="0"><span class="stc-ops-g">${glyphT}</span>${discs}${badges}</div>`;
        }
        if (it.core && it.core.kind === 'wo') {
          const wo = it.core.wo;
          const pr = wo.kind === 'pm' || wo.priority === 'pm' ? 'pm' : (wo.priority === 'emergency' || wo.priority === 'urgent') ? 'hot' : 'routine';
          const title = wo.due_only
            ? `${wo.title} · ${wo.status.replace('_', ' ') || 'open'} · ${wo.who || 'nobody assigned'} · due ${this.fmtMD(wo.date)}, not booked yet · click or drag to book a day`
            : `${wo.title} · ${wo.status.replace('_', ' ') || 'open'} · ${wo.who || 'nobody assigned'} · booked ${this.fmtMD(wo.date)}${wo.end ? ` → ${this.fmtMD(wo.end)}` : ''}${wo.due_date ? ` · due ${this.fmtMD(wo.due_date)}` : ''} · click to reschedule, drag to another day`;
          return `<div class="stc-ops-chip stc-ops-wo stc-ops-p-${pr}${wo.due_only ? ' stc-ops-due' : ''}${hl}${ldim}${this.popWo === wo.id ? ' stc-ops-open' : ''}" style="${geo}" data-stc-wo="${this.esc(wo.id)}" draggable="true" title="${this.esc(title)}" role="button" tabindex="0"><span class="stc-ops-g">🔧</span>${w >= 50 ? `<span class="stc-ops-t">${this.esc(wo.title)}</span>` : ''}</div>`;
        }
        // a module chip, drawn from its ChipSpec
        const sp = it.spec;
        const key = `${sp.entity.kind}|${sp.entity.id}|${sp.key}`;
        this.chipIndex.set(key, sp);
        const kind = getChipKind(sp.entity.kind);
        const movable = !!(sp.movable && kind && kind.move);
        const ini = (sp.initials || []).slice(0, w >= 44 ? 2 : 1);
        const discs = ini.length && w >= 36 ? `<span class="stc-ops-avs">${ini.map(i => `<span class="stc-ops-av">${this.esc(String(i).slice(0, 2))}</span>`).join('')}</span>` : '';
        const cls = `stc-ops-chip stc-mchip stc-mchip-${toneOf(sp.tone)}${sp.ring ? ' stc-ops-risk' : ''}${sp.dashed ? ' stc-ops-due' : ''}${sp.strike ? ' stc-mchip-strike' : ''}${hl}${ldim}${movable ? ' stc-ops-drag' : ''}`;
        return `<div class="${cls}" style="${geo}" data-stc-chip="${this.esc(sp.entity.kind)}|${this.esc(sp.entity.id)}" data-stc-chip-key="${this.esc(key)}"${movable ? ' draggable="true"' : ''} title="${this.esc(sp.title)}" role="button" tabindex="0">${sp.glyph ? `<span class="stc-ops-g">${this.esc(sp.glyph)}</span>` : ''}${discs}${sp.label && w >= 50 ? `<span class="stc-ops-t">${this.esc(sp.label)}</span>` : ''}${sp.badge ? `<span class="stc-ops-cb">${this.esc(sp.badge)}</span>` : ''}</div>`;
      }).join('');
      const pav = this.avatarHtml(p.thumbnail_url, p.name, true);
      const pct = Math.max(0, Math.min(100, Number(p.occupancy_pct) || 0));
      const tags = `${p.is_mtr ? `<span class="stc-tag" title="Mid-term rental home${p.mtr_source ? ` (${this.esc(p.mtr_source === 'min_nights' ? 'min nights' : p.mtr_source)})` : ''}">MTR</span>` : ''}`;
      const sub = vsub ? `<div class="stc-lbl-sub stc-lbl-sub-v">${this.esc(vsub)}</div>`
        : stat && stat.key === 'open_weekend' && p.open_weekend ? `<div class="stc-lbl-sub">Open ${this.esc(this.fmtWMD(p.open_weekend.fri))} · ${this.esc(this.fmtMD(p.open_weekend.sat))}</div>`
        : stat && stat.key === 'vacant_tonight' && p.vacant_tonight ? `<div class="stc-lbl-sub">Vacant tonight</div>` : '';
      return `
        <div class="stc-row" style="height:${rowH}px;" data-stc-row="${this.esc(p.listing_id)}">
          <div class="stc-lbl" title="${this.esc(p.full_name || p.name)} — open this home" data-stc-prop="${this.esc(p.listing_id)}" role="button" tabindex="0">
            ${pav}
            <div class="stc-lbl-txt">
              <div class="stc-pname">${this.esc(p.name)}${tags}</div>
              <div class="stc-meter"><span class="stc-meter-bar"><span class="stc-meter-fill" style="width:${pct}%;"></span></span><span class="stc-meter-txt">${pct}% booked</span></div>
              ${rbadges}${sub}
            </div>
          </div>
          <div class="stc-lanes" style="width:${W}px;" data-stc-listing="${this.esc(p.listing_id)}">${openHtml}${ratesHtml}${barsHtml}${opsLanes ? `<div class="stc-ops-sep" style="top:${baseH}px;"></div>${opsHtml}` : ''}</div>
        </div>`;
    }).join('');

    // same window → keep where the user scrolled (↻, search, Apply and the
    // 409 redraw all land here); a NEW window lands on pendingScroll (B9:
    // "‹ Earlier" / paging keep the visual position) or, anchored, on the
    // anchor column (yesterday)
    const prevSc = el.querySelector<HTMLElement>('.stc-scroller');
    const key = `${d.from}|${d.days}`;
    const sameKey = !!prevSc && this.drawKey === key;
    const keep = sameKey ? { left: prevSc!.scrollLeft, top: prevSc!.scrollTop } : null;
    const top = prevSc ? prevSc.scrollTop : 0;
    const newLeft = this.pendingScroll ? this.pendingScroll.left : (!this.from ? anchorColumn(this.loadedWin()) * COL : 0);
    this.drawKey = key;
    const shown = props.length;
    const corner = shown !== total
      ? `<span>Showing ${shown} of ${total}</span> · <button type="button" class="stc-link" data-stc-showall>Show all</button>`
      : `${total} propert${total === 1 ? 'y' : 'ies'}`;
    el.innerHTML = `
      <div class="stc-scroller">
        <div class="stc-grid">
          <div class="stc-shadecols" style="width:${W}px;">${shadeHtml}</div>
          <div class="stc-hdr">
            <div class="stc-corner">${earlierBtn}<span class="stc-corner-n">${corner}</span></div>
            <div class="stc-daysrow" style="width:${W}px;">
              <div class="stc-months">${monthsHtml}</div>
              <div class="stc-daycells">${daysHtml}</div>
            </div>
          </div>
          ${rows}
        </div>
      </div>`;
    const sc = el.querySelector<HTMLElement>('.stc-scroller');
    if (sc) {
      if (keep) { sc.scrollLeft = keep.left; sc.scrollTop = keep.top; }
      else { sc.scrollLeft = newLeft; sc.scrollTop = top; }
    }
  }
  /** Module layers' decorateStay for one bar: extra classes, inline glyph badges, a title suffix. */
  private decoFor(s: Stay, layers: Layer[]): { cls: string; html: string; titleSuffix: string } {
    let cls = '', html = '', titleSuffix = '';
    for (const l of layers) {
      if (!l.decorateStay) continue;
      const dd = safe<StayDecoration | null>(() => l.decorateStay!(s, this.ctx), null);
      if (!dd) continue;
      if (dd.cls) cls += ' ' + clsSafe(dd.cls);
      for (const b of (dd.badges || []).slice(0, 3)) html += `<span class="stc-bdeco tone-${toneOf(b.tone)}" title="${this.esc(b.title)}">${this.esc(b.glyph)}${b.text ? ` ${this.esc(b.text)}` : ''}</span>`;
      if (dd.titleSuffix) titleSuffix += ` · ${String(dd.titleSuffix).slice(0, 200)}`;
    }
    return { cls, html, titleSuffix };
  }
  /** Module layers' row badges (property state: ★ score, ⚑ open issues, 📦 supply requests …) under the name; one with an entity opens it. */
  private rowBadgesHtml(p: Property, layers: Layer[]): string {
    const out: string[] = [];
    for (const l of layers) {
      if (!l.badges) continue;
      for (const b of safe<BadgeSpec[]>(() => l.badges!(p, this.ctx), []).slice(0, 4)) {
        if (!b || !b.glyph) continue;
        const inner = `${this.esc(b.glyph)}${b.text ? ` ${this.esc(b.text)}` : ''}`;
        out.push(b.entity && b.entity.kind && b.entity.id
          ? `<button type="button" class="stc-rb tone-${toneOf(b.tone)}" title="${this.esc(b.title)}" data-stc-rb="${this.esc(b.entity.kind)}|${this.esc(b.entity.id)}|${this.esc(b.entity.listing_id || p.listing_id)}">${inner}</button>`
          : `<span class="stc-rb tone-${toneOf(b.tone)}" title="${this.esc(b.title)}">${inner}</span>`);
      }
    }
    return out.length ? `<div class="stc-rbadges">${out.slice(0, 6).join('')}</div>` : '';
  }

  // ── cells: an empty-cell tap → the chooser (Reservations: a free future night → one-tap Hold or reserve…) ──
  private nightTaken(listingId: string, date: string): boolean {
    const d = this.data;
    if (!d) return true;
    return d.stays.some(s => s.listing_id === listingId && s.kind !== 'inquiry' && s.check_in <= date && date < s.check_out)
      || d.blocks.some(b => b.listing_id === listingId && b.start <= date && date < b.end);
  }
  private cellOf(listingId: string, date: string): DateCell {
    const d = this.data;
    const asOf = d ? d.as_of : this.bizToday();
    const holder = listingId && d ? d.stays.find(s => s.listing_id === listingId && s.kind !== 'inquiry' && s.check_in <= date && date < s.check_out) : null;
    return { listing_id: listingId, date, past: date < asOf, taken: !!listingId && this.nightTaken(listingId, date), stay_id: holder ? holder.id : null };
  }
  private dateRef(listingId: string, date: string): EntityRef { return { kind: 'date', id: `${listingId}|${date}`, listing_id: listingId, date }; }
  /** The chooser's choices for a cell: date actions the role may use whose when() holds, ordered by the view's createOrder. */
  private dateActions(cell: DateCell): Action[] {
    const ref = this.dateRef(cell.listing_id, cell.date);
    const order = this.activeView().createOrder || [];
    const rank = (a: Action) => { const i = order.indexOf(a.key); return i < 0 ? 1000 + a.order : i; };
    return actionsFor('date', this.role()).filter(a => safe(() => a.when(ref, cell, this.ctx), false)).sort((a, b) => rank(a) - rank(b));
  }
  private vacantClick(lanes: HTMLElement, e: MouseEvent): void {
    const d = this.data;
    const lid = lanes.dataset.stcListing || '';
    if (!d || !lid) return;
    const rect = lanes.getBoundingClientRect();
    const c = Math.floor((e.clientX - rect.left) / this.colW);
    if (c < 0 || c >= d.days) return;
    const date = this.addDays(d.from, c);
    const cell = this.cellOf(lid, date);
    // Reservations keeps today's one tap: a free future night opens "Hold or reserve…" (the Add modal) directly
    if (this.view === 'stays' && !cell.past && !cell.taken && this.dateActions(cell).some(a => a.key === 'hold')) { this.openAdd({ listing_id: lid, check_in: date }); return; }
    this.openRoot(this.dateRef(lid, date));
  }

  // ── the inspector host: ONE panel for every entity, with a back stack ──
  private top(): InspEntry | null { return this.stack.length ? this.stack[this.stack.length - 1] : null; }
  private stackClear(): void { for (const e of this.stack) { try { e.ctrl && e.ctrl.abort(); } catch { /* gone */ } } this.stack = []; }
  // R115 integ: an inspector whose busy() is true (unsaved WO edits, a half-typed MTR form, a write in
  // flight) ASKS before it is closed, backed out of or replaced by a timeline click — and every kind
  // that leaves the stack gets its reset(), so a closed editor never parks the auto-refresh veto.
  private kindBusy(kind: string): boolean {
    const ins = getInspector(kind);
    if (ins && ins.busy && safe(() => ins.busy!(), false)) return true;
    return sectionsFor(kind).some(x => x.busy && safe(() => x.busy!(), false));
  }
  /** `leaving` are about to come off the stack (`staying` stay on it). False = the user kept the unsaved work. force skips the ask (the entity is gone). */
  private leaveOk(leaving: InspEntry[], staying: InspEntry[], force = false): boolean {
    const kinds = [...new Set(leaving.map(e => e.ref.kind))].filter(k => !staying.some(e => e.ref.kind === k));
    if (!force && kinds.some(k => this.kindBusy(k)) && !window.confirm('Discard the unsaved changes in this panel?')) return false;
    for (const k of kinds) { const ins = getInspector(k); if (ins && ins.reset) safe(() => { ins.reset!(); return true; }, true); }
    return true;
  }
  /** Clear the stack for a new root (asks first when something unsaved would be lost). */
  private replaceStack(): boolean {
    if (!this.stack.length) return true;
    if (!this.leaveOk(this.stack, [])) return false;
    this.stackClear();
    return true;
  }
  /** A timeline click: a fresh stack with this entity at its root. */
  private openRoot(ref: EntityRef): void { if (!this.replaceStack()) return; this.pushEntry(ref); if (!this.stack.length) this.panelClosed(); }
  /** ctx.openInspector: push (the same entity on top just refreshes). */
  private pushEntry(ref: EntityRef): void {
    if (!ref || typeof ref.kind !== 'string' || typeof ref.id !== 'string' || !ref.kind) return;
    const top = this.top();
    if (top && sameRef(top.ref, ref)) { this.loadTop(true); this.drawPanel(); return; }
    if (this.stack.length >= 12) { const old = this.stack.shift(); try { old && old.ctrl && old.ctrl.abort(); } catch { /* gone */ } }
    this.stack.push({ ref: { ...ref }, data: null, loading: false, err: null, ctrl: null, seq: 0 });
    this.enterTop(true);
  }
  private backEntry(force = false): void {
    if (this.stack.length <= 1) { this.closeInspectorAll(force); return; }
    if (!this.leaveOk(this.stack.slice(-1), this.stack.slice(0, -1), force)) return;
    const e = this.stack.pop();
    try { e && e.ctrl && e.ctrl.abort(); } catch { /* gone */ }
    this.enterTop(false);
  }
  /** Drop the top entity (it left the window / was dismissed). */
  private closeTop(): void { if (this.stack.length <= 1) this.closeInspectorAll(true); else this.backEntry(true); }
  private closeInspectorAll(force = false): void {
    if (this.stack.length && !this.leaveOk(this.stack, [], force)) return;
    this.stackClear();
    this.panelClosed();
  }
  /** The panel's empty state (the stack is already empty). */
  private panelClosed(): void {
    this.syncLegacy();
    document.body.classList.remove('stc-panel-open');
    closeKebab();
    this.markSelection();
    this.drawPanel();
    this.drawRail();
  }
  private enterTop(fresh: boolean): void {
    closeKebab();
    this.syncLegacy();
    document.body.classList.toggle('stc-panel-open', this.stack.length > 0);
    this.markSelection();
    this.drawPanel();
    this.loadTop(fresh);
  }
  /** The stay / block / turn / WO inspectors keep their R110–R114 state on the page — sync it from the top of the stack. */
  private syncLegacy(): void {
    const top = this.top();
    const k = top ? top.ref.kind : '';
    if (k === 'stay') {
      const id = top!.ref.id;
      if (this.selectedId !== id) this.applyNote = '';
      this.selectedId = id;
      const cached = top!.data as Detail | null;
      if (cached && cached.stay && cached.stay.id === id) { this.detail = cached; this.detailFor = id; this.detailLoading = false; }
      else if (this.detailFor !== id) { this.detail = null; this.detailFor = id; this.detailLoading = true; }
    } else this.selectedId = null;
    if (k === 'block') {
      const [lid, st, en] = top!.ref.id.split('|');
      const same = this.selectedBlock && this.selectedBlock.listing_id === lid && this.selectedBlock.start === st && this.selectedBlock.end === en;
      this.selectedBlock = { listing_id: lid, start: st, end: en };
      const info = top!.data as BlockInfo | null;
      if (info) { this.blockInfo = info; this.blockLoading = false; } else if (!same) { this.blockInfo = null; this.blockLoading = true; }
    } else { this.selectedBlock = null; this.blockInfo = null; this.blockLoading = false; }
    if (k === 'turn') {
      const id = top!.ref.id;
      if (this.popTurn !== id) { this.popConfirm = null; this.popExisting = null; this.popErr = ''; this.popBusy = false; this.answerTurnAsk(false); }
      this.popTurn = id;
      const td = top!.data as TurnPop | null;
      if (td && td.turn && td.turn.id === id) { this.popTurnData = td; this.popTurnLoading = false; } else if (!this.popTurnData || this.popTurnData.turn.id !== id) { this.popTurnData = null; this.popTurnLoading = true; }
    } else {
      if (this.popTurn) this.answerTurnAsk(false);
      this.popTurn = null; this.popTurnData = null; this.popTurnLoading = false; this.popConfirm = null; this.popExisting = null;
    }
    if (k === 'wo') { if (this.popWo !== top!.ref.id) { this.popErr = ''; this.popBusy = false; } this.popWo = top!.ref.id; }
    else this.popWo = null;
    if (k !== 'turn' && k !== 'wo') { this.popErr = ''; this.popBusy = false; }
  }
  /** Fetch what the top entity needs (core kinds: their own routes; module kinds: Inspector.load). */
  private loadTop(fresh: boolean): void {
    const top = this.top();
    if (!top) return;
    const k = top.ref.kind;
    if (this.isCoreKind(k)) {
      if (k === 'stay') { if (fresh || !this.detail) { this.detailLoading = true; void this.fetchDetail(top.ref.id); } return; }
      if (k === 'turn') { if (fresh || !this.popTurnData) { this.popTurnLoading = true; void this.fetchTurn(top.ref.id); } return; }
      if (k === 'block') { if (fresh || !this.blockInfo) void this.fetchBlock(); return; }
      return;   // wo (lite) / listing / date / rail draw from the base payload
    }
    const ins = getInspector(k);
    if (!ins || typeof ins.load !== 'function' || (!fresh && top.data != null)) return;
    try { top.ctrl && top.ctrl.abort(); } catch { /* gone */ }
    const ctrl = new AbortController();
    const seq = ++this.inspSeq;
    top.ctrl = ctrl; top.seq = seq; top.loading = true; top.err = null;
    this.drawPanel();
    Promise.resolve().then(() => ins.load!(top.ref, ctrl.signal, this.ctx))
      .then(data => { if (top.seq !== seq || ctrl.signal.aborted) return; top.data = data === undefined ? null : data; top.loading = false; if (this.top() === top) this.drawPanel(); })
      .catch(e => { if (top.seq !== seq || ctrl.signal.aborted) return; top.loading = false; top.err = (e && e.message) || 'Could not load it'; if (this.top() === top) this.drawPanel(); });
  }
  /** What sections / actions receive as `data` (shapes: calendar/contract-ext.ts). */
  private dataOf(e: InspEntry): unknown {
    const k = e.ref.kind;
    if (!this.isCoreKind(k)) return e.data;
    if (k === 'stay') { const s = this.findStay(e.ref.id); return s ? this.stayData(s) : null; }
    if (k === 'block') { const b = this.selectedBlock; return b ? ({ ...b, info: this.blockInfo, loading: this.blockLoading } as BlockInspectorData) : null; }
    if (k === 'listing') return this.propOf(e.ref.listing_id || e.ref.id);
    if (k === 'turn') return this.popTurnData;
    if (k === 'wo') return this.woOf(e.ref.id);
    if (k === 'date') return this.cellOf(e.ref.listing_id || '', e.ref.date || (this.data ? this.data.as_of : this.bizToday()));
    return null;
  }
  private stayData(s: Stay): StayInspectorData {
    const det = this.detail && this.detail.stay.id === s.id ? this.detail : null;
    const base: Detail = det || { stay: s, guest: { name: s.guest_name, phone: null, email: null, guests: s.guests, confirmation_code: null }, flagged: s.flagged, checks: null, details: null, review: s.review || null, events: null, arrival_check: null, eci_lco: null };
    return { ...base, loading: !det && this.detailLoading, listStay: s };
  }
  /** Outline what the inspector shows on the timeline. */
  private markSelection(): void {
    document.querySelectorAll<HTMLElement>('#stc-timeline .stc-bar-sel').forEach(b => b.classList.remove('stc-bar-sel'));
    document.querySelectorAll<HTMLElement>('#stc-timeline .stc-ops-open').forEach(c => c.classList.remove('stc-ops-open'));
    const top = this.top();
    if (!top) return;
    const id = top.ref.id;
    const sel = top.ref.kind === 'stay' ? `[data-stc-stay="${CSS.escape(id)}"]` : top.ref.kind === 'block' ? `[data-stc-block="${CSS.escape(id)}"]` : '';
    if (sel) document.querySelector<HTMLElement>(`#stc-timeline ${sel}`)?.classList.add('stc-bar-sel');
    const chipSel = top.ref.kind === 'turn' ? `[data-stc-turn="${CSS.escape(id)}"]` : top.ref.kind === 'wo' ? `[data-stc-wo="${CSS.escape(id)}"]` : `[data-stc-chip="${CSS.escape(top.ref.kind)}|${CSS.escape(id)}"]`;
    document.querySelector<HTMLElement>(`#stc-timeline ${chipSel}`)?.classList.add('stc-ops-open');
  }
  private secIsOpen(kind: string, s: Section): boolean {
    if (!s.title) return true;
    const k = `${kind}|${s.key}`;
    if (this.secOpen.has(k)) return !!this.secOpen.get(k);
    if (kind === 'stay' && s.key === 'details') return this.detailsOpen;
    const v = this.activeView();
    const ids = [String(v.id), v.parent ? String(v.parent) : ''].filter(Boolean);
    if (s.openIn && (s.openIn as string[]).some(x => ids.includes(x))) return true;
    if (v.openSections && v.openSections.includes(s.key)) return true;
    return false;
  }
  /** Core sections' live header extras (a count pill). */
  private secTitleExtra(kind: string, key: string): string {
    if (kind !== 'stay') return '';
    const s = this.findStay(this.selectedId);
    if (!s) return '';
    if (key === 'opportunities') { const det = this.detailForSelected(); const n = ((det ? det.stay.opportunities : s.opportunities) || []).length; return `<span class="stc-count">${n}</span>`; }
    if (key === 'arrival_checklist') { const det = this.detailForSelected(); const ac = det && det.arrival_check; return ac && ac.generated ? `<span class="stc-count">${ac.required_done}/${ac.required_total}</span>` : ''; }
    return '';
  }
  /** The panel: head (Back · title / sub · ✕), the action row (one blue primary, ≤2 secondary, the rest behind ⋯), the sections. */
  private drawPanel(): void {
    const el = document.getElementById('stc-panel');
    if (!el) return;
    const top = this.top();
    if (!top) { el.classList.remove('open'); el.innerHTML = ''; document.body.classList.remove('stc-panel-open'); return; }
    const kind = top.ref.kind;
    const core = this.isCoreKind(kind);
    // a core entity that left the window closes its view (the stay was cancelled / the 409 redraw) so auto-refresh resumes
    if (core && kind === 'stay' && !this.findStay(top.ref.id) && !this.detailLoading) { this.closeTop(); return; }
    if (core && kind === 'block' && !this.findBlock(this.selectedBlock) && !this.blockInfo && !this.blockLoading) { this.closeTop(); return; }
    const ins = getInspector(kind);
    const ctx = this.ctx;
    const data = this.dataOf(top);
    const back = this.stack.length > 1 ? `<button type="button" class="stc-back" data-stc-back title="Back to ${this.esc(this.entryTitle(this.stack[this.stack.length - 2]))}">‹ Back</button>` : '';
    const title = ins ? safe(() => ins.title(top.ref, data, ctx), '') : '';
    const sub = ins && ins.sub ? safe(() => ins.sub!(top.ref, data, ctx), '') : '';
    const avatar = this.headIcon(top);
    el.classList.add('open');
    if (!ins) {
      el.innerHTML = `<div class="stc-panel-head">${back}<div class="stc-ih-txt"><h2 class="stc-gname">Not available</h2></div><button type="button" class="stc-close" data-stc-close aria-label="Close">✕</button></div><div class="stc-panel-body"><div class="stc-none">This calendar can't show a "${this.esc(kind)}" yet — the feature that opens it is not on this build.</div></div>`;
      return;
    }
    const actions = kind === 'date' || kind === 'rail' ? '' : this.actionRowHtml(top, data);
    const secs = sectionsFor(kind).filter(s => safe(() => s.when(top.ref, data, ctx), false));
    const coreLoading = core && kind === 'stay' && !this.findStay(top.ref.id) && this.detailLoading;
    const status = top.loading || coreLoading ? `<div class="stc-iload">Loading…</div>` : top.err ? `<div class="stc-isec-err">Couldn't load it — ${this.esc(top.err)}. <button type="button" class="stc-link" data-stc-insp-retry>Try again</button></div>` : '';
    const body = secs.map(s => {
      const open = this.secIsOpen(kind, s);
      const html = open ? safe(() => s.render(top.ref, data, ctx), '') : '';
      const head = s.title ? `<button type="button" class="stc-sec stc-sec-btn" data-stc-sec-toggle="${this.esc(s.key)}" aria-expanded="${open ? 'true' : 'false'}">${this.esc(s.title)} ${core ? this.secTitleExtra(kind, s.key) : ''}<span class="stc-chev">${open ? '▾' : '▸'}</span></button>` : '';
      return `<section class="stc-isec" data-stc-sec="${this.esc(s.key)}">${head}${open ? `<div class="stc-isec-body">${html}</div>` : ''}</section>`;
    }).join('');
    el.innerHTML = `
      <div class="stc-panel-head">${back}${avatar}<div class="stc-ih-txt"><h2 class="stc-gname">${this.esc(title)}</h2>${sub ? `<div class="stc-ih-sub">${this.esc(sub)}</div>` : ''}</div><button type="button" class="stc-close" data-stc-close aria-label="Close">✕</button></div>
      <div class="stc-panel-body">${actions}${status}${body || (status ? '' : '<div class="stc-none">Nothing to show here.</div>')}</div>`;
    // R115 integ fix (D3): measure the action row BEFORE binding (it may be rebuilt with fewer secondaries)
    this.fitActions(el, top, data);
    // kebab items move to <body> when opened — bind them (and the buttons) per element after every draw
    el.querySelectorAll<HTMLElement>('[data-stc-act]').forEach(b => b.addEventListener('click', (ev) => { ev.preventDefault(); this.runAction(kind, String(b.dataset.stcAct || '')); }));
    for (const s of secs) {
      if (!s.bind || !this.secIsOpen(kind, s)) continue;
      const secEl = el.querySelector<HTMLElement>(`[data-stc-sec="${CSS.escape(s.key)}"] .stc-isec-body`);
      if (secEl) safe(() => { s.bind!(secEl, top.ref, data, ctx); return true; }, true);
    }
  }
  private headIcon(e: InspEntry): string {
    const k = e.ref.kind;
    if (k === 'stay') { const s = this.findStay(e.ref.id); return s ? `<span class="stc-gav${s.negative ? ' stc-gav-neg' : ''}">${s.kind === 'owner' ? '🏠' : this.esc(this.initials(s.guest_name))}</span>` : ''; }
    if (k === 'block') return `<span class="stc-gav stc-gav-blk">${this.blockKind() === 'held' ? '◔' : '⊘'}</span>`;
    if (k === 'listing') { const p = this.propOf(e.ref.listing_id || e.ref.id); return p ? this.avatarHtml(p.thumbnail_url, p.name, false).replace(/class="stc-pav"/g, 'class="stc-pav stc-ihpav"') : ''; }
    if (k === 'turn') return '<span class="stc-ihic">🧹</span>';
    if (k === 'wo') return '<span class="stc-ihic">🔧</span>';
    if (k === 'date') return '<span class="stc-ihic">＋</span>';
    if (k === 'rail') return '<span class="stc-ihic">📥</span>';
    return '';
  }
  private entryTitle(e: InspEntry): string { const ins = getInspector(e.ref.kind); return ins ? safe(() => ins.title(e.ref, this.dataOf(e), this.ctx), e.ref.kind) : e.ref.kind; }
  /** secMax: how many secondaries may be buttons (≤2 on desktop, 0 on phones); fitActions lowers it until the row fits. */
  private actionRowHtml(top: InspEntry, data: unknown, secMax = 2): string {
    const list = actionsFor(top.ref.kind, this.role()).filter(a => safe(() => a.when(top.ref, data, this.ctx), false));
    if (!list.length) return '';
    const phone = window.innerWidth < 640;
    const max = phone ? 0 : Math.max(0, Math.min(2, secMax));
    const v = this.activeView();
    let split = splitActions(list, String(v.id), max);
    if (v.parent && !(split.primary && split.primary.primaryIn && (split.primary.primaryIn as string[]).includes(String(v.id)))) {
      const viaParent = splitActions(list, String(v.parent), max);
      if (viaParent.primary && viaParent.primary.primaryIn && (viaParent.primary.primaryIn as string[]).includes(String(v.parent))) split = viaParent;
    }
    const btn = (a: Action, cls: string) => `<button type="button" class="btn ${cls}" data-stc-act="${this.esc(a.key)}" title="${this.esc(a.label)}">${glyph(a.icon, 14)} <span class="stc-act-lbl">${this.esc(a.label)}</span></button>`;
    const items: KebabItem[] = split.more.map(a => ({ label: a.label, icon: a.icon, attrs: { 'data-stc-act': a.key } }));
    const disabled = top.ref.kind === 'stay' && this.detailLoading && !this.detail ? ' data-loading="1"' : '';
    return `<div class="stc-actions"${disabled}>${split.primary ? btn(split.primary, 'btn-primary stc-act-primary') : ''}${split.secondary.map(a => btn(a, 'btn-secondary')).join('')}${kebabHtml(items, { tip: 'More actions', title: 'More' })}</div>`;
  }
  /** R115 integ fix (D3): the action row is ONE line, MEASURED — never clipped with an ellipsis. Buttons keep their natural
   *  width; while the row overflows, the last secondary moves into ⋯ (rebuilt markup, so ⋯ lists it). The blue primary
   *  always reads in full: only when it alone (+ ⋯) still does not fit may its label wrap. Runs before the row is bound. */
  private fitActions(el: HTMLElement, top: InspEntry, data: unknown): void {
    let row = el.querySelector<HTMLElement>('.stc-actions');
    if (!row || !row.clientWidth) return;   // not laid out (a hidden panel) — keep what was asked for
    let n = row.querySelectorAll(':scope > .btn-secondary').length;
    while (n > 0 && this.actionsOverflow(row)) {
      n--;
      const tmp = document.createElement('div');
      tmp.innerHTML = this.actionRowHtml(top, data, n);
      const next = tmp.firstElementChild as HTMLElement | null;
      if (!next) return;
      row.replaceWith(next);
      row = next;
    }
    row.classList.toggle('stc-actions-tight', this.actionsOverflow(row));
  }
  private actionsOverflow(row: HTMLElement): boolean {
    const w = row.clientWidth;
    if (!w) return false;
    const kids = [...row.children].filter((k): k is HTMLElement => k instanceof HTMLElement && !k.hidden && k.getClientRects().length > 0);
    const gap = parseFloat(getComputedStyle(row).columnGap) || 0;
    const need = kids.reduce((sum, k) => sum + k.getBoundingClientRect().width, 0) + gap * Math.max(0, kids.length - 1);
    return need > w + 0.5 || row.scrollWidth > w + 1;
  }
  /** The window (so the panel) changed size: rebuild + re-measure the action row alone (sections keep their typing). */
  private refitActions(): void {
    const el = document.getElementById('stc-panel');
    const top = this.top();
    if (!el || !top || !el.classList.contains('open')) return;
    const row = el.querySelector<HTMLElement>('.stc-actions');
    if (!row) return;
    const kind = top.ref.kind;
    const data = this.dataOf(top);
    const tmp = document.createElement('div');
    tmp.innerHTML = this.actionRowHtml(top, data);
    const next = tmp.firstElementChild as HTMLElement | null;
    if (!next) return;
    row.replaceWith(next);
    this.fitActions(el, top, data);
    el.querySelector<HTMLElement>('.stc-actions')?.querySelectorAll<HTMLElement>('[data-stc-act]').forEach(b => b.addEventListener('click', (ev) => { ev.preventDefault(); this.runAction(kind, String(b.dataset.stcAct || '')); }));
  }
  private runAction(kind: string, key: string): void {
    const top = this.top();
    if (!top || top.ref.kind !== kind) return;
    const a = actionsFor(kind, this.role()).find(x => x.key === key);
    if (!a) return;
    const data = this.dataOf(top);
    try { a.run(top.ref, data, this.ctx); }
    catch (e) { console.warn('[calendar] action failed:', e); this.toast(`Couldn't do that — ${(e as Error).message || 'unexpected error'}`); }
  }
  /** Delegated clicks in the panel: the head, accordions, the chooser, the rail list, then today's stay / block / turn / WO handlers. */
  private panelClick(e: MouseEvent): void {
    const t = e.target as HTMLElement;
    if (t.closest('[data-stc-act]') || t.closest('[data-kebab]')) return;   // bound per element / by Kebab
    if (t.closest('[data-stc-close]')) { this.closeInspectorAll(); return; }
    if (t.closest('[data-stc-back]')) { this.backEntry(); return; }
    if (t.closest('[data-stc-insp-retry]')) { this.loadTop(true); return; }
    const tog = t.closest<HTMLElement>('[data-stc-sec-toggle]');
    if (tog) {
      const top = this.top(); if (!top) return;
      const key = String(tog.dataset.stcSecToggle || '');
      const sec = sectionsFor(top.ref.kind).find(s => s.key === key);
      if (!sec) return;
      const open = !this.secIsOpen(top.ref.kind, sec);
      this.secOpen.set(`${top.ref.kind}|${key}`, open);
      if (top.ref.kind === 'stay' && key === 'details') { this.detailsOpen = open; ls.set('stc_details_open', open ? '1' : '0'); }
      this.drawPanel();
      return;
    }
    if (t.closest('[data-stc-ritem]') || t.closest('[data-stc-rail-close]')) { this.railClick(e, true); return; }
    const ch = t.closest<HTMLElement>('[data-stc-ch-act]');
    if (ch) { this.chooserPick(String(ch.dataset.stcChAct || '')); return; }
    const chs = t.closest<HTMLElement>('[data-stc-ch-open]');
    if (chs) { const [kind, id, lid] = String(chs.dataset.stcChOpen || '').split('|'); if (kind && id) this.pushEntry({ kind, id, listing_id: lid || undefined }); return; }
    const ap = t.closest<HTMLElement>('[data-stc-apply]');
    if (ap) { void this.apply(ap.dataset.stcApply as Opp['kind']); return; }
    if (t.closest('[data-stc-conv]')) { this.openConversation(); return; }
    if (t.closest('[data-stc-issue]')) { this.openIssue(); return; }
    if (t.closest('[data-stc-rv-open]')) { this.openReview(); return; }
    if (t.closest('[data-stc-arrivals]')) { this.openArrivals(); return; }
    if (t.closest('[data-stc-blocks-manage]')) { this.go('portfolio', 'calendar-blocks'); return; }   // App.ts registers 'calendar-blocks' inside the Portfolio hub
    const hold = t.closest<HTMLElement>('[data-stc-hold-from]');
    if (hold) { const [lid, ci] = String(hold.dataset.stcHoldFrom || '').split('|'); this.openAdd({ listing_id: lid, check_in: ci }); return; }
    // R112
    if (t.closest('[data-stc-inq-dismiss]')) { void this.dismissInquiry(this.selectedId); return; }
    if (t.closest('[data-stc-inq-restore]')) { void this.restoreInquiry(this.selectedId); return; }
    const item = t.closest<HTMLElement>('[data-stc-item]');
    if (item) { void this.setCheckItem(item.dataset.stcItem || '', (item.dataset.stcState || 'pending') as ItemState); return; }
    if (t.closest('[data-stc-gen]')) { void this.generateChecks(); return; }
    const sw = t.closest<HTMLElement>('[data-stc-el-toggle]');
    if (sw) { void this.toggleEciLco(sw.dataset.stcElToggle === 'lco' ? 'lco' : 'eci'); return; }
    const clr = t.closest<HTMLElement>('[data-stc-el-clear]');
    if (clr) { void this.writeEciLco(clr.dataset.stcElClear === 'lco' ? 'lco' : 'eci', null); return; }
    const ph = t.closest<HTMLElement>('[data-stc-photo-open]');
    if (ph) { window.open(String(ph.dataset.stcPhotoOpen || ''), '_blank', 'noopener'); return; }
    // the turn view (R112 popover body) and the lite WO view (R111) — the same data attributes as the old #stc-pop
    if (t.closest('[data-stc-pop-open]')) { this.openMaintenance(); return; }
    if (t.closest('[data-stc-pop-details]') && this.popWo && this.data) {   // full WO card without leaving the calendar
      const wo = this.data.events.work_orders.find(w => w.id === this.popWo);
      if (wo) openWoQuickView(wo.id, { listingId: wo.listing_id, onChange: () => { void this.load(true, true); } });
      return;
    }
    if (t.closest('[data-stc-pop-book]') && this.popWo && this.data) {   // a due-only chip booked on its due day
      const wo = this.data.events.work_orders.find(w => w.id === this.popWo);
      if (wo && !this.popBusy) void this.rescheduleWo(wo.id, wo.title, wo.date);
      return;
    }
    // the 409 "already has a clean on <date>" notice: open HK on THAT clean (the one to finish or move), not the one being moved
    if (t.closest('[data-stc-pop-hk-existing]')) { this.openHousekeeping((this.popExisting && this.popExisting.id) || this.popTurn); return; }
    if (t.closest('[data-stc-pop-hk]')) { this.openHousekeeping(this.popTurn); return; }
    if (t.closest('[data-stc-pop-confirm]')) {   // "Move" (the first post) / "Assign anyway" / "Move anyway" (the shared 409 helper's inline answer)
      const c = this.popConfirm, id = this.popTurn;
      if (!c || !id) return;
      if (c.override && this.turnAsk) { this.popConfirm = null; this.answerTurnAsk(true); return; }
      if (this.popBusy) return;
      this.popConfirm = null;
      if (c.kind === 'assign') void this.assignTurn(id, (c.body.cleaner_ids as string[]) || [], true);
      else void this.moveTurn(id, String(c.body.date || ''), { override: c.override });
      return;
    }
    if (t.closest('[data-stc-pop-cancel]')) { this.popConfirm = null; this.popExisting = null; this.popErr = ''; this.answerTurnAsk(false); this.drawPanel(); return; }   // the redraw snaps the selects / date back
  }
  private panelChange(e: Event): void {
    const tgt = e.target as HTMLElement;
    const t = tgt as HTMLInputElement;
    const photo = t.closest<HTMLInputElement>('[data-stc-item-photo]');
    if (photo) { const f = photo.files && photo.files[0]; if (f) void this.uploadItemPhoto(photo.dataset.stcItemPhoto || '', f); return; }
    const time = t.closest<HTMLInputElement>('[data-stc-el-time]');
    if (time) { void this.saveEciLcoTime(time.dataset.stcElTime === 'lco' ? 'lco' : 'eci', time.value); return; }
    if (tgt.closest('[data-stc-ch-prop]') || tgt.closest('[data-stc-ch-date]')) { this.chooserRetarget(); return; }
    const inp = tgt.closest<HTMLInputElement>('[data-stc-pop-date]');
    // Branch on the view that is DRAWN (.stc-pop-turn / .stc-pop-wo), never on popTurn / popWo alone:
    // a WO "Booked day" change must post /work-orders/:id/schedule and can never move a turn.
    if (tgt.closest('.stc-pop-turn')) {
      if (!this.popTurn || !this.data) return;
      const turn = this.data.events.turns.find(x => x.id === this.popTurn);
      if (!turn) return;
      if (inp) {   // turn: clean day — never posts on raw `change` (a half-typed date fires it); the view asks first
        // Fix-verify [11]: Chromium fires `change` on EVERY keystroke inside a date
        // segment ("1" then "5" → Oct 1 then Oct 15; a typed year passes 0002-…), so
        // the handler never redraws the panel (that recreated the input and
        // dropped the focus) and never snaps the typed value: it only patches the
        // ask strip under the input. The "Move" button is the only thing that posts.
        const v = inp.value;
        if (turn.done) { inp.value = turn.date; return; }
        if (!this.validDate(v)) { if (this.popConfirm && this.popConfirm.kind === 'move') this.popConfirm = null; this.popErr = ''; this.patchTurnAsk(); return; }   // mid-edit (empty) — keep typing
        if (v === turn.date) { if (this.popConfirm && this.popConfirm.kind === 'move') { this.popConfirm = null; this.popErr = ''; this.patchTurnAsk(); } return; }   // back to where it is — drop the ask (a 409 "Move anyway" too)
        if (v < this.data.as_of) { if (this.popConfirm && this.popConfirm.kind === 'move') this.popConfirm = null; this.popErr = 'Cleans can\'t move into the past.'; this.patchTurnAsk(); return; }
        this.askTurnMove(turn, v);
        return;
      }
      const sel = tgt.closest<HTMLSelectElement>('[data-stc-pop-cleaner]');
      if (sel) {   // turn: cleaner / second cleaner → de-duped ids → assign
        const host = document.getElementById('stc-panel');
        const ids = [...new Set(Array.from(host ? host.querySelectorAll<HTMLSelectElement>('[data-stc-pop-cleaner]') : []).sort((a, b) => Number(a.dataset.stcPopCleaner) - Number(b.dataset.stcPopCleaner)).map(s => s.value).filter(Boolean))];
        void this.assignTurn(this.popTurn, ids, false);
      }
      return;
    }
    if (!tgt.closest('.stc-pop-wo') || !inp || !this.popWo || !this.data) return;
    const wo = this.data.events.work_orders.find(w => w.id === this.popWo);
    if (!wo) return;
    const v = inp.value;
    if (!this.validDate(v)) { inp.value = wo.date; return; }
    if (v === wo.date && !wo.due_only) return;
    void this.rescheduleWo(wo.id, wo.title, v).then(ok => { if (!ok) inp.value = wo.date; });
  }

  // ── the chooser (inspector 'date'): every create entry for a listing + day, ordered by the view ──
  private secChooser(ref: EntityRef): string {
    const d = this.data;
    const date = ref.date || (d ? d.as_of : this.bizToday());
    const lid = ref.listing_id || '';
    const cell = this.cellOf(lid, date);
    const acts = this.dateActions(cell);
    // R115 integ fix (D2): no home yet ("+ Add") never gates the list — the view's create kinds show at once and
    // each form asks "Which home?" itself (calendar/homePick.ts; a Task may stay an office task). Only the day is here.
    const pick = !lid ? `<div class="stc-ch-pick stc-ch-pick-day"><label class="stc-ch-daylbl"><span class="stc-dl">Day</span><input type="date" class="input" data-stc-ch-date value="${this.esc(date)}" aria-label="Day"></label><span class="stc-ch-hint">Each form asks which home — a task can stay an office task.</span></div>` : '';
    const holder = cell.stay_id ? this.findStay(cell.stay_id) : null;
    const blk = lid && d ? d.blocks.find(b => b.listing_id === lid && b.start <= date && date < b.end) : null;
    const night = !lid ? '' : `<div class="stc-ch-night">${cell.past ? '<span class="stc-pill stc-pill-muted">past day</span>' : ''}${holder ? `Booked — <button type="button" class="stc-link" data-stc-ch-open="stay|${this.esc(holder.id)}|${this.esc(lid)}">${this.esc(holder.guest_name)}</button>` : blk ? `${blk.kind === 'held' ? 'Held' : 'Blocked'}${blk.note ? ` — ${this.esc(blk.note)}` : ''}` : 'Free night'}</div>`;
    const list = acts.length
      ? `<div class="stc-choose">${acts.map((a, i) => `<button type="button" class="btn ${i === 0 ? 'btn-primary' : 'btn-secondary'}" data-stc-ch-act="${this.esc(a.key)}"><span class="stc-ch-ic">${glyph(a.icon, 15)}</span>${this.esc(a.label)}</button>`).join('')}</div>`
      : `<div class="stc-none">${cell.past ? 'Nothing can be added on a past day here.' : cell.taken ? 'This night is taken — nothing else can be added on it here yet.' : 'Nothing can be added here yet.'}</div>`;
    const evs = lid && d ? [
      ...d.events.turns.filter(t => t.listing_id === lid && t.date === date).map(t => `<button type="button" class="stc-link" data-stc-ch-open="turn|${this.esc(t.id)}|${this.esc(lid)}">🧹 Clean ${this.esc(t.status.replace('_', ' '))}</button>`),
      ...d.events.work_orders.filter(w => w.listing_id === lid && w.date <= date && date <= (w.end || w.date)).map(w => `<button type="button" class="stc-link" data-stc-ch-open="wo|${this.esc(w.id)}|${this.esc(lid)}">🔧 ${this.esc(w.title)}</button>`),
    ] : [];
    return `${pick}${night}${list}${evs.length ? `<div class="stc-ch-ctx"><div class="stc-dl">Already that day</div><div class="stc-evlist">${evs.join('')}</div></div>` : ''}`;
  }
  private chooserPick(key: string): void {
    const top = this.top();
    if (!top || top.ref.kind !== 'date') return;
    const cell = this.cellOf(top.ref.listing_id || '', top.ref.date || (this.data ? this.data.as_of : this.bizToday()));
    const a = this.dateActions(cell).find(x => x.key === key);
    if (!a) return;
    try { a.run(top.ref, cell, this.ctx); } catch (e) { this.toast(`Couldn't do that — ${(e as Error).message || 'unexpected error'}`); }
  }
  private chooserRetarget(): void {
    const top = this.top();
    if (!top || top.ref.kind !== 'date') return;
    const el = document.getElementById('stc-panel');
    const lid = (el?.querySelector<HTMLSelectElement>('[data-stc-ch-prop]') || { value: '' }).value || '';
    const date = (el?.querySelector<HTMLInputElement>('[data-stc-ch-date]') || { value: '' }).value || '';
    if (!this.validDate(date)) return;
    top.ref = this.dateRef(lid, date);
    this.drawPanel();
  }
  private dateSub(ref: EntityRef): string {
    const date = ref.date || '';
    if (!this.validDate(date)) return '';
    const cell = this.cellOf(ref.listing_id || '', date);
    return `${this.fmtWMD(date)}${ref.listing_id ? (cell.past ? ' · past' : cell.taken ? ' · taken' : ' · free') : ''}`;
  }

  // ── the form sheet (z 300): every create / edit form; never redrawn by timeline / panel redraws ──
  private openSheet(html: string, o?: { title?: string; onClose?: () => void }): HTMLElement {
    const host = document.getElementById('stc-sheet');
    if (this.sheetOpen && !this.closeSheet(false)) {   // a dirty sheet asked and the user kept it — hand back its body
      const keep = host && host.querySelector<HTMLElement>('.stc-sheet-body');
      if (keep) return keep;
    }
    const div = document.createElement('div');
    if (!host) return div;
    host.innerHTML = `<div class="stc-sheet-ovl"><div class="stc-sheet" role="dialog" aria-modal="true" aria-label="${this.esc(o && o.title || 'Form')}"><div class="stc-sheet-head"><h3>${this.esc(o && o.title || '')}</h3><button type="button" class="stc-close" data-stc-sheet-close aria-label="Close">✕</button></div><div class="stc-sheet-body"></div></div></div>`;
    const body = host.querySelector<HTMLElement>('.stc-sheet-body')!;
    body.innerHTML = html;
    body.querySelectorAll<HTMLElement>('.stc-sheet-foot').forEach(f => f.setAttribute('data-bottom-bar', ''));
    this.sheetOpen = true; this.sheetDirty = false; this.sheetOnClose = o && o.onClose ? o.onClose : null;
    closeKebab();
    return body;
  }
  /** Close the sheet; a dirty one asks first (force skips the ask). Returns false when the user kept it open. */
  private closeSheet(force: boolean): boolean {
    if (!this.sheetOpen) return true;
    const host = document.getElementById('stc-sheet');
    // a sheet can put its own question on an element in its body
    const ask = host && host.querySelector('[data-stc-sheet-ask]')?.getAttribute('data-stc-sheet-ask');
    if (!force && this.sheetDirty && !window.confirm(ask || 'Discard what you typed?')) return false;
    if (host) host.innerHTML = '';
    this.sheetOpen = false; this.sheetDirty = false; this.picking = false;
    const f = this.sheetOnClose; this.sheetOnClose = null;
    if (f) safe(() => { f(); return true; }, true);
    return true;
  }
  /** ctx.pickDay: the listing's next 21 days (from base data) in the sheet — past refused unless allowPast, taken unless allowTaken. */
  private pickDay(o: { listing_id: string; label: string; allowPast?: boolean; allowTaken?: boolean; onPick: (date: string) => void }): void {
    const d = this.data;
    const asOf = d ? d.as_of : this.bizToday();
    const start = o.allowPast ? this.addDays(asOf, -7) : asOf;
    const days = Array.from({ length: 21 }, (_, i) => this.addDays(start, i));
    const known = (dt: string) => !!d && dt >= d.from && dt <= d.to;
    const cells = days.map(dt => {
      const holder = d ? d.stays.find(s => s.listing_id === o.listing_id && s.kind !== 'inquiry' && s.check_in <= dt && dt < s.check_out) : null;
      const blk = d ? d.blocks.find(b => b.listing_id === o.listing_id && b.start <= dt && dt < b.end) : null;
      const past = dt < asOf;
      const taken = !!holder || !!blk;
      const ok = (!past || !!o.allowPast) && (!taken || !!o.allowTaken);
      const ops = d ? d.events.turns.filter(t => t.listing_id === o.listing_id && t.date === dt).length + d.events.work_orders.filter(w => w.listing_id === o.listing_id && w.date === dt).length : 0;
      const st = !known(dt) ? '' : holder ? (holder.kind === 'owner' ? 'Owner' : this.initials(holder.guest_name)) : blk ? (blk.kind === 'held' ? 'Held' : 'Blocked') : 'Free';
      return `<button type="button" class="stc-sday${dt === asOf ? ' today' : ''}${holder ? ' booked' : blk ? ' blocked' : ''}" data-stc-pick="${dt}" ${ok ? '' : 'disabled'} title="${this.esc(this.fmtWMD(dt))}${st ? ` · ${this.esc(st)}` : ''}${ops ? ` · ${ops} already scheduled` : ''}"><span class="stc-sdow">${this.fmt(dt, { weekday: 'short' })}</span><span class="stc-snum">${Number(dt.slice(8, 10))}</span><span class="stc-sst">${this.esc(st)}${ops ? ` · ${ops}` : ''}</span></button>`;
    }).join('');
    const body = this.openSheet(`<div class="stc-strip-hint">${this.esc(this.propName(o.listing_id))} · tap a day${o.allowPast ? '' : ' (past days are greyed out)'}${o.allowTaken ? '' : ' — booked or blocked nights are greyed out'}.</div><div class="stc-strip" data-stc-strip>${cells}</div>
      <div class="stc-sheet-foot"><button type="button" class="btn btn-secondary" data-stc-sheet-close>Cancel</button></div>`, { title: o.label });
    this.picking = true;
    body.querySelectorAll<HTMLButtonElement>('[data-stc-pick]').forEach(b => b.addEventListener('click', () => {
      if (b.disabled) return;
      const dt = String(b.dataset.stcPick || '');
      if (!o.allowPast && dt < asOf) { this.toast('That day is in the past.'); return; }
      this.closeSheet(true);
      safe(() => { o.onPick(dt); return true; }, true);
    }));
  }

  // ── the quick popover (ctx.openPop): ≤320px anchored; a bottom sheet under 640px ──
  private modPopSkip = false;
  private openModPop(anchor: HTMLElement | DOMRect, html: string, o?: { width?: number; cls?: string; onClose?: () => void }): HTMLElement {
    this.closeModPop();
    const host = document.getElementById('stc-pop');
    const div = document.createElement('div');
    if (!host) return div;
    const vw = window.innerWidth, vh = window.innerHeight;
    const phone = vw < 640;
    const PW = Math.min((o && o.width) || 320, vw - 16);
    const rect = anchor instanceof HTMLElement ? anchor.getBoundingClientRect() : anchor;
    let left = Math.max(8, Math.min(rect.left, vw - PW - 8));
    let top = rect.bottom + 6;
    host.innerHTML = `<div class="stc-pop stc-mpop${phone ? ' stc-pop-sheet' : ''} ${clsSafe(o && o.cls)}" role="dialog" style="left:${left}px; top:${top}px; width:${PW}px;">${html}</div>`;
    const el = host.querySelector<HTMLElement>('.stc-pop')!;
    if (!phone) {
      const h = el.offsetHeight;
      if (top + h > vh - 8) top = rect.top - h - 6 >= 8 ? rect.top - h - 6 : Math.max(8, vh - h - 8);
      el.style.top = `${top}px`;
    }
    this.modPopOpen = true; this.modPopOnClose = o && o.onClose ? o.onClose : null;
    this.modPopSkip = true; setTimeout(() => { this.modPopSkip = false; }, 0);   // the click that opened it must not close it
    return el;
  }
  private closeModPop(): void {
    if (!this.modPopOpen) return;
    const host = document.getElementById('stc-pop');
    if (host) host.innerHTML = '';
    this.modPopOpen = false;
    const f = this.modPopOnClose; this.modPopOnClose = null;
    if (f) safe(() => { f(); return true; }, true);
  }

  // ── one shared 409 needs_confirm → confirm → resend {override:true} helper (ctx.postWithOverride; the turn view answers inline) ──
  private async postOverride<T = any>(url: string, body: Record<string, unknown>, o: { text?: (j: any) => string; ask?: (text: string) => Promise<boolean> }): Promise<PostResult<T>> {
    const send = async (b: Record<string, unknown>): Promise<PostResult<T>> => {
      let r: Response | null = null;
      try { r = await fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(b) }); } catch { r = null; }
      if (!r) return { ok: false, status: 0, json: null, error: 'no connection' };
      const j = await r.json().catch(() => null);
      return { ok: r.ok, status: r.status, json: j, error: r.ok ? undefined : String((j && (j.error || j.message)) || `HTTP ${r.status}`) };
    };
    const first = await send(body);
    if (first.status !== 409 || !first.json || !(first.json as any).needs_confirm) return first;
    const text = (o.text ? safe(() => o.text!(first.json), '') : '') || String((first.json as any).error || 'This goes over a limit. Go ahead anyway?');
    const yes = o.ask ? await o.ask(text) : window.confirm(text);
    if (!yes) return first;
    return send({ ...body, override: true });
  }
  /** The turn view's inline "… anyway" step for the shared helper: the strip shows the server's words; Assign / Move anyway answers. */
  private turnAskInline(kind: 'assign' | 'move', body: Record<string, unknown>): (text: string) => Promise<boolean> {
    return (text: string) => new Promise<boolean>(resolve => {
      this.answerTurnAsk(false);
      this.turnAsk = resolve;
      this.popBusy = false;
      this.popConfirm = { kind, body, override: true, text };
      this.drawPanel();
    }).then(ok => { if (ok) { this.popBusy = true; this.drawPanel(); } return ok; });
  }
  private answerTurnAsk(ok: boolean): void { const f = this.turnAsk; this.turnAsk = null; if (f) f(ok); }

  // ── entity helpers (titles, lookups) ──
  private propOf(id: string): Property | null { return (this.data && this.data.properties.find(p => p.listing_id === id)) || (this.roster ? this.roster.find(p => p.listing_id === id) || null : null); }   // the roster: a home the search hid still has a name
  private propName(id: string | null | undefined): string { if (!id) return ''; const p = this.propOf(id); return p ? p.name : (this.detail && this.detail.stay.listing_id === id && this.detail.stay.property ? this.detail.stay.property.name : id); }
  private woOf(id: string): WoEv | null { return this.data ? this.data.events.work_orders.find(w => w.id === id) || null : null; }
  private turnOf(id: string): TurnEv | null { return this.data ? this.data.events.turns.find(t => t.id === id) || null : null; }
  private turnDone(id: string): boolean { const t = this.turnOf(id); return !!t && t.done; }
  private turnMovable(): boolean { const t = this.popTurn ? this.turnOf(this.popTurn) : null; return !!t && !t.done; }
  private popTurnId(): string | null { return this.popTurn; }
  private flagOf(id: string): unknown { const s = this.findStay(id); const det = this.detail && this.detail.stay.id === id ? this.detail : null; return (det && det.flagged) || (s && s.flagged) || null; }
  private hostawayUrl(): string { const det = this.detailForSelected(); const u = det && det.details && det.details.hostaway_url; return typeof u === 'string' && /^https:\/\/[a-z0-9.-]*hostaway\.com\//i.test(u) ? u : ''; }
  private detailForSelected(): Detail | null { return this.detail && this.selectedId && this.detail.stay.id === this.selectedId ? this.detail : null; }
  private stayTitle(id: string): string { const s = this.findStay(id); return s ? s.guest_name : (this.detailLoading ? 'Loading the stay…' : 'Stay'); }
  private staySub(id: string): string { const s = this.findStay(id); return s ? `${this.propName(s.listing_id)} · ${this.fmtMD(s.check_in)} → ${this.fmtMD(s.check_out)}` : ''; }
  private blockKind(): 'held' | 'blocked' { const b = this.findBlock(this.selectedBlock); return ((this.blockInfo ? this.blockInfo.kind : b && b.kind) || 'blocked') as 'held' | 'blocked'; }
  private listingSub(id: string): string { const p = this.propOf(id); if (!p) return ''; return [p.full_name && p.full_name !== p.name ? p.full_name : '', p.bedrooms != null ? `${p.bedrooms} bd` : '', p.max_guests != null ? `sleeps ${p.max_guests}` : ''].filter(Boolean).join(' · '); }
  private turnTitle(id: string): string { const t = this.turnOf(id); return (this.popTurnData && this.popTurnData.turn.id === id && this.popTurnData.turn.listing_name) || (t ? this.propName(t.listing_id) : 'Clean'); }
  private turnSub(id: string): string { const t = this.turnOf(id); return t ? `Clean day ${this.fmtWMD(t.date)}${t.next_check_in && t.next_check_in !== t.date ? ` · arrival ${this.fmtMD(t.next_check_in)}` : ''}` : ''; }
  private woTitle(id: string): string { const w = this.woOf(id); return w ? w.title : 'Work order'; }
  /** Turn: "Move to another day…" → the day strip (taken nights are fine for a clean); the pick goes through the same ask as the date input. */
  private pickTurnDay(): void {
    const t = this.popTurn ? this.turnOf(this.popTurn) : null;
    if (!t || t.done) return;
    this.pickDay({ listing_id: t.listing_id, label: 'Move the clean to…', allowTaken: true, onPick: (date) => { if (date !== t.date) { this.askTurnMove(t, date); this.drawPanel(); } } });
  }
  private askTurnMove(turn: TurnEv, v: string): void {
    const prop = this.data ? this.data.properties.find(p => p.listing_id === turn.listing_id) : null;
    this.popConfirm = { kind: 'move', body: { date: v }, override: false, text: `Move the ${prop ? prop.name : turn.listing_id} clean from ${this.fmtWMD(turn.date)} to ${this.fmtWMD(v)}? (An inspection scheduled that day moves with it.)` };
    this.popErr = ''; this.popExisting = null;
    this.patchTurnAsk();
  }
  /** WO (lite): "Book / move to a day…" → the day strip (taken nights allowed — a guest may be in; past refused). */
  private pickWoDay(id: string): void {
    const w = this.woOf(id);
    if (!w) return;
    this.pickDay({ listing_id: w.listing_id, label: `Book "${w.title}" on…`, allowTaken: true, onPick: (date) => { void this.rescheduleWo(w.id, w.title, date); } });
  }

  private stateLabel(s: Stay, today: string): string {
    if (s.kind === 'inquiry') return 'Inquiry';
    if (s.state === 'checked_out') return 'Checked out';
    if (s.state === 'in_house') return s.check_out === today ? 'Checks out today' : s.check_in === today ? 'Arrives today' : 'In house';
    const n = this.diffDays(today, s.check_in);
    return n <= 0 ? 'Arrives today' : n === 1 ? 'Arrives tomorrow' : n <= 14 ? `Arrives in ${n} days` : `Arrives ${this.fmtMD(s.check_in)}`;
  }

  /** How the bar/pill LOOKS: a guest arriving today is in_house for the KPIs but isn't here yet — show them as upcoming. */
  private vstate(s: Stay, today: string): Stay['state'] {
    return s.state === 'in_house' && s.check_in === today ? 'upcoming' : s.state;
  }

  // ── the stay inspector: GET /api/stays/:id, then today's stay panel as sections ──
  private findStay(id: string | null): Stay | null {
    if (!id) return null;
    const s = this.data ? this.data.stays.find(x => x.id === id) : null;
    return s || (this.detail && this.detail.stay.id === id ? this.detail.stay : null);
  }
  private async fetchDetail(id: string): Promise<void> {
    const j = await fetch(`${API}/${encodeURIComponent(id)}`).then(r => r.ok ? r.json() : null).catch(() => null);
    if (this.selectedId !== id) return;
    this.detailLoading = false;
    this.detail = j && j.stay ? normalizeDetail(j, this.data ? this.data.as_of : this.bizToday()) : null;
    const top = this.top();
    if (top && top.ref.kind === 'stay' && top.ref.id === id) top.data = this.detail;
    this.drawPanel();
    this.markSelection();
  }
  private findBlock(sel: { listing_id: string; start: string; end: string } | null): Block | null {
    if (!sel || !this.data) return null;
    return this.data.blocks.find(b => b.listing_id === sel.listing_id && b.start === sel.start && b.end === sel.end) || null;
  }
  private async fetchBlock(): Promise<void> {
    const sel = this.selectedBlock;
    if (!sel || !this.validDate(sel.start) || !this.validDate(sel.end)) { this.blockLoading = false; this.drawPanel(); return; }
    this.blockLoading = true;
    const qs = `listing_id=${encodeURIComponent(sel.listing_id)}&start=${encodeURIComponent(sel.start)}&end=${encodeURIComponent(sel.end)}`;
    const j = await fetch(`${API}/block?${qs}`).then(r => r.ok ? r.json() : null).catch(() => null);
    const cur = this.selectedBlock;
    if (!cur || cur.listing_id !== sel.listing_id || cur.start !== sel.start || cur.end !== sel.end) return;
    this.blockLoading = false;
    this.blockInfo = j && typeof j === 'object' && (j.start || j.nights != null) ? { listing_id: String(j.listing_id || sel.listing_id), start: this.validDate(j.start) ? j.start : sel.start, end: this.validDate(j.end) ? j.end : sel.end, nights: Number(j.nights) || this.diffDays(sel.start, sel.end), note: typeof j.note === 'string' && j.note.trim() ? j.note : null, kind: j.kind === 'held' ? 'held' : 'blocked', manage_url: typeof j.manage_url === 'string' ? j.manage_url : undefined } : null;
    const top = this.top();
    if (top && top.ref.kind === 'block') top.data = this.blockInfo;
    this.drawPanel();
  }
  private stayBits(s: Stay) {
    const d = this.data;
    const today = d ? d.as_of : this.bizToday();
    const prop = (d && d.properties.find(p => p.listing_id === s.listing_id)) || (this.detail && this.detail.stay.property) || null;
    const det = this.detail && this.detail.stay.id === s.id ? this.detail : null;
    const flag = (det && det.flagged) || s.flagged;
    return { d, today, prop, det, flag, vs: this.vstate(s, today) };
  }
  /** The stay card: property, channel · guests · confirmation, state / verification / review pills, dates, ECI/LCO switches, turnover, verification, negative. */
  private secStayCard(s: Stay): string {
    const { today, prop, det, vs } = this.stayBits(s);
    const pillCls = s.kind === 'owner' ? 'stc-pill-owner' : s.kind === 'inquiry' ? 'stc-pill-inq' : `stc-pill-${vs}`;
    const pav = this.avatarHtml(prop ? (prop as Property).thumbnail_url : null, prop ? prop.name : s.listing_id, false);
    const eciLcoHtml = det && det.eci_lco && s.kind !== 'inquiry' && s.state !== 'checked_out' ? this.eciLcoHtml(det.eci_lco) : '';
    const guestsLine = [s.channel ? this.esc(s.channel) : '', s.guests != null ? `${s.guests} guest${s.guests === 1 ? '' : 's'}` : '', det && det.guest.confirmation_code ? this.esc(det.guest.confirmation_code) : ''].filter(Boolean).join(' · ');
    // R111: verification (Arrivals rollup) for upcoming / arriving-today stays, the review state
    const ck = (det && det.stay.checks) || s.checks || null;
    const showVer = s.kind === 'stay' && vs === 'upcoming';
    const verMeta = showVer ? (ck ? (VER_PILL[ck.rollup] || VER_PILL.pending) : null) : null;
    const verPill = verMeta && ck ? `<span class="stc-pill stc-pill-v-${verMeta.cls}" title="Arrival checks (Arrivals page)">${verMeta.ic} ${verMeta.label}${ck.rollup === 'pending' && Number.isFinite(ck.required_done) && Number.isFinite(ck.required_total) ? ` (${ck.required_done}/${ck.required_total})` : ''}</span>` : '';
    const verLine = showVer ? `<div class="stc-verline">${ck ? `Arrival checks: ${this.esc(verMeta!.label.toLowerCase())}${Number.isFinite(ck.required_total) && ck.required_total > 0 ? ` · ${ck.required_done}/${ck.required_total} required done` : ''}${ck.rollup === 'not_generated' ? ' · not generated yet (they sync automatically, or ↻ Generate on Arrivals)' : ''}` : 'Arrival checks live on the Arrivals page'} · <button type="button" class="stc-link" data-stc-arrivals>Open Arrivals</button></div>` : '';
    const rv = (det && det.review) || s.review || null;
    const awaitPill = rv && rv.status === 'awaiting' && s.kind === 'stay' ? `<span class="stc-pill stc-pill-muted" title="The guest has not left a review yet">☆ awaiting review</span>` : '';
    const rvPill = rv && rv.status === 'received' && s.kind === 'stay' ? `<span class="stc-pill stc-pill-rv">★ ${rv.rating != null ? this.esc(rv.rating) : 'Review'}</span>` : '';
    const negLine = s.negative ? `<div class="stc-negline">😟 Negative experience — the latest classified message from this guest is negative and still open.</div>` : '';
    const loading = this.detailLoading && !det ? `<div class="stc-muted" style="margin-top:8px;">Loading the guest…</div>` : '';
    return `<div class="stc-staycard${s.negative ? ' stc-staycard-neg' : ''}">
          <div class="stc-staycard-top">
            ${pav}
            <div class="stc-staycard-txt"><div class="stc-pname">${this.esc(prop ? prop.name : s.listing_id)}</div><div class="stc-muted">${guestsLine}</div></div>
            <div class="stc-pills"><span class="stc-pill ${pillCls}"><span class="stc-pill-dot"></span>${s.kind === 'owner' ? '🏠 Owner · ' : ''}${this.esc(this.stateLabel(s, today))}</span>${verPill}${rvPill}${awaitPill}</div>
          </div>
          <div class="stc-staycard-dates">
            <div><div class="stc-dl">Check-in</div><div class="stc-dv">${this.esc(this.fmtWMD(s.check_in))}</div></div>
            <span class="stc-nights">🌙 ${s.nights} night${s.nights === 1 ? '' : 's'}${s.long_stay ? ' · 30+' : ''}</span>
            <div style="text-align:right;"><div class="stc-dl">Checkout</div><div class="stc-dv">${this.esc(this.fmtWMD(s.check_out))}</div></div>
          </div>
          ${eciLcoHtml}
          ${s.turnover ? `<div class="stc-turnline"><span class="stc-turn stc-turn-${this.esc(s.turnover.status)}"></span>Turnover before arrival: ${this.esc(s.turnover.status.replace('_', ' '))}${s.turnover.same_day ? ' · same-day' : ''}</div>` : ''}
          ${verLine}
          ${negLine}
          ${loading}
        </div>`;
  }
  private secInquiry(s: Stay): string { return this.inquiryHtml(s, this.detail && this.detail.stay.id === s.id ? this.detail : null); }
  private secOpps(s: Stay): string {
    const { det } = this.stayBits(s);
    const opps = (det ? det.stay.opportunities : s.opportunities) || [];
    const oppIcon: Record<Opp['kind'], string> = { early_checkin: '🌅', gap_after: '🌙', late_checkout: '🌇' };
    const oppsHtml = opps.length ? opps.map(o => {
      const busy = this.applying.has(o.kind);
      const btn = o.applied
        ? `<button type="button" class="btn btn-secondary stc-apply stc-applied" disabled>Applied ✓</button>`
        : o.declined
          ? `<button type="button" class="btn btn-secondary stc-apply stc-applied" disabled title="A Housekeeping manager turned this off by hand — lift it on the Housekeeping page">Off in Housekeeping</button>`
          : `<button type="button" class="btn btn-secondary stc-apply" data-stc-apply="${o.kind}" ${busy ? 'disabled' : ''}>${busy ? 'Applying…' : '✦ Apply'}</button>`;
      const src = o.applied && o.applied_src && o.applied_src !== 'calendar' ? `<div class="stc-opp-src">Marked on ${this.esc(o.applied_src === 'manual' ? 'Housekeeping' : o.applied_src === 'auto' ? 'Hostaway times (auto)' : o.applied_src === 'gap-nights' ? 'Gap Nights' : o.applied_src)}</div>`
        : o.declined ? `<div class="stc-opp-src">Turned off by hand on Housekeeping — the manager's call stands until it is changed there.</div>` : '';
      return `<div class="stc-opp ${o.applied || o.declined ? 'stc-opp-done' : ''}">
        <span class="stc-opp-ic">${oppIcon[o.kind] || '✦'}</span>
        <div class="stc-opp-body"><div class="stc-opp-title">${this.esc(o.title)}</div><div class="stc-opp-text">${this.esc(o.text)}</div>${src}</div>
        ${btn}</div>`;
    }).join('') : `<div class="stc-none">${s.kind === 'stay' ? (s.state === 'checked_out' ? 'This stay is over — nothing to offer.' : 'Nothing to offer right now: a neighbour checks out on arrival day, arrives on checkout day, and no gap follows.') : s.kind === 'owner' ? 'Owner stays get no offers.' : 'An inquiry is not booked yet — offers come once it converts.'}</div>`;
    return `${oppsHtml}
        ${opps.length ? `<div class="stc-fine">Apply marks the offer as made so Housekeeping, Inspections and Gap Nights see it. It does not message the guest or change Hostaway — send the offer from the conversation.</div>` : ''}
        ${this.applyNote ? `<div class="stc-note">${this.esc(this.applyNote)}</div>` : ''}`;
  }
  private secSentiment(s: Stay): string {
    const { flag } = this.stayBits(s);
    const sentIcon = { positive: '😊', neutral: '😐', negative: '😟' } as const;
    const sentLabel = { positive: 'Positive', neutral: 'Neutral', negative: 'Negative' } as const;
    return flag
      ? `<div class="stc-sent stc-sent-${flag.sentiment}">
          <span class="stc-sent-ic">${sentIcon[flag.sentiment] || '😐'}</span>
          <div><div class="stc-sent-title">${sentLabel[flag.sentiment] || 'Neutral'}${flag.open ? '' : ' <span class="stc-muted">· resolved</span>'}${flag.recovery_status && flag.recovery_status !== 'open' ? ` <span class="stc-muted">· recovery: ${this.esc(stateLabel(flag))}</span>` : ''}</div>
          <div class="stc-sent-text">${this.esc(flag.summary || flag.excerpt || 'Classified from the guest conversation.')}</div>
          ${flag.last_message_at ? `<div class="stc-muted" style="margin-top:4px;">Last message ${this.esc(Formatters.formatDate(flag.last_message_at))}</div>` : ''}</div>
        </div>`
      : `<div class="stc-sent stc-sent-none"><span class="stc-sent-ic">💬</span><div><div class="stc-sent-title">No flagged messages</div><div class="stc-sent-text">Nothing from this guest's conversation has been classified — that usually means a quiet, easy stay.</div></div></div>`;
  }
  private secReview(s: Stay): string {
    const { det } = this.stayBits(s);
    const rv = (det && det.review) || s.review || null;
    return this.reviewHtml(rv, s);
  }
  private secChecklist(s: Stay): string {
    const { det, today } = this.stayBits(s);
    const checks = det && det.checks;
    // R112: the Arrival checklist section (item ticks) replaces the R111 readiness line whenever the server sends arrival_check
    const ac = det && det.arrival_check && s.kind === 'stay' && this.vstate(s, today) === 'upcoming' ? det.arrival_check : null;
    return ac ? this.checklistHtml(ac, s, today)
      : checks ? `<div class="stc-checks">${checks.done}/${checks.total} pre-arrival checks done${checks.pending.length ? ` · pending: ${this.esc(checks.pending.map(p => p.label).join(', '))}` : ' · all set'}</div>` : '';
  }

  // ── the listing inspector (shell: name, thumbnail, basic facts — the guest builder adds sections) ──
  private secListing(id: string): string {
    const p = this.propOf(id);
    const d = this.data;
    if (!p || !d) return '';
    const pav = this.avatarHtml(p.thumbnail_url, p.name, false);
    const row = (k: string, v: string) => `<div class="stc-kv-row"><span class="stc-kv-k">${k}</span><span class="stc-kv-v">${v}</span></div>`;
    const tonight = d.stays.find(s => s.listing_id === id && s.kind !== 'inquiry' && s.check_in <= d.as_of && d.as_of < s.check_out);
    const next = d.stays.filter(s => s.listing_id === id && s.kind === 'stay' && s.check_in > d.as_of).sort((a, b) => a.check_in.localeCompare(b.check_in))[0];
    const rows = [
      row('Tonight', tonight ? `<button type="button" class="stc-link" data-stc-ch-open="stay|${this.esc(tonight.id)}|${this.esc(id)}">${this.esc(tonight.guest_name)}</button>` : p.vacant_tonight ? 'Vacant' : '<span class="stc-muted">blocked / held</span>'),
      next ? row('Next arrival', `<button type="button" class="stc-link" data-stc-ch-open="stay|${this.esc(next.id)}|${this.esc(id)}">${this.esc(this.fmtWMD(next.check_in))} · ${this.esc(next.guest_name)}</button>`) : '',
      row('Booked in view', `${Math.max(0, Math.min(100, Number(p.occupancy_pct) || 0))}%`),
      p.turn_next ? row('Next clean', `${this.esc(p.turn_next.date === d.as_of ? 'today' : this.fmtWMD(p.turn_next.date))}${p.turn_next.unassigned ? ' · unassigned' : p.turn_next.status ? ` · ${this.esc(p.turn_next.status.replace('_', ' '))}` : ''}`) : '',
      p.open_weekend ? row('Open weekend', `${this.esc(this.fmtWMD(p.open_weekend.fri))} – ${this.esc(this.fmtMD(p.open_weekend.sat))}`) : '',
      p.bedrooms != null || p.max_guests != null ? row('Home', [p.bedrooms != null ? `${p.bedrooms} bedroom${p.bedrooms === 1 ? '' : 's'}` : '', p.max_guests != null ? `sleeps ${p.max_guests}` : ''].filter(Boolean).join(' · ')) : '',
      p.is_mtr ? row('Mid-term', `MTR home${p.mtr_source ? ` (${this.esc(p.mtr_source === 'min_nights' ? 'min nights' : p.mtr_source)})` : ''}`) : '',
    ].filter(Boolean).join('');
    return `<div class="stc-staycard"><div class="stc-staycard-top">${pav}<div class="stc-staycard-txt"><div class="stc-pname">${this.esc(p.name)}</div><div class="stc-muted">${this.esc(p.full_name || '')}</div></div></div></div>
      <div class="stc-kv" style="margin-top:12px;">${rows}</div>`;
  }

  private stars(r: number): string {
    const n = Math.max(0, Math.min(5, Math.round(r)));
    return '★'.repeat(n) + '☆'.repeat(5 - n);
  }
  private reviewHtml(rv: Review | null, s: Stay): string {
    if (!rv || s.kind !== 'stay' || rv.status === 'none') return '';
    if (rv.status === 'awaiting') {
      return `<div class="stc-none">Awaiting the guest's review — the window runs about 14 days after checkout${s.state === 'in_house' ? ' (they are still here)' : ''}.</div>`;
    }
    const ch = rv.challenge && rv.challenge.status ? `<span class="stc-pill stc-pill-muted">challenge · ${this.esc(rv.challenge.status)}</span>` : '';
    const when = rv.submitted_at ? `<span class="stc-muted">· ${this.esc(Formatters.formatDate(rv.submitted_at))}</span>` : '';
    const text = rv.text ? `<div class="stc-rv-text">“${this.esc(rv.text)}”</div>` : (rv.has_text && this.detailLoading ? `<div class="stc-muted">Loading the review text…</div>` : `<div class="stc-muted">Rating only — the guest left no public text.</div>`);
    const priv = rv.private_feedback ? `<div class="stc-rv-sub"><span class="stc-dl">Private feedback</span><div class="stc-rv-text">${this.esc(rv.private_feedback)}</div></div>` : '';
    const reply = rv.host_reply ? `<div class="stc-rv-sub"><span class="stc-dl">Our reply</span><div class="stc-rv-text">${this.esc(rv.host_reply)}</div></div>` : (rv.text ? `<div class="stc-rv-sub stc-muted">No host reply yet.</div>` : '');
    const open = rv.id ? `<button type="button" class="btn btn-secondary stc-rv-open" data-stc-rv-open>★ Open in Reviews</button>` : '';
    return `<div class="stc-review">
        <div class="stc-rv-head"><span class="stc-stars">${rv.rating != null ? this.stars(Number(rv.rating)) : '★'}</span>${rv.rating != null ? `<b>${this.esc(rv.rating)}</b>` : ''} ${when} ${ch}</div>
        ${text}${priv}${reply}
        ${open ? `<div class="stc-rv-actions">${open}</div>` : ''}
      </div>`;
  }
  private chanLabel(c: Chan, fallback: string): string {
    if (c && typeof c === 'object') return String(c.label || c.raw || fallback || '');
    return String(c || fallback || '');
  }
  private fmtMoney(n: number | null | undefined, cur: string | null | undefined): string {
    if (!Number.isFinite(n as number)) return '—';
    const code = (cur || 'USD').toUpperCase();
    try { return new Intl.NumberFormat('en-US', { style: 'currency', currency: code, maximumFractionDigits: 2 }).format(Number(n)); }
    catch { return `${code} ${Number(n).toFixed(2)}`; }
  }
  private detailsHtml(det: Detail | null, s: Stay): string {
    if (!det) return '';
    const dd = det.details || {};
    const row = (k: string, v: string) => `<div class="stc-kv-row"><span class="stc-kv-k">${k}</span><span class="stc-kv-v">${v}</span></div>`;
    const rows: string[] = [];
    const conf = dd.confirmation_code || det.guest.confirmation_code;
    if (conf) rows.push(row('Confirmation', `<span class="mono">${this.esc(conf)}</span>`));
    rows.push(row('Channel', this.esc(this.chanLabel(dd.channel, s.channel))));
    const st = dd.status || s.status;
    if (st) rows.push(row('Status', this.esc(String(st).replace(/_/g, ' '))));
    if (dd.booked_at) rows.push(row('Booked on', this.esc(Formatters.formatDate(dd.booked_at))));
    const parts = [[dd.adults, 'adult'], [dd.children, 'child', 'children'], [dd.infants, 'infant'], [dd.pets, 'pet']] as [number | null | undefined, string, string?][];
    const breakdown = parts.filter(([n]) => n != null && Number.isFinite(n as number)).map(([n, one, many]) => `${n} ${n === 1 ? one : (many || one + 's')}`).join(' · ');
    const guests = dd.guests != null ? dd.guests : s.guests;
    if (guests != null || breakdown) rows.push(row('Guests', `${guests != null ? this.esc(guests) : ''}${breakdown ? `${guests != null ? ' · ' : ''}${this.esc(breakdown)}` : ''}`));
    rows.push(row('Nights', this.esc(dd.nights != null ? dd.nights : s.nights)));
    if (det.guest.email) rows.push(row('Email', this.esc(det.guest.email)));
    if (det.guest.phone) rows.push(row('Phone', this.esc(det.guest.phone)));
    if (dd.host_note) rows.push(row('Host note', this.esc(dd.host_note)));
    if (dd.guest_note) rows.push(row('Guest note', this.esc(dd.guest_note)));
    const m = dd.money;
    if (m) {
      rows.push(row('Total', this.fmtMoney(m.total_price, m.currency)));
      if (m.cleaning_fee != null) rows.push(row('Cleaning fee', this.fmtMoney(m.cleaning_fee, m.currency)));
      if (m.payout != null) rows.push(row('Expected payout', this.fmtMoney(m.payout, m.currency)));
    }
    const url = typeof dd.hostaway_url === 'string' && /^https:\/\/[a-z0-9.-]*hostaway\.com\//i.test(dd.hostaway_url) ? dd.hostaway_url : '';
    const link = url ? `<a class="btn btn-secondary stc-ext" href="${this.esc(url)}" target="_blank" rel="noopener noreferrer">Open in Hostaway ↗</a>` : '';
    return `<div class="stc-kv">${rows.join('')}${link ? `<div class="stc-kv-actions">${link}</div>` : ''}${m ? '' : '<div class="stc-fine">Money rows show for admins and directors.</div>'}</div>`;
  }

  /** The turn + work orders around this stay (detail payload, this listing only). R115: each row opens its own view (Back returns to the stay). */
  private detailOpsHtml(det: Detail | null): string {
    if (!det || !det.events) return '';
    const turns = (det.events.turns || []).filter(t => t && t.date);
    const wos = (det.events.work_orders || []).filter(w => w && w.date);
    if (!turns.length && !wos.length) return '';
    const inWin = (kind: 'turn' | 'wo', id: string) => !!this.data && (kind === 'turn' ? this.data.events.turns.some(t => t.id === id) : this.data.events.work_orders.some(w => w.id === id));
    const wrap = (kind: 'turn' | 'wo', id: string, lid: string, inner: string) => inWin(kind, id)
      ? `<button type="button" class="stc-evrow stc-evbtn" data-stc-ch-open="${kind}|${this.esc(id)}|${this.esc(lid)}">${inner}</button>`
      : `<div class="stc-evrow">${inner}</div>`;
    const items = [
      ...turns.map(t => wrap('turn', t.id, t.listing_id, `<span class="stc-ops-chip stc-ops-turn stc-ops-${t.unassigned ? 'unassigned' : DONE_STATUSES.includes(String(t.status).toLowerCase()) ? 'done' : 'busy'} stc-lg-chip">${t.unassigned ? '⚠' : '✓'}</span><div><div class="stc-evt">Turnover ${this.esc(this.fmtWMD(t.date))}</div><div class="stc-muted">${this.esc(String(t.status || 'unstarted').replace('_', ' '))} · ${this.esc((t.assignees || []).join(', ') || 'unassigned')}${t.same_day ? ' · same-day' : ''}</div></div>`)),
      ...wos.map(w => wrap('wo', String(w.id), String(w.listing_id), `<span class="stc-ops-chip stc-ops-wo stc-ops-p-${w.kind === 'pm' || w.priority === 'pm' ? 'pm' : (w.priority === 'emergency' || w.priority === 'urgent') ? 'hot' : 'routine'} stc-lg-chip">🔧</span><div><div class="stc-evt">${this.esc(w.title || 'Work order')}</div><div class="stc-muted">${w.due_only ? `due ${this.esc(this.fmtWMD(w.date))} · not booked yet` : `${this.esc(this.fmtWMD(w.date))}${w.end ? ` → ${this.esc(this.fmtMD(w.end))}` : ''}`} · ${this.esc(String(w.status || 'open').replace('_', ' '))} · ${this.esc(w.who || 'nobody assigned')}</div></div>`)),
    ];
    return `<div class="stc-evlist">${items.join('')}</div>`;
  }

  // ── R112: inquiries that can no longer happen / dismissed ───────────────
  private impossibleWhy(imp: Impossible): string {
    const when = imp.date && this.validDate(imp.date) ? this.fmtWMD(imp.date) : 'those nights';
    const other = imp.stay_id && this.data ? this.data.stays.find(x => x.id === imp.stay_id) : null;
    switch (imp.why) {
      case 'past': return 'its check-in date has passed';
      case 'overlap_stay': return `${other ? `${other.guest_name}'s stay` : 'another guest\'s stay'} already covers ${when}`;
      case 'overlap_owner': return `the owner is staying on ${when}`;
      case 'blocked': return `the calendar is blocked on ${when}`;
      case 'held': return `${when} is held by another booking`;
      default: return String(imp.why).replace(/_/g, ' ');
    }
  }
  private inquiryHtml(s: Stay, det: Detail | null): string {
    const src = det ? det.stay : s;
    const imp = src.impossible || s.impossible || null;
    const dis = src.dismissed || s.dismissed || null;
    const busy = this.inqBusy;
    const reason = imp ? `<div class="stc-inqbox stc-inqbox-bad">⛔ This inquiry can no longer happen — ${this.esc(this.impossibleWhy(imp))}. It is hidden from the calendar${dis ? '' : ' (Hostaway still lists it)'}.</div>` : '';
    const dismissed = dis ? `<div class="stc-inqbox stc-inqbox-dis">Dismissed${dis.by ? ` by ${this.esc(dis.by)}` : ''}${dis.at ? ` · ${this.esc(Formatters.formatDate(dis.at))}` : ''} — hidden from the calendar. Hostaway still has it.</div>` : '';
    const plain = !imp && !dis ? `<div class="stc-inqbox">An inquiry is not a booking — the nights stay open. Dismiss it to hide it from the calendar; it stays in Hostaway and can be restored here.</div>` : '';
    const btn = dis
      ? `<button type="button" class="btn btn-secondary" data-stc-inq-restore ${busy ? 'disabled' : ''}>${busy ? 'Restoring…' : '↺ Restore to the calendar'}</button>`
      : `<button type="button" class="btn btn-secondary" data-stc-inq-dismiss ${busy ? 'disabled' : ''}>${busy ? 'Dismissing…' : '✕ Dismiss inquiry'}</button>`;
    return `${reason}${dismissed}${plain}<div class="stc-inq-acts">${btn}</div>`;
  }
  private async dismissInquiry(id: string | null): Promise<void> {
    const s = this.findStay(id);
    if (!id || !s || s.kind !== 'inquiry' || this.inqBusy) return;
    this.inqBusy = true; this.drawPanel();
    const r = await fetch(`${API}/inquiries/${encodeURIComponent(id)}/dismiss`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ by: StaysCalendarPage.actorName() || 'office' }) }).catch(() => null);
    const j = r ? await r.json().catch(() => ({})) : null;
    this.inqBusy = false;
    if (!r || !r.ok) {
      this.toast(r && r.status === 404 ? 'This server can\'t dismiss inquiries yet — redeploy with R112.' : r && r.status === 403 ? 'Your role can\'t dismiss inquiries.' : `Couldn't dismiss — ${(j && j.error) || (r ? `HTTP ${r.status}` : 'no connection')}`);
      this.drawPanel(); return;
    }
    const entry: Dismissed = j && j.entry && typeof j.entry === 'object' ? { by: String(j.entry.by || ''), at: String(j.entry.at || '') } : { by: StaysCalendarPage.actorName() || 'office', at: new Date().toISOString() };
    this.patchInquiry(id, entry);
    const keep: Stay = { ...s };   // R115: once reloaded the inquiry is OFF the wire — the undo keeps its own copy
    const top = this.top();
    if (top && top.ref.kind === 'stay' && top.ref.id === id) this.closeTop();   // the bar is hidden now — the panel would show a ghost
    this.drawTimeline();
    this.toastUndo(`Dismissed ${s.guest_name ? `${s.guest_name}'s` : 'the'} inquiry — hidden from the calendar.`, () => { void this.restoreInquiry(id, keep); });
    void this.load(true, true);
  }
  private async restoreInquiry(id: string | null, hint?: Stay): Promise<void> {
    const s = this.findStay(id) || (hint && hint.id === id ? hint : null);
    if (!id || !s || s.kind !== 'inquiry' || this.inqBusy) return;
    this.inqBusy = true; this.drawPanel();
    const r = await fetch(`${API}/inquiries/${encodeURIComponent(id)}/restore`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' }).catch(() => null);
    const j = r ? await r.json().catch(() => ({})) : null;
    this.inqBusy = false;
    if (!r || !r.ok) {
      this.toast(r && r.status === 404 ? 'This server can\'t restore inquiries yet — redeploy with R112.' : `Couldn't restore — ${(j && j.error) || (r ? `HTTP ${r.status}` : 'no connection')}`);
      this.drawPanel(); return;
    }
    this.patchInquiry(id, null);
    this.drawTimeline(); this.drawPanel();
    this.toastUndo(`Restored ${s.guest_name ? `${s.guest_name}'s` : 'the'} inquiry to the calendar.`, () => { void this.dismissInquiry(id); });
    void this.load(true, true);
  }
  private patchInquiry(id: string, dismissed: Dismissed | null): void {
    const mark = (s: Stay | null) => { if (s) s.dismissed = dismissed; };
    mark(this.data ? this.data.stays.find(x => x.id === id) || null : null);
    if (this.detail && this.detail.stay.id === id) mark(this.detail.stay);
  }

  // ── the block inspector (today's "Blocked nights" panel body) ──
  private secBlock(): string {
    const sel = this.selectedBlock;
    if (!sel) return '';
    const d = this.data;
    const b = this.findBlock(sel);
    const info = this.blockInfo;
    const kind = (info ? info.kind : (b && b.kind)) || 'blocked';
    const held = kind === 'held';
    const note = info ? info.note : (b ? b.note || null : null);
    const start = info ? info.start : sel.start, end = info ? info.end : sel.end;
    const nights = info ? info.nights : this.diffDays(start, end);
    const prop = d ? d.properties.find(p => p.listing_id === sel.listing_id) : null;
    const pav = this.avatarHtml(prop ? prop.thumbnail_url : null, prop ? prop.name : sel.listing_id, false);
    return `<div class="stc-staycard">
          <div class="stc-staycard-top">
            ${pav}
            <div class="stc-staycard-txt"><div class="stc-pname">${this.esc(prop ? prop.name : sel.listing_id)}</div><div class="stc-muted">${nights} night${nights === 1 ? '' : 's'} ${held ? 'held' : 'closed'} on the calendar</div></div>
            <span class="stc-pill stc-pill-block"><span class="stc-pill-dot"></span>${held ? 'Held' : 'Blocked'}</span>
          </div>
          <div class="stc-staycard-dates">
            <div><div class="stc-dl">First night</div><div class="stc-dv">${this.esc(this.fmtWMD(start))}</div></div>
            <span class="stc-nights">⊘ ${nights} night${nights === 1 ? '' : 's'}</span>
            <div style="text-align:right;"><div class="stc-dl">Last night</div><div class="stc-dv">${this.esc(this.fmtWMD(this.addDays(end, -1)))}</div></div>
          </div>
        </div>
        <div class="stc-sec">Note${this.blockLoading ? ' <span class="stc-muted">· loading…</span>' : ''}</div>
        ${note ? `<div class="stc-note">${this.esc(note)}</div>` : `<div class="stc-none">No note — add one on Calendar blocks so the team knows why these nights are closed.</div>`}
        ${held ? `<div class="stc-sent stc-sent-none" style="margin-top:12px;"><span class="stc-sent-ic">◔</span><div><div class="stc-sent-title">Held by a pending / unseen booking</div><div class="stc-sent-text">Hostaway marks these nights reserved but no visible reservation covers them — a pending or awaiting-payment booking, or one hidden by its status. It clears on its own when the booking confirms or expires.</div></div></div>` : ''}
        <div class="stc-fine" style="margin-top:12px;">Calendar blocks is where a block gets its note, or is lifted. ${held ? 'Held nights cannot be lifted there — they belong to the booking.' : 'Unblocking writes to Hostaway and every channel.'}</div>`;
  }

  // ── the WO inspector (core LITE view, R111's popover body — the maintenance builder replaces the kind with the full editor) ──
  private secWo(): string {
    const wo = this.popWo && this.data ? this.data.events.work_orders.find(w => w.id === this.popWo) : null;
    if (!wo) return '<div class="stc-none">This work order is no longer in this window.</div>';
    const prLabel = wo.kind === 'pm' || wo.priority === 'pm' ? 'Preventive maintenance' : wo.priority.charAt(0).toUpperCase() + wo.priority.slice(1);
    return `<div class="stc-pop-wo">
        <div class="stc-kv stc-pop-kv">
          <div class="stc-kv-row"><span class="stc-kv-k">Status</span><span class="stc-kv-v">${this.esc(wo.status.replace('_', ' ') || 'open')}</span></div>
          <div class="stc-kv-row"><span class="stc-kv-k">Who</span><span class="stc-kv-v">${wo.who ? this.esc(wo.who) : '<span class="stc-muted">nobody assigned</span>'}</span></div>
          <div class="stc-kv-row"><span class="stc-kv-k">Priority</span><span class="stc-kv-v">${this.esc(prLabel)}</span></div>
          <div class="stc-kv-row"><span class="stc-kv-k">Due</span><span class="stc-kv-v">${wo.due_date ? this.esc(this.fmtWMD(wo.due_date)) : '<span class="stc-muted">no due date</span>'}</span></div>
        </div>
        <label class="stc-pop-date"><span class="stc-dl">${wo.due_only ? `Book a day (due ${this.esc(this.fmtMD(wo.date))}, not booked yet)` : `Booked day${wo.end ? ` (through ${this.esc(this.fmtMD(wo.end))})` : ''}`}</span><input type="date" class="input" value="${this.esc(wo.date)}" data-stc-pop-date ${this.popBusy ? 'disabled' : ''}></label>
        ${this.popBusy ? `<div class="stc-muted">Moving…</div>` : ''}
        ${this.popErr ? `<div class="stc-pop-err">${this.esc(this.popErr)}</div>` : ''}
        <div class="stc-fine" style="margin-top:10px;">Changing the day moves the booking in Maintenance. You can also drag the chip to another day.</div>
      </div>`;
  }
  /** Confirm, POST the existing Maintenance schedule route, reload. Returns true when the move landed. */
  private async rescheduleWo(id: string, title: string, date: string): Promise<boolean> {
    if (!this.validDate(date)) return false;
    if (!window.confirm(`Move "${title}" to ${this.fmtWMD(date)}?`)) return false;
    if (this.popWo === id) { this.popBusy = true; this.popErr = ''; this.drawPanel(); }
    const r = await fetch(`${WO_API}/${encodeURIComponent(id)}/schedule`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ date, by: StaysCalendarPage.actorName(), source: 'calendar' }),
    }).catch(() => null);
    const j = r ? await r.json().catch(() => ({})) : null;
    if (!r || !r.ok) {
      const msg = r && r.status === 409 ? 'This work order is already done — it can\'t be moved.' : r && r.status === 403 ? 'Your role can\'t reschedule work orders.' : `Couldn't move it — ${(j && j.error) || (r ? `HTTP ${r.status}` : 'no connection')}`;
      if (this.popWo === id) { this.popBusy = false; this.popErr = msg; this.drawPanel(); }
      else this.toast(msg);
      return false;
    }
    if (this.popWo === id) { this.popBusy = false; this.popErr = ''; }
    this.toast(`Moved "${title}" to ${this.fmtWMD(date)}.`);
    await this.load(true, true);
    return true;
  }
  private showDropCol(lanes: HTMLElement, clientX: number): number {
    const d = this.data;
    const rect = lanes.getBoundingClientRect();
    const days = d ? d.days : this.days;
    const c = Math.max(0, Math.min(days - 1, Math.floor((clientX - rect.left) / this.colW)));
    let ind = lanes.querySelector<HTMLElement>('.stc-dropcol');
    if (!ind) {
      document.querySelectorAll('#stc-timeline .stc-dropcol').forEach(x => x.remove());
      ind = document.createElement('div'); ind.className = 'stc-dropcol'; lanes.appendChild(ind);
    }
    const drag = this.dragWo || this.dragTurn || this.dragChip || this.dragExt;
    const date = d ? this.addDays(d.from, c) : '';
    // a wrong row, or (for a turn) a day before today, shows the red "no" column
    ind.classList.toggle('stc-dropcol-no', !!(drag && ((!!drag.listing_id && lanes.dataset.stcListing !== drag.listing_id) || (this.dragTurn && d && date < d.as_of))));
    ind.style.left = `${c * this.colW}px`; ind.style.width = `${this.colW}px`;
    return c;
  }
  /** R115 integ fix (verifier note): released over another home's row the browser never fires drop (dropEffect
   *  'none' — only the red column showed) — say why once the drag ends without a drop. */
  private dragEnded(e: DragEvent): void {
    const wrong = this.dragWrongRow;
    this.dragWrongRow = false;
    const dt = e && e.dataTransfer;
    if (wrong && (!dt || dt.dropEffect === 'none')) this.toast('Drop it on its own home\'s row.');
    this.endDrag();
  }
  private endDrag(): void {
    this.dragWo = null; this.dragTurn = null; this.dragChip = null; this.dragExt = null;
    document.querySelectorAll('#stc-timeline .stc-dropcol').forEach(x => x.remove());
    document.getElementById('stc-root')?.classList.remove('stc-dragging');
  }

  // ── R112: the turn view (now the 'turn' inspector) — assign (cleaner selects), move (clean day), Open in Housekeeping ──
  private async fetchTurn(id: string): Promise<void> {
    let r: Response | null = null, j: any = null;
    try { r = await fetch(`${API}/turn/${encodeURIComponent(id)}`); j = await r.json().catch(() => null); } catch { r = null; }
    if (this.popTurn !== id) return;   // closed or another chip meanwhile
    this.popTurnLoading = false;
    const today = this.data ? this.data.as_of : this.bizToday();
    const srv = j && r && r.ok && j.turn ? normTurn(j.turn, today) : null;
    const chip = this.data ? this.data.events.turns.find(x => x.id === id) : null;
    // a record for ANOTHER turn (a server bug, or a stub) must never overwrite the chip — keep the chip's facts, still take the roster
    const base = srv && srv.id === id ? srv : (srv && chip ? { ...chip } : null);
    if (base) {
      const t = srv && srv.id === id ? j.turn : {};
      const el = t.eci_lco && typeof t.eci_lco === 'object' ? t.eci_lco : {};
      const flag = (v: any): EciLcoFlag | null => (v && typeof v === 'object' ? { on: v.on === true, src: v.src ? String(v.src) : null, time: typeof v.time === 'string' && v.time.trim() ? v.time.slice(0, 20) : null } : null);
      const turn: TurnDetail = {
        ...base, listing_name: String(t.listing_name || ''), note: String(t.note || '').slice(0, 500), sla: t.sla && typeof t.sla === 'object' ? t.sla : {},
        photo_urls: (Array.isArray(t.photo_urls) ? t.photo_urls : []).filter((u: unknown) => typeof u === 'string').slice(0, 6), issues_open: Math.max(0, Number(t.issues_open) || 0),
        reports: t.reports && typeof t.reports === 'object' ? { damage: t.reports.damage ?? null, supply: t.reports.supply ?? null } : null, eci_lco: { eci: flag(el.eci), lco: flag(el.lco) },
      };
      const cleaners: TurnCleaner[] = (Array.isArray(j.cleaners) ? j.cleaners : []).filter((c: any) => c && c.id != null && c.name).map((c: any) => ({ id: String(c.id), name: String(c.name), kind: String(c.kind || ''), company: c.company ? String(c.company) : null, active: c.active !== false, assigned_today: Number(c.assigned_today) || 0, same_day_today: Number(c.same_day_today) || 0 }))
        .sort((a: TurnCleaner, b: TurnCleaner) => a.name.localeCompare(b.name));
      const cap = j.capacity && typeof j.capacity === 'object' ? j.capacity : {};
      // `unassigned` keeps the CHIP's rule (nobody AND today..today+7 — patchTurn's rule, the server's list clamp): a raw
      // "nobody" from an older /turn/:id must never flip a far-out or past chip to the ⚠ tone / into the Unassigned filter
      const horizonUnassigned = (x: TurnEv) => x.assignee_ids.length === 0 && x.date >= today && x.date <= this.addDays(today, TURN_HORIZON_DAYS);
      turn.unassigned = horizonUnassigned(turn);
      this.popTurnData = { turn, cleaners, capacity: { max_cleans_default: Number(cap.max_cleans_default) || 4, max_same_day_default: Number(cap.max_same_day_default) || 2 } };
      const top = this.top();
      if (top && top.ref.kind === 'turn' && top.ref.id === id) top.data = this.popTurnData;
      // the list chip follows the fresher record (status / cleaners may have changed on Housekeeping since the last load)
      const li = this.data ? this.data.events.turns.findIndex(x => x.id === id) : -1;
      if (li >= 0 && this.data) {
        const merged: TurnEv = { ...this.data.events.turns[li], ...base };
        merged.unassigned = horizonUnassigned(merged);
        this.data.events.turns[li] = merged;
      }
    } else {
      this.popErr = r && r.status === 404 ? ((j && j.error) ? 'This turn is gone — refresh the calendar.' : 'This server has no turn details yet (redeploy with R112) — open Housekeeping to assign.') : r && r.status === 403 ? 'Your role can\'t see turn details.' : `Couldn't load the turn — ${(j && j.error) || (r ? `HTTP ${r.status}` : 'no connection')}`;
    }
    this.drawPanel();
  }
  /** The turn view's body (R112's popover body: pills, facts, cleaner selects, the clean-day input and its ask strip). */
  private secTurn(): string {
    const d = this.data;
    const turn = this.popTurn && d ? d.events.turns.find(t => t.id === this.popTurn) : null;
    if (!turn || !d) return '<div class="stc-none">This clean is no longer in this window.</div>';
    const td = this.popTurnData;
    const t: TurnEv | TurnDetail = td ? td.turn : turn;
    const prop = d.properties.find(p => p.listing_id === turn.listing_id);
    const name = (td && td.turn.listing_name) || (prop ? prop.name : turn.listing_id);
    const st = t.status.toLowerCase();
    const tone = t.unassigned ? 'unassigned' : t.done ? 'done' : 'busy';
    const glyphT = tone === 'done' ? '✓' : tone === 'unassigned' ? '⚠' : '◐';
    const statusPill = `<span class="stc-pill stc-pill-v-${tone === 'done' ? 'ok' : tone === 'busy' ? 'warn' : 'none'}">${glyphT} ${this.esc((st || 'unstarted').replace('_', ' '))}</span>`;
    const pills = `${statusPill}${t.same_day ? '<span class="stc-pill stc-pill-v-warn">same-day</span>' : ''}${t.next_check_in ? `<span class="stc-pill stc-pill-muted">next arrival ${this.esc(this.fmtMD(t.next_check_in))}</span>` : '<span class="stc-pill stc-pill-muted">no arrival booked</span>'}${t.at_risk ? '<span class="stc-pill stc-pill-v-bad">⚠ at risk</span>' : ''}`;
    const who = t.assignees.length ? t.assignees.map(n => this.esc(n)).join(', ') : '<span class="stc-muted">unassigned</span>';
    const flags = td ? [td.turn.eci_lco.lco && td.turn.eci_lco.lco.on ? `LCO${td.turn.eci_lco.lco.time ? ` ${this.esc(td.turn.eci_lco.lco.time)}` : ''}` : '', td.turn.eci_lco.eci && td.turn.eci_lco.eci.on ? `ECI${td.turn.eci_lco.eci.time ? ` ${this.esc(td.turn.eci_lco.eci.time)}` : ''}` : ''].filter(Boolean).join(' · ') : '';
    const row = (k: string, v: string) => `<div class="stc-kv-row"><span class="stc-kv-k">${k}</span><span class="stc-kv-v">${v}</span></div>`;
    const rows = [
      row('Cleaners', who),
      row('Photos', t.photos > 0 ? `${t.photos}${td && td.turn.photo_urls.length ? ` · ${td.turn.photo_urls.slice(0, 3).map((u, i) => `<a href="${this.esc(u)}" target="_blank" rel="noopener noreferrer" class="stc-link">${i + 1}</a>`).join(' ')}` : ''}` : '<span class="stc-muted">none yet</span>'),
      t.moved_from ? row('Moved from', this.esc(this.fmtWMD(t.moved_from))) : '',
      t.checkout_guest ? row('Checking out', this.esc(t.checkout_guest)) : '',
      t.gap_days != null && t.gap_days > 0 ? row('Gap after', `${t.gap_days} night${t.gap_days === 1 ? '' : 's'}`) : '',
      td && (td.turn.issues_open > 0 || (td.turn.reports && (td.turn.reports.damage === 'reported' || td.turn.reports.supply === 'reported'))) ? row('Flags', [td.turn.issues_open > 0 ? `${td.turn.issues_open} open issue${td.turn.issues_open === 1 ? '' : 's'}` : '', td.turn.reports && td.turn.reports.damage === 'reported' ? 'damage reported' : '', td.turn.reports && td.turn.reports.supply === 'reported' ? 'supplies reported' : ''].filter(Boolean).map(x => this.esc(x)).join(' · ')) : '',
      row('ECI / LCO', flags || '<span class="stc-muted">none</span>'),
      td && td.turn.note ? row('Note', this.esc(td.turn.note)) : '',
    ].filter(Boolean).join('');
    const ids = t.assignee_ids.length ? t.assignee_ids : [];
    const dateLabel = turn.date === d.as_of ? 'today' : `on ${this.fmtMD(turn.date)}`;
    const selects = td
      ? `<label class="stc-pop-sel"><span class="stc-dl">Cleaner</span><select class="input" data-stc-pop-cleaner="0" ${this.popBusy ? 'disabled' : ''}>${this.cleanerOptsHtml(td.cleaners, ids[0] || '', '— Unassigned —', dateLabel)}</select></label>
         <label class="stc-pop-sel"><span class="stc-dl">Second cleaner</span><select class="input" data-stc-pop-cleaner="1" ${this.popBusy ? 'disabled' : ''}>${this.cleanerOptsHtml(td.cleaners, ids[1] || '', '— none —', dateLabel)}</select></label>`
      : this.popTurnLoading ? `<div class="stc-muted stc-pop-loading">Loading cleaners…</div>` : '';
    // the date input shows the day being asked about (the ask, or the "Move anyway" step); Cancel snaps it back
    const pendingDate = this.popConfirm && this.popConfirm.kind === 'move' ? String(this.popConfirm.body.date || '') : '';
    const dateValue = this.validDate(pendingDate) ? pendingDate : turn.date;
    return `<div class="stc-pop-turn">
        <div class="stc-pop-pills">${pills}</div>
        <div class="stc-kv stc-pop-kv">${rows}</div>
        ${selects}
        <label class="stc-pop-date"><span class="stc-dl">Clean day</span><input type="date" class="input" value="${this.esc(dateValue)}" min="${this.esc(d.as_of)}" data-stc-pop-date ${this.popBusy || t.done ? 'disabled' : ''}></label>
        ${t.done ? `<div class="stc-fine">This clean is done — it can't be rescheduled.</div>` : ''}
        ${this.popBusy ? `<div class="stc-muted">Saving…</div>` : ''}
        <div data-stc-pop-ask>${this.turnAskHtml(name)}</div>
        <div class="stc-fine" style="margin-top:10px;">Cleaners save to Housekeeping right away (capacity limits warn first). ${t.done ? '' : 'A new clean day asks before it moves. You can also drag the chip to another day.'}</div>
      </div>`;
  }
  private async assignTurn(id: string, ids: string[], override: boolean): Promise<void> {
    if (this.popBusy) return;
    this.popBusy = true; this.popErr = ''; this.popExisting = null; if (!override) this.popConfirm = null;
    this.drawPanel();
    const body: Record<string, unknown> = { cleaner_ids: ids, source: 'calendar' };
    if (override) body.override = true;
    // R115: the shared 409 needs_confirm helper; this view answers its "Assign anyway" inline (the view IS the confirm)
    const res = await this.postOverride(`${TURN_API}/${encodeURIComponent(id)}/assign`, body, { ask: this.turnAskInline('assign', { cleaner_ids: ids }) });
    const j: any = res.json;
    if (this.popTurn !== id) { if (res.ok) void this.load(true, true); return; }   // closed meanwhile — still reconcile
    this.popBusy = false; this.popConfirm = null;
    if (res.status === 409 && j && j.needs_confirm) { this.drawPanel(); return; }   // the user said no to "Assign anyway" — the redraw snaps the selects back
    if (!res.ok) {
      this.popErr = res.status === 404 ? 'This turn is gone — refresh the calendar.' : res.status === 403 ? 'Your role can\'t assign cleaners.' : `Couldn't assign — ${(j && j.error) || (res.status ? `HTTP ${res.status}` : 'no connection')}`;
      this.drawPanel(); return;   // the redraw snaps the selects back to what is saved
    }
    this.patchTurn(id, j && j.turn, { assignee_ids: ids, status: ids.length ? 'assigned' : 'unstarted' });
    this.drawTimeline(); this.markSelection(); this.drawPanel();
    const names = (this.data ? this.data.events.turns.find(x => x.id === id) : null)?.assignees || [];
    this.toast(names.length ? `Assigned ${names.join(' + ')}.` : 'Unassigned the clean.');
    await this.load(true, true);
  }
  /** Move the clean day. The view asks first (its "Move" button is what calls this); a drag asks with {confirm:true} (like a work order). */
  private async moveTurn(id: string, date: string, opts: { confirm?: boolean; override?: boolean }): Promise<boolean> {
    const d = this.data;
    const turn = d ? d.events.turns.find(t => t.id === id) : null;
    if (!d || !turn || !this.validDate(date)) return false;
    const prop = d.properties.find(p => p.listing_id === turn.listing_id);
    const pname = prop ? prop.name : turn.listing_id;
    if (date < d.as_of) { if (this.popTurn === id) { this.popErr = 'Cleans can\'t move into the past.'; this.drawPanel(); } else this.toast('Cleans can\'t move into the past.'); return false; }
    if (turn.done) { if (this.popTurn === id) { this.popErr = 'This clean is already done — it can\'t be rescheduled.'; this.drawPanel(); } else this.toast('This clean is already done — it can\'t be rescheduled.'); return false; }
    if (opts.confirm && !window.confirm(`Move the ${pname} clean from ${this.fmtWMD(turn.date)} to ${this.fmtWMD(date)}? (An inspection scheduled that day moves with it.)`)) return false;
    const inPop = this.popTurn === id;
    if (inPop) { this.popBusy = true; this.popErr = ''; this.popExisting = null; if (!opts.override) this.popConfirm = null; this.drawPanel(); }
    // R114: the clean's inspection follows it (best-effort server-side).
    const body: Record<string, unknown> = { date, by: StaysCalendarPage.actorName() || 'office', with_inspection: true, source: 'calendar' };
    if (opts.override) body.override = true;
    const fromDate = turn.date;   // before patchTurn moves it — "its inspection stayed on …"
    // R115: the shared 409 needs_confirm helper — inline "Move anyway" in the view, a window.confirm after a drag (no view open)
    const res = await this.postOverride(`${TURN_API}/${encodeURIComponent(id)}/move`, body, inPop ? { ask: this.turnAskInline('move', { date }) } : {});
    const j: any = res.json;
    const stillPop = this.popTurn === id;
    if (stillPop) { this.popBusy = false; this.popConfirm = null; }
    if (res.status === 409 && j && j.needs_confirm) { if (stillPop) this.drawPanel(); return false; }   // "Move anyway" declined
    if (res.status === 409 && j && j.existing) {   // an active clean already sits on that day — keep ITS id for the Housekeeping hop
      const ex = { id: j.existing.id != null ? String(j.existing.id) : '', status: String(j.existing.status || 'open'), date: this.validDate(j.existing.check_out) ? j.existing.check_out : date };
      if (stillPop) { this.popExisting = ex; this.popErr = ''; this.drawPanel(); }
      else this.toast(`${pname} already has a ${ex.status.replace('_', ' ')} clean on ${this.fmtMD(ex.date)} — finish or move that one first.`);
      return false;
    }
    if (!res.ok) {
      const msg = res.status === 404 ? 'This turn is gone — refresh the calendar.' : res.status === 403 ? 'Your role can\'t move cleans.' : `Couldn't move it — ${(j && j.error) || (res.status ? `HTTP ${res.status}` : 'no connection')}`;
      if (stillPop) { this.popErr = msg; this.drawPanel(); } else this.toast(msg);
      return false;
    }
    this.patchTurn(id, j && j.turn, { date });
    this.drawTimeline(); this.markSelection();
    if (stillPop) this.drawPanel();
    this.toast(`Moved the ${pname} clean to ${this.fmtWMD(date)}${j && j.inspection && j.inspection.moved ? ` — its inspection moved too${j.inspection.inspector ? ` (${j.inspection.inspector})` : ''}` : ''}.`);
    // the confirm promised the inspection would follow — when it couldn't
    // (collision / blocked / office only / error) the split must be said
    const warn = followWarning(j && j.inspection, { name: pname, to: this.fmtWMD(date), fromLabel: this.fmtWMD(fromDate) });
    if (warn) window.alert(warn);
    await this.load(true, true);
    return true;
  }
  /** The listing inspector's primary: Property Details for this home (the OnboardingPage calendar hand-off, ob_open src:'calendar'). */
  private openPropertyDetails(listingId: string): void {
    if (!listingId) return;
    try { sessionStorage.setItem('ob_open', JSON.stringify({ id: String(listingId), tab: 'details', phases: [], src: 'calendar' })); } catch { /* private mode: the board opens */ }
    this.go('onboarding');
  }
  private openHousekeeping(turnId: string | null): void {
    try { if (turnId) sessionStorage.setItem('hk_focus_turn', turnId); } catch { /* private mode */ }
    this.go('hk-insp', 'housekeeping');
  }

  // ── R112: the Arrival checklist in the panel (writes go to restracker's own routes) ──
  private relDue(it: CheckItem, nowMs: number): string {
    if (!it.due_at) return '';
    const ms = Date.parse(it.due_at);
    if (!Number.isFinite(ms)) return '';
    const h = Math.round((ms - nowMs) / 3600000);
    if (it.state !== 'pending') return `due ${this.esc(Formatters.formatDate(it.due_at))}`;
    if (h < 0) return `<b>overdue ${-h < 48 ? `${-h}h` : `${Math.round(-h / 24)}d`}</b>`;
    return h < 1 ? 'due now' : h < 48 ? `due in ${h}h` : `due in ${Math.round(h / 24)}d`;
  }
  // R114: machine ticks name their SOURCE — the Hostaway pre-arrival message
  // (✉, with the message time) is distinct from the payment/agreement facts.
  private checkWho(it: CheckItem): string {
    const verb = it.state === 'done' ? 'done' : it.state;
    const by = String(it.done_by || '');
    if (by === 'auto:hostaway_msg') {
      const at = (it.auto && it.auto.at) || it.done_at;
      return `<span title="Hostaway shows the pre-arrival message was sent${at ? ` at ${this.esc(new Date(at).toLocaleString())}` : ''} — it proves the message went out, not that the lock code or wifi were tested">✉ Hostaway message (auto)${at ? ` ${this.esc(Formatters.formatDate(at))}` : ''}</span>`;
    }
    const who = by.startsWith('auto:hostaway') ? 'Hostaway (auto)' : (by.startsWith('auto:') ? 'auto' : by);
    return `${verb} by ${this.esc(who)}${it.done_at ? ` ${this.esc(Formatters.formatDate(it.done_at))}` : ''}`;
  }
  private checklistHtml(ac: ArrivalCheck, s: Stay, today: string): string {
    const open = `<button type="button" class="stc-link" data-stc-arrivals>Open Arrivals</button>`;
    if (!ac.generated) {
      const days = Math.max(1, Math.min(60, this.diffDays(today, s.check_in) + 1));
      return `<div class="stc-none">No pre-arrival checks for this stay yet.${ac.eligible ? ' Generate them to track ID, payment and the pre-arrival message right here.' : ' Checks are generated for confirmed stays arriving within 60 days.'}</div>
        <div class="stc-cl-foot">${ac.eligible ? `<button type="button" class="btn btn-primary" data-stc-gen ${this.genBusy ? 'disabled' : ''}>${this.genBusy ? 'Generating…' : `✦ Generate checks (${days} day${days === 1 ? '' : 's'})`}</button>` : ''}${open}</div>
        ${this.genBusy ? `<div class="stc-fine">Pulling the latest from Hostaway first — this can take a few seconds.</div>` : ''}`;
    }
    const nowMs = Date.now();
    const rows = ac.items.map(it => {
      const pill = ITEM_PILL[it.state] || ITEM_PILL.pending;
      const busy = this.checkBusy.has(it.key);
      const sub = [this.relDue(it, nowMs), it.done_by ? this.checkWho(it) : '', it.note ? `“${this.esc(it.note)}”` : '', it.photos > 0 ? `📷 ${it.photos}` : ''].filter(Boolean).join(' · ');
      const uploading = this.photoBusy === it.key;
      const acts = it.state === 'pending'
        ? `<button type="button" class="stc-ci-btn stc-ci-btn-ok" data-stc-item="${this.esc(it.key)}" data-stc-state="done" title="Mark done" ${busy ? 'disabled' : ''}>✓</button><button type="button" class="stc-ci-btn" data-stc-item="${this.esc(it.key)}" data-stc-state="waived" title="Waive (asks for a reason)" ${busy ? 'disabled' : ''}>⤼</button><button type="button" class="stc-ci-btn stc-ci-btn-bad" data-stc-item="${this.esc(it.key)}" data-stc-state="failed" title="Mark failed" ${busy ? 'disabled' : ''}>✕</button>`
        : `<button type="button" class="stc-ci-btn" data-stc-item="${this.esc(it.key)}" data-stc-state="pending" title="Back to pending" ${busy ? 'disabled' : ''}>↺</button>`;
      const photo = `<label class="stc-ci-btn stc-ci-photo" title="${uploading ? 'Uploading…' : 'Add a photo'}">${uploading ? '…' : '📷'}<input type="file" accept="image/*" data-stc-item-photo="${this.esc(it.key)}" ${uploading || busy ? 'disabled' : ''} hidden></label>`;
      return `<div class="stc-ci stc-ci-${this.esc(it.state)}${it.overdue && it.state === 'pending' ? ' stc-ci-over' : ''}" data-stc-ci="${this.esc(it.key)}">
        <span class="stc-ci-req${it.required ? '' : ' stc-ci-opt'}" title="${it.required ? 'Required' : 'Optional'}"></span>
        <div class="stc-ci-body"><div class="stc-ci-top"><span class="stc-ci-label">${this.esc(it.label)}</span><span class="stc-pill stc-pill-v-${pill.cls}">${busy ? '…' : this.esc(pill.label)}</span></div>${sub ? `<div class="stc-ci-sub">${sub}</div>` : ''}</div>
        <div class="stc-ci-acts">${acts}${photo}</div>
      </div>`;
    }).join('');
    const meta = VER_PILL[ac.rollup] || VER_PILL.pending;
    return `<div class="stc-cl">${rows || '<div class="stc-none">The template has no items.</div>'}</div>
      ${ac.note ? `<div class="stc-note">${this.esc(ac.note)}</div>` : ''}
      <div class="stc-cl-foot"><span class="stc-pill stc-pill-v-${meta.cls}">${meta.ic} ${this.esc(meta.label)}</span>${ac.hours_to_checkin != null ? `<span class="stc-muted">check-in in ${Math.round(ac.hours_to_checkin)}h</span>` : ''}${open}</div>
      <div class="stc-fine">Ticks save to Arrivals right away. Waiving asks for a reason; ↺ puts an item back to pending.</div>`;
  }
  private panelCheck(): { id: string; ac: ArrivalCheck } | null {
    const det = this.detail && this.detail.stay.id === this.selectedId ? this.detail : null;
    const ac = det && det.arrival_check;
    return ac && ac.generated && ac.id && this.selectedId ? { id: this.selectedId, ac } : null;
  }
  private async setCheckItem(key: string, state: ItemState): Promise<void> {
    const pc = this.panelCheck();
    if (!pc || !key || !(ITEM_STATES as readonly string[]).includes(state) || this.checkBusy.has(key)) return;
    const it = pc.ac.items.find(x => x.key === key);
    if (!it) return;
    let note = '';
    if (state === 'waived') {   // restracker refuses a waive without a reason (400)
      const reason = window.prompt('Reason for waiving this item (required):');
      if (reason == null) return;
      note = reason.trim();
      if (!note) { this.toast('A reason is required to waive an item.'); return; }
    } else if (state === 'failed') {
      const n = window.prompt('What failed? (optional note)');
      if (n == null) return;
      note = n.trim();
    }
    const prev = { ...it };
    it.state = state;   // optimistic: the pill flips now, the server reconciles after
    if (state === 'done') { it.done_by = StaysCalendarPage.actorName() || 'office'; it.done_at = new Date().toISOString(); }
    if (state === 'pending') { it.done_by = null; it.done_at = null; }
    it.auto = null;   // R114: a manual change drops the machine evidence (the server does the same)
    if (note) it.note = note;
    this.checkBusy.add(key); this.drawPanel();
    const r = await fetch(`${RT_API}/checks/${encodeURIComponent(pc.ac.id)}/item`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ key, state, note }) }).catch(() => null);
    const j = r ? await r.json().catch(() => ({})) : null;
    this.checkBusy.delete(key);
    if (!r || !r.ok) {
      Object.assign(it, prev);
      this.toast(r && r.status === 404 ? 'That check is gone — regenerate it on Arrivals.' : `Couldn't update the item — ${(j && j.error) || (r ? `HTTP ${r.status}` : 'no connection')}`);
      this.drawPanel(); return;
    }
    if (j && j.check) {   // the route returns the enriched check — patch before the refetch so the rollup pill follows at once
      const fresh = normArrivalCheck({ ...j.check, generated: true });
      if (fresh && this.detail && this.detail.stay.id === pc.id) this.detail.arrival_check = fresh;
    }
    this.drawPanel();
    await this.refreshAfterWrite(pc.id);
  }
  private async generateChecks(): Promise<void> {
    const id = this.selectedId;
    const s = this.findStay(id);
    const d = this.data;
    if (!id || !s || !d || this.genBusy) return;
    const days = Math.max(1, Math.min(60, this.diffDays(d.as_of, s.check_in) + 1));
    this.genBusy = true; this.drawPanel();
    const r = await fetch(`${RT_API}/generate?days=${days}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' }).catch(() => null);
    const j = r ? await r.json().catch(() => ({})) : null;
    this.genBusy = false;
    if (!r || !r.ok) { this.toast(`Couldn't generate the checks — ${(j && j.error) || (r ? `HTTP ${r.status}` : 'no connection')}`); this.drawPanel(); return; }
    this.toast(`Generated arrival checks for the next ${days} day${days === 1 ? '' : 's'}.`);
    await this.refreshAfterWrite(id);
  }
  private async uploadItemPhoto(key: string, file: File): Promise<void> {
    const pc = this.panelCheck();
    if (!pc || !key || this.photoBusy) return;
    this.photoBusy = key; this.drawPanel();
    const fd = new FormData(); fd.append('key', key); fd.append('photo', file, file.name);
    const r = await fetch(`${RT_API}/checks/${encodeURIComponent(pc.ac.id)}/item-photo`, { method: 'POST', body: fd }).catch(() => null);
    const j = r ? await r.json().catch(() => ({})) : null;
    this.photoBusy = null;
    if (!r || !r.ok) { this.toast(`Couldn't upload the photo — ${(j && j.error) || (r ? `HTTP ${r.status}` : 'no connection')}`); this.drawPanel(); return; }
    const it = pc.ac.items.find(x => x.key === key);
    if (it) it.photos += 1;
    this.drawPanel();
    await this.refreshAfterWrite(pc.id);
  }

  // ── R112: ECI / LCO switch rows (the "approved by hand" path; Apply in Opportunities stays the "offered" path) ──
  private eciLcoHtml(e: EciLco): string {
    const row = (kind: 'eci' | 'lco', side: EciLcoSide | null) => {
      if (!side) return '';
      const busy = this.eciBusy.has(kind);
      const on = side.on, off = !on && side.declined;
      const srcBadge = on && side.src ? `<span class="stc-el-src${side.src === 'auto' ? ' stc-el-src-auto' : ''}">${this.esc(ECI_LCO_SRC[side.src] || side.src)}</span>`
        : off ? `<span class="stc-el-src stc-el-src-off">turned off by hand</span>` : '';
      const tight = side.tight ? `<div class="stc-el-tight">⚠ ${kind === 'eci' ? 'Someone checks out that day — a same-day turn.' : 'Someone arrives that day — a same-day turn.'}</div>` : '';
      const who = on && side.by ? `<div class="stc-muted">${this.esc(side.by)}${side.at ? ` · ${this.esc(Formatters.formatDate(side.at))}` : ''}</div>` : '';
      const clear = off ? `<button type="button" class="stc-link stc-el-clear" data-stc-el-clear="${kind}" ${busy ? 'disabled' : ''}>clear</button>` : '';
      return `<div class="stc-el-row${off ? ' stc-el-row-off' : ''}">
        <button type="button" class="stc-sw${on ? ' on' : ''}${off ? ' declined' : ''}" role="switch" aria-checked="${on ? 'true' : 'false'}" aria-label="${kind === 'eci' ? 'Early check-in' : 'Late checkout'}" data-stc-el-toggle="${kind}" ${busy ? 'disabled' : ''}><span class="stc-sw-knob"></span></button>
        <div class="stc-el-body"><div class="stc-el-title">${kind === 'eci' ? '🌅 Early check-in' : '🌇 Late checkout'} ${srcBadge}${clear}${busy ? '<span class="stc-muted">saving…</span>' : ''}</div>${tight}${who}</div>
        <input type="text" class="input stc-el-time" placeholder="time" maxlength="20" value="${this.esc(side.time || '')}" data-stc-el-time="${kind}" title="${kind === 'eci' ? 'Early check-in time, e.g. 1 PM' : 'Late checkout time, e.g. 1 PM'}" ${on && !busy ? '' : 'disabled'}>
      </div>`;
    };
    return `<div class="stc-el">${row('eci', e.eci)}${row('lco', e.lco)}${this.eciNote ? `<div class="stc-pop-err">${this.esc(this.eciNote)}</div>` : ''}</div>`;
  }
  private panelEciLco(kind: 'eci' | 'lco'): { s: Stay; side: EciLcoSide } | null {
    const det = this.detail && this.detail.stay.id === this.selectedId ? this.detail : null;
    const side = det && det.eci_lco ? det.eci_lco[kind] : null;
    return det && side ? { s: det.stay, side } : null;
  }
  private async toggleEciLco(kind: 'eci' | 'lco'): Promise<void> {
    const cur = this.panelEciLco(kind);
    if (!cur) return;
    await this.writeEciLco(kind, !cur.side.on);   // on → off by hand (declined); off / declined → on
  }
  private async saveEciLcoTime(kind: 'eci' | 'lco', raw: string): Promise<void> {
    const cur = this.panelEciLco(kind);
    if (!cur || !cur.side.on) return;
    const val = raw.trim().slice(0, 20);
    if (val === (cur.side.time || '')) return;
    await this.writeEciLco(kind, true, val);
  }
  /** value true = on (src manual), false = turned off by hand (declined), null = clear the flag. */
  private async writeEciLco(kind: 'eci' | 'lco', value: boolean | null, time?: string): Promise<void> {
    const cur = this.panelEciLco(kind);
    if (!cur || this.eciBusy.has(kind)) return;
    const { s, side } = cur;
    const date = kind === 'eci' ? s.check_in : s.check_out;
    const body: Record<string, unknown> = { listing_id: s.listing_id, date, kind, value, by: StaysCalendarPage.actorName() || 'office' };
    if (time != null && value != null) body[`${kind}_time`] = time;
    this.eciBusy.add(kind); this.eciNote = ''; this.drawPanel();
    const r = await fetch(ECI_LCO_API, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }).catch(() => null);
    const j = r ? await r.json().catch(() => ({})) : null;
    this.eciBusy.delete(kind);
    if (!r || !r.ok) {
      this.eciNote = `Couldn't save ${kind === 'eci' ? 'early check-in' : 'late checkout'} — ${(j && j.error) || (r ? (r.status === 403 ? 'your role can\'t change it' : `HTTP ${r.status}`) : 'no connection')}`;
      this.drawPanel(); return;
    }
    // optimistic: the switch reflects the write now; the refetch brings src/by/at and the Opportunities card (applied/declined) in line
    const entry = j && j.entry && typeof j.entry === 'object' ? j.entry : null;
    side.on = value === true; side.declined = value === false; side.src = value == null ? null : 'manual';
    side.time = value == null ? null : (entry && typeof entry[`${kind}_time`] === 'string' && entry[`${kind}_time`].trim() ? entry[`${kind}_time`] : (time != null ? (time || null) : side.time));
    side.by = value == null ? null : (StaysCalendarPage.actorName() || 'office');
    this.drawPanel();
    await this.refreshAfterWrite(s.id);
  }

  private async apply(kind: Opp['kind']): Promise<void> {
    const id = this.selectedId;
    if (!id || this.applying.has(kind)) return;
    this.applying.add(kind); this.applyNote = ''; this.drawPanel();
    let by = '';
    try { by = String(JSON.parse(sessionStorage.getItem('userProfile') || '{}').email || '').trim().toLowerCase(); } catch { /* private mode */ }
    const r = await fetch(`${API}/${encodeURIComponent(id)}/apply`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ kind, by }),
    }).catch(() => null);
    const j = r ? await r.json().catch(() => ({})) : null;
    this.applying.delete(kind);
    if (!r || !r.ok) {
      this.applyNote = `Couldn't apply — ${(j && j.error) || 'try again'}`;
      if (r && r.status === 409) await this.load(true, true);   // the calendar moved: redraw with the truth, panel included
      this.drawPanel();
      return;
    }
    const mark = (s: Stay | null) => { const o = s && s.opportunities.find(x => x.kind === kind); if (o) { o.applied = true; o.applied_src = 'calendar'; } };
    mark(this.findStay(id));
    if (this.detail && this.detail.stay.id === id) mark(this.detail.stay);
    this.applyNote = j && j.already ? 'Already marked as offered.' : 'Marked as offered — no message was sent.';
    this.drawPanel();
    await this.load(true, true);   // KPI + $ badge + the panel follow the server
    document.querySelector<HTMLElement>(`#stc-timeline [data-stc-stay="${CSS.escape(id)}"]`)?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }

  private openConversation(): void {
    const det = this.detail && this.detail.stay.id === this.selectedId ? this.detail : null;
    const phone = det && det.guest.phone ? det.guest.phone : '';
    try { if (phone) sessionStorage.setItem('qx_focus_phone', phone); else sessionStorage.removeItem('qx_focus_phone'); } catch { /* private mode */ }
    document.dispatchEvent(new CustomEvent('app:navigate', { detail: 'guest-texts' }));
  }
  // R111: Issues is its own page again (the Tasks & Issues hub dissolved) — no hub_tab hand-off
  private openIssue(): void {
    const s = this.findStay(this.selectedId);
    const flag = (this.detail && this.detail.stay.id === this.selectedId && this.detail.flagged) || (s && s.flagged);
    if (!flag) return;
    try { sessionStorage.setItem('iss_focus', `msg:${flag.ticket_id}`); } catch { /* private mode */ }
    document.dispatchEvent(new CustomEvent('app:navigate', { detail: 'issues' }));
  }
  private openReview(): void {
    const s = this.findStay(this.selectedId);
    const det = this.detail && this.detail.stay.id === this.selectedId ? this.detail : null;
    const rv = (det && det.review) || (s && s.review) || null;
    if (!rv || !rv.id) return;
    const prop = this.data && s ? this.data.properties.find(p => p.listing_id === s.listing_id) : null;
    try { sessionStorage.setItem('rv_focus_review', JSON.stringify({ ids: [rv.id], listing_name: prop ? prop.name : '' })); } catch { /* private mode */ }
    document.dispatchEvent(new CustomEvent('app:navigate', { detail: 'reviews' }));
  }
  private openMaintenance(): void {
    const id = this.popWo;
    if (!id) return;
    try { sessionStorage.setItem('mn_focus_wo', id); } catch { /* private mode */ }
    document.dispatchEvent(new CustomEvent('app:navigate', { detail: 'maintenance' }));
  }

  private cleanerOptsHtml(cleaners: TurnCleaner[], selId: string, blank: string, dateLabel: string): string {
    const opts = [`<option value=""${!selId ? ' selected' : ''}>${blank}</option>`]
      .concat(cleaners.map(c => `<option value="${this.esc(c.id)}"${selId === c.id ? ' selected' : ''}>${this.esc(c.name)}${c.assigned_today > 0 ? ` (${c.assigned_today} ${dateLabel}${c.same_day_today > 0 ? `, ${c.same_day_today} same-day` : ''})` : ''}${c.kind === 'vendor' && c.company ? ` · ${this.esc(c.company)}` : ''}</option>`));
    if (selId && !cleaners.some(c => c.id === selId)) opts.push(`<option value="${this.esc(selId)}" selected>${this.esc(selId)} (archived)</option>`);
    return opts.join('');
  }
  // The turn popover's ask strip (the move / "anyway" confirm, the 409 "already
  // has a clean" notice, an error) — drawn inside [data-stc-pop-ask] and patched
  // alone while the date input is being typed in, so the input keeps its focus.
  private turnAskHtml(name: string): string {
    const confirmLabel = !this.popConfirm ? '' : this.popConfirm.kind === 'assign' ? 'Assign anyway' : this.popConfirm.override ? 'Move anyway' : 'Move';
    const confirm = this.popConfirm ? `<div class="stc-pop-warn">${this.esc(this.popConfirm.text)}<div class="stc-pop-warn-acts"><button type="button" class="btn btn-primary" data-stc-pop-confirm ${this.popBusy ? 'disabled' : ''}>${confirmLabel}</button><button type="button" class="btn btn-secondary" data-stc-pop-cancel ${this.popBusy ? 'disabled' : ''}>Cancel</button></div></div>` : '';
    // its own button: the hop lands on THAT clean (the one to finish or move), not the one being moved
    const existing = this.popExisting ? `<div class="stc-pop-err">${this.esc(name)} already has ${/^[aeiou]/i.test(this.popExisting.status) ? 'an' : 'a'} ${this.esc(this.popExisting.status.replace('_', ' '))} clean on ${this.esc(this.fmtWMD(this.popExisting.date))}. Finish or move that one first.<div class="stc-pop-warn-acts"><button type="button" class="btn btn-secondary" data-stc-pop-hk-existing>Open that clean in Housekeeping</button><button type="button" class="btn btn-secondary" data-stc-pop-cancel>Dismiss</button></div></div>` : '';
    return `${confirm}${existing}${this.popErr && !this.popExisting ? `<div class="stc-pop-err">${this.esc(this.popErr)}</div>` : ''}`;
  }
  private patchTurnAsk(): void {
    const slot = document.querySelector<HTMLElement>('#stc-panel .stc-pop-turn [data-stc-pop-ask]');
    if (!slot || !this.popTurn || !this.data) { this.drawPanel(); return; }
    const turn = this.data.events.turns.find(t => t.id === this.popTurn);
    if (!turn) { this.drawPanel(); return; }
    const prop = this.data.properties.find(p => p.listing_id === turn.listing_id);
    slot.innerHTML = this.turnAskHtml((this.popTurnData && this.popTurnData.turn.listing_name) || (prop ? prop.name : turn.listing_id));
  }
  /** Patch the chip (list + popover) from the assign/move response (full ao_turns record) or from what we sent. */
  private patchTurn(id: string, rec: any, fallback: Partial<TurnEv>): void {
    const d = this.data;
    const today = d ? d.as_of : this.bizToday();
    const roster = this.popTurnData ? this.popTurnData.cleaners : [];
    const nameOf = (cid: string) => { const c = roster.find(x => x.id === cid); return c ? c.name : cid; };
    const apply = (t: TurnEv) => {
      const ids: string[] = rec && typeof rec === 'object' ? ((Array.isArray(rec.assignees) && rec.assignees.length) ? rec.assignees.map(String) : rec.assigned_to ? [String(rec.assigned_to)] : (rec.assignees ? [] : (fallback.assignee_ids || t.assignee_ids))) : (fallback.assignee_ids || t.assignee_ids);
      const status: string = rec && rec.status ? String(rec.status) : (fallback.status || t.status);
      const date: string = rec && this.validDate(rec.check_out) ? rec.check_out : (fallback.date || t.date);
      const names: string[] = ids.map(nameOf);
      const done = DONE_STATUSES.includes(status.toLowerCase());
      Object.assign(t, {
        status, date, assignee_ids: ids, assignees: names, initials: names.map(n => this.initials(n)), done,
        unassigned: ids.length === 0 && date >= today && date <= this.addDays(today, TURN_HORIZON_DAYS),
        next_check_in: rec && rec.next_check_in !== undefined ? (this.validDate(rec.next_check_in) ? rec.next_check_in : null) : t.next_check_in,
        same_day: rec && typeof rec.same_day === 'boolean' ? rec.same_day : t.same_day,
        gap_days: rec && rec.gap_days !== undefined ? (Number.isFinite(Number(rec.gap_days)) && rec.gap_days != null ? Number(rec.gap_days) : null) : t.gap_days,
        moved_from: rec && this.validDate(rec.moved_from) ? rec.moved_from : t.moved_from,
        photos: rec && rec.capture && Array.isArray(rec.capture.photos) ? rec.capture.photos.length : t.photos,
      });
      t.at_risk = t.same_day && t.date <= today && !t.done;
    };
    const li = d ? d.events.turns.find(x => x.id === id) : null;
    if (li) apply(li);
    if (this.popTurnData && this.popTurnData.turn.id === id) apply(this.popTurnData.turn);
    if (this.detail && this.detail.events) { const dt = (this.detail.events.turns || []).find(x => x.id === id); if (dt) apply(dt); }
  }

  // ── "+ Add": hold nights / manual reservation (writes to Hostaway behind a summary) ──
  private openAdd(prefill?: { listing_id?: string; check_in?: string }): void {
    this.closeModPop();
    this.addForm = StaysCalendarPage.blankAdd();
    this.addPropQ = ''; this.addErr = ''; this.addConflicts = []; this.addBusy = false; this.addStep = 'form';
    if (prefill) {
      if (prefill.listing_id && this.data && this.data.properties.some(p => p.listing_id === prefill.listing_id)) this.addForm.listing_id = prefill.listing_id;
      if (prefill.check_in && this.validDate(prefill.check_in)) { this.addForm.check_in = prefill.check_in; this.addForm.check_out = this.addDays(prefill.check_in, 1); }
    }
    this.addOpen = true;
    document.body.classList.add('stc-modal-open');
    this.drawAdd();
    const focusEl = document.querySelector<HTMLElement>(this.addForm.listing_id ? '#stc-modal [data-stc-add-f="check_in"]' : '#stc-modal [data-stc-add-propq]');
    focusEl?.focus();
  }
  private closeAdd(): void {
    this.addOpen = false; this.addBusy = false;
    document.body.classList.remove('stc-modal-open');
    const host = document.getElementById('stc-modal');
    if (host) host.innerHTML = '';
  }
  /** Pull the typed values into addForm before any redraw (the form is innerHTML — a redraw would wipe them). */
  private syncAddForm(): void {
    const host = document.getElementById('stc-modal');
    if (!host) return;
    host.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>('[data-stc-add-f]').forEach(inp => {
      const k = inp.dataset.stcAddF as keyof AddForm;
      if (k && k in this.addForm && k !== 'kind') (this.addForm as any)[k] = inp.value;
    });
    const pq = host.querySelector<HTMLInputElement>('[data-stc-add-propq]');
    if (pq && !this.addForm.listing_id) this.addPropQ = pq.value;
  }
  private addNights(): number | null {
    const f = this.addForm;
    if (!this.validDate(f.check_in) || !this.validDate(f.check_out)) return null;
    return this.diffDays(f.check_in, f.check_out);
  }
  private addNightsLabel(): string {
    const n = this.addNights();
    if (n == null) return 'Pick both dates';
    if (n <= 0) return 'Checkout must be after check-in';
    return `${n} night${n === 1 ? '' : 's'}${n > MAX_NIGHTS ? ` — max ${MAX_NIGHTS}` : ''}`;
  }
  private addPropListHtml(): string {
    const d = this.data;
    if (!d) return '';
    const q = this.addPropQ.trim().toLowerCase();
    if (!q) return `<div class="stc-combo-hint">Type to search the ${d.properties.length} properties in view</div>`;
    const hits = d.properties.filter(p => p.name.toLowerCase().includes(q) || String(p.full_name || '').toLowerCase().includes(q) || p.listing_id.includes(q)).slice(0, 8);
    if (!hits.length) return `<div class="stc-combo-hint">No property matches "${this.esc(this.addPropQ.trim())}"</div>`;
    return hits.map(p => `<button type="button" class="stc-combo-opt" data-stc-add-prop="${this.esc(p.listing_id)}">${this.avatarHtml(p.thumbnail_url, p.name, false)}<span class="stc-combo-name">${this.esc(p.name)}</span>${p.is_mtr ? '<span class="stc-tag">MTR</span>' : ''}</button>`).join('');
  }
  private drawAdd(): void {
    const host = document.getElementById('stc-modal');
    if (!host) return;
    if (!this.addOpen) { host.innerHTML = ''; return; }
    const f = this.addForm;
    const d = this.data;
    const prop = d ? d.properties.find(p => p.listing_id === f.listing_id) : null;
    const res = f.kind === 'reservation';
    const minDay = d ? d.as_of : '';
    const conflicts = this.addConflicts.length ? `<div class="stc-add-conf"><div class="stc-sent-title">Those nights aren't free</div><ul>${this.addConflicts.slice(0, 12).map(c => `<li><b>${this.esc(this.validDate(c.date) ? this.fmtWMD(c.date) : c.date)}</b> — ${this.esc(c.why || 'taken')}</li>`).join('')}</ul>${this.addConflicts.length > 12 ? `<div class="stc-muted">+ ${this.addConflicts.length - 12} more</div>` : ''}</div>` : '';
    const err = this.addErr ? `<div class="stc-pop-err">${this.esc(this.addErr)}</div>` : '';
    let body = '', foot = '';
    if (this.addStep === 'form') {
      body = `
        <div class="stc-field">
          <label class="stc-dl" for="stc-add-propq">Property</label>
          ${prop ? `<div class="stc-combo-sel">${this.avatarHtml(prop.thumbnail_url, prop.name, false)}<span class="stc-combo-name">${this.esc(prop.name)}</span><button type="button" class="stc-close" data-stc-add-clearprop aria-label="Change property">✕</button></div>`
            : `<div class="stc-combo"><input id="stc-add-propq" class="input" type="text" placeholder="Search property" autocomplete="off" data-stc-add-propq value="${this.esc(this.addPropQ)}"><div class="stc-combo-list" data-stc-add-proplist>${this.addPropListHtml()}</div></div>`}
        </div>
        <div class="stc-field">
          <span class="stc-dl">What</span>
          <div class="seg-tabs stc-add-kind"><button type="button" class="seg-tab${res ? '' : ' active'}" data-stc-add-kind="block">⊘ Hold nights</button><button type="button" class="seg-tab${res ? ' active' : ''}" data-stc-add-kind="reservation">🛏 Manual reservation</button></div>
          <div class="stc-fine">${res ? 'Creates a direct reservation in Hostaway (channel: direct) and shows it here right away.' : 'Closes the nights in Hostaway (like an owner hold) — reversible on Calendar blocks.'}</div>
        </div>
        <div class="stc-field stc-field-2">
          <label><span class="stc-dl">Check-in</span><input type="date" class="input" data-stc-add-f="check_in" value="${this.esc(f.check_in)}" min="${this.esc(minDay)}"></label>
          <label><span class="stc-dl">Checkout</span><input type="date" class="input" data-stc-add-f="check_out" value="${this.esc(f.check_out)}" min="${this.esc(minDay)}"></label>
        </div>
        <div class="stc-muted" data-stc-add-nights>${this.esc(this.addNightsLabel())}</div>
        ${res ? `
        <div class="stc-field stc-field-2">
          <label><span class="stc-dl">Guest name *</span><input type="text" class="input" data-stc-add-f="guest_name" value="${this.esc(f.guest_name)}" maxlength="120" autocomplete="off"></label>
          <label><span class="stc-dl">Guests</span><input type="number" class="input" data-stc-add-f="guests" value="${this.esc(f.guests)}" min="1" max="30" inputmode="numeric"></label>
        </div>
        <div class="stc-field stc-field-2">
          <label><span class="stc-dl">Email</span><input type="email" class="input" data-stc-add-f="guest_email" value="${this.esc(f.guest_email)}" maxlength="200" autocomplete="off"></label>
          <label><span class="stc-dl">Phone</span><input type="tel" class="input" data-stc-add-f="guest_phone" value="${this.esc(f.guest_phone)}" maxlength="40" autocomplete="off"></label>
        </div>
        <div class="stc-field">
          <label><span class="stc-dl">Total price (optional, USD)</span><input type="number" class="input" data-stc-add-f="total_price" value="${this.esc(f.total_price)}" min="0" step="0.01" inputmode="decimal" placeholder="Leave empty for $0 / settle later"></label>
        </div>` : ''}
        <div class="stc-field">
          <label><span class="stc-dl">${res ? 'Host note' : 'Reason / note'}</span><textarea class="input stc-ta" data-stc-add-f="note" maxlength="300" rows="2" placeholder="${res ? 'Internal note on the reservation' : 'Why these nights are held — the team sees it on Calendar blocks'}">${this.esc(f.note)}</textarea></label>
        </div>
        ${err}${conflicts}`;
      foot = `<button type="button" class="btn btn-secondary" data-stc-add-close>Cancel</button><button type="button" class="btn btn-primary" data-stc-add-review>Review →</button>`;
    } else {
      const n = this.addNights() || 0;
      const row = (k: string, v: string) => `<div class="stc-kv-row"><span class="stc-kv-k">${k}</span><span class="stc-kv-v">${v}</span></div>`;
      body = `
        <div class="stc-add-warn">⚠ This writes to Hostaway and syncs to every channel.</div>
        <div class="stc-kv">
          ${row('Property', this.esc(prop ? prop.name : f.listing_id))}
          ${row('What', res ? 'Manual reservation (direct)' : 'Hold nights (block)')}
          ${row('Dates', `${this.esc(this.fmtWMD(f.check_in))} → ${this.esc(this.fmtWMD(f.check_out))} · ${n} night${n === 1 ? '' : 's'}`)}
          ${res ? row('Guest', `${this.esc(f.guest_name)}${f.guests ? ` · ${this.esc(f.guests)} guest${f.guests === '1' ? '' : 's'}` : ''}`) : ''}
          ${res && (f.guest_email || f.guest_phone) ? row('Contact', [f.guest_email, f.guest_phone].filter(Boolean).map(v => this.esc(v)).join(' · ')) : ''}
          ${res ? row('Total', f.total_price ? this.fmtMoney(Number(f.total_price), 'USD') : '<span class="stc-muted">none set</span>') : ''}
          ${f.note ? row('Note', this.esc(f.note)) : ''}
        </div>
        ${this.addBusy ? `<div class="stc-muted" style="margin-top:12px;">Writing to Hostaway…</div>` : ''}
        ${err}${conflicts}`;
      foot = `<button type="button" class="btn btn-secondary" data-stc-add-back ${this.addBusy ? 'disabled' : ''}>← Back</button><button type="button" class="btn btn-primary" data-stc-add-confirm ${this.addBusy || this.addConflicts.length ? 'disabled' : ''}>${this.addBusy ? 'Writing…' : (res ? 'Confirm reservation' : 'Confirm hold')}</button>`;
    }
    host.innerHTML = `
      <div class="modal-overlay active stc-ovl">
        <div class="stc-modal" role="dialog" aria-modal="true" aria-labelledby="stc-add-title">
          <div class="stc-modal-head"><h3 id="stc-add-title">${this.addStep === 'form' ? 'Add to the calendar' : (res ? 'Confirm the reservation' : 'Confirm the hold')}</h3><button type="button" class="stc-close" data-stc-add-close aria-label="Close" ${this.addBusy ? 'disabled' : ''}>✕</button></div>
          <div class="stc-modal-body">${body}</div>
          <div class="stc-modal-foot">${foot}</div>
        </div>
      </div>`;
  }
  private reviewAdd(): void {
    this.syncAddForm();
    const f = this.addForm;
    const res = f.kind === 'reservation';
    const problems: string[] = [];
    if (!f.listing_id || !(this.data && this.data.properties.some(p => p.listing_id === f.listing_id))) problems.push('pick a property');
    const n = this.addNights();
    if (n == null) problems.push('pick both dates');
    else if (n <= 0) problems.push('checkout must be after check-in');
    else if (n > MAX_NIGHTS) problems.push(`${MAX_NIGHTS} nights at most`);
    if (this.data && this.validDate(f.check_in) && f.check_in < this.data.as_of) problems.push('check-in is in the past');
    if (res && !f.guest_name.trim()) problems.push('a guest name is required');
    if (res && f.guests && !(Number(f.guests) >= 1 && Number(f.guests) <= 30)) problems.push('guests must be 1–30');
    if (res && f.total_price && !(Number(f.total_price) >= 0)) problems.push('the total must be a number');
    if (res && f.guest_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.guest_email.trim())) problems.push('that email looks off');
    if (problems.length) { this.addErr = `Before the summary: ${problems.join(', ')}.`; this.addConflicts = []; this.drawAdd(); return; }
    this.addErr = ''; this.addConflicts = []; this.addStep = 'summary';
    this.drawAdd();
    document.querySelector<HTMLElement>('#stc-modal [data-stc-add-confirm]')?.focus();
  }
  private async submitAdd(): Promise<void> {
    if (this.addBusy || this.addStep !== 'summary') return;
    const f = this.addForm;
    const res = f.kind === 'reservation';
    const body: Record<string, unknown> = { listing_id: f.listing_id, kind: f.kind, check_in: f.check_in, check_out: f.check_out, confirm: true };
    if (f.note.trim()) body.note = f.note.trim().slice(0, 300);
    if (res) {
      const guest: Record<string, unknown> = { name: f.guest_name.trim() };
      if (f.guest_email.trim()) guest.email = f.guest_email.trim();
      if (f.guest_phone.trim()) guest.phone = f.guest_phone.trim();
      if (f.guests && Number(f.guests) >= 1) guest.guests = Math.round(Number(f.guests));
      body.guest = guest;
      body.total_price = f.total_price !== '' && Number.isFinite(Number(f.total_price)) ? Number(f.total_price) : null;
    }
    this.addBusy = true; this.addErr = ''; this.addConflicts = []; this.drawAdd();
    const r = await fetch(`${API}/create`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }).catch(() => null);
    const j = r ? await r.json().catch(() => ({})) : null;
    if (!this.addOpen) return;
    this.addBusy = false;
    if (!r || !r.ok) {
      if (r && r.status === 409) { this.addConflicts = Array.isArray(j && j.conflicts) ? j.conflicts.filter((c: any) => c && c.date).map((c: any) => ({ date: String(c.date), why: String(c.why || '') })) : []; this.addErr = (j && j.error) || 'Those nights are not free.'; }
      else if (r && r.status === 503) this.addErr = 'Hostaway isn\'t configured on this server (HOSTAWAY_ACCOUNT_ID / HOSTAWAY_API_KEY) — nothing was written.';
      else if (r && r.status === 404) this.addErr = (j && j.error) || 'This server doesn\'t support adding from the calendar yet — redeploy with R111.';
      else if (r && r.status === 403) this.addErr = 'Your role can\'t write to the calendar.';
      else this.addErr = `Couldn't write it — ${(j && j.error) || (r ? `HTTP ${r.status}` : 'no connection')}`;
      this.drawAdd();
      return;
    }
    const prop = this.data ? this.data.properties.find(p => p.listing_id === f.listing_id) : null;
    const n = this.addNights() || 0;
    this.closeAdd();
    this.toast(res ? `Reservation created for ${f.guest_name.trim()} at ${prop ? prop.name : f.listing_id} — Hostaway is syncing it.` : `Held ${n} night${n === 1 ? '' : 's'} at ${prop ? prop.name : f.listing_id} — Hostaway is syncing it.`);
    await this.load(true, true);
  }

  // ── styles (page-local, tokens only) ───────────────────────────────────
  private css(): string {
    return `
      .stc-kpis { margin-bottom: 16px; }
      .stc-kpi-sub { font-size: 11.5px; color: var(--text-muted); margin-top: 6px; }
      .stc-kpi-sub-bad { color: var(--danger-text); font-weight: 600; }
      .stc-toolbar { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; margin-bottom: 10px; }
      .stc-search { display: flex; align-items: center; gap: 8px; width: 300px; max-width: 100%; padding: 0 10px; border: 1px solid var(--border); border-radius: var(--radius-btn); background: var(--card); color: var(--text-faint); }
      .stc-search .input { border: none; padding: 9px 0; background: transparent; box-shadow: none; }
      .stc-search .input:focus { box-shadow: none; }
      .stc-search:focus-within { border-color: var(--accent); }
      .stc-updated { font-size: 12px; color: var(--text-faint); white-space: nowrap; }
      .stc-refresh { padding: 7px 10px; font-size: 14px; line-height: 1; }
      .stc-spacer { flex: 1; }
      .stc-nav { display: flex; gap: 4px; }
      .stc-nav .btn { padding: 7px 11px; }
      .stc-wins { display: flex; gap: 6px; }
      .stc-add-btn { padding: 8px 14px; white-space: nowrap; }
      .stc-filters { display: flex; gap: 6px; flex-wrap: wrap; margin-bottom: 14px; }
      .stc-card { background: var(--card); border: 1px solid var(--border); border-radius: var(--radius); box-shadow: var(--shadow-card); overflow: hidden; }
      .stc-scroller { overflow: auto; max-height: calc(100vh - 330px); min-height: 280px; position: relative; }
      .stc-hdr { display: flex; position: sticky; top: 0; z-index: 5; background: var(--card); border-bottom: 1px solid var(--border); }
      .stc-corner { position: sticky; left: 0; z-index: 6; flex: none; width: var(--stc-label-w, 260px); background: var(--card); border-right: 1px solid var(--border); padding: 0 16px; display: flex; align-items: flex-end; padding-bottom: 10px; font-size: 12px; color: var(--text-muted); }
      .stc-daysrow { flex: none; position: relative; height: 72px; }
      .stc-months { position: relative; height: 26px; }
      .stc-month { position: absolute; top: 6px; padding-left: 10px; font-size: 13px; font-weight: 700; color: var(--text); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; border-left: 1px solid var(--border-soft); }
      .stc-daycells { position: relative; height: 46px; }
      .stc-day { position: absolute; top: 0; height: 46px; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 2px; }
      .stc-day.stc-wk { background: var(--bg-subtle); }
      .stc-dow { font-size: 10px; font-weight: 600; letter-spacing: 0.06em; text-transform: uppercase; color: var(--text-faint); }
      .stc-dnum { font-size: 13px; font-weight: 600; color: var(--text); width: 26px; height: 26px; border-radius: var(--radius-pill); display: inline-flex; align-items: center; justify-content: center; font-variant-numeric: tabular-nums; }
      .stc-day.stc-today .stc-dow { color: var(--info); }
      .stc-day.stc-today .stc-dnum { background: var(--info); color: var(--card); }
      .stc-row { display: flex; border-bottom: 1px solid var(--border-soft); }
      .stc-hdr, .stc-row { width: max-content; min-width: 100%; }   /* span the full scroll width so the sticky label column never leaves its containing block */
      .stc-row:last-child { border-bottom: none; }
      .stc-lbl { position: sticky; left: 0; z-index: 4; flex: none; width: var(--stc-label-w, 260px); background: var(--card); border-right: 1px solid var(--border); padding: 0 16px; display: flex; align-items: center; gap: 12px; overflow: hidden; }
      .stc-lbl-txt { min-width: 0; flex: 1; }
      .stc-lbl-sub { font-size: 11px; color: var(--success-text); font-weight: 600; margin-top: 3px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
      .stc-pav { flex: none; width: 36px; height: 36px; border-radius: var(--radius-sm); background: var(--info-soft); color: var(--info-text); font-size: 11px; font-weight: 700; display: inline-flex; align-items: center; justify-content: center; object-fit: cover; letter-spacing: 0.02em; }
      .stc-pname { font-size: 13px; font-weight: 600; color: var(--text); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
      .stc-tag { display: inline-block; margin-left: 6px; padding: 1px 6px; border-radius: var(--radius-pill); background: var(--accent-soft); color: var(--accent); border: 1px solid var(--accent-border); font-size: 9.5px; font-weight: 700; letter-spacing: 0.06em; vertical-align: 1px; }
      .stc-meter { display: flex; align-items: center; gap: 8px; margin-top: 5px; }
      .stc-meter-bar { width: 44px; height: 4px; border-radius: var(--radius-pill); background: var(--border); overflow: hidden; flex: none; }
      .stc-meter-fill { display: block; height: 100%; background: var(--info); border-radius: var(--radius-pill); }
      .stc-meter-txt { font-size: 11px; color: var(--text-muted); white-space: nowrap; }
      .stc-lanes { position: relative; flex: none; overflow: hidden; background-image: linear-gradient(to right, var(--border-soft) 1px, transparent 1px); background-size: var(--stc-col, 64px) 100%; }
      .stc-shade { position: absolute; top: 0; bottom: 0; pointer-events: none; }
      .stc-shade-wk { background: var(--bg-subtle); }
      .stc-shade-today { background: var(--info-soft); }
      .stc-shade-open { background: var(--success-soft); box-shadow: inset 0 3px 0 var(--success); }
      .stc-rate { position: absolute; display: inline-flex; align-items: center; justify-content: center; font-size: 11px; color: var(--text-faint); font-variant-numeric: tabular-nums; pointer-events: none; }
      .stc-bar { position: absolute; border-radius: var(--radius-pill); display: flex; align-items: center; cursor: pointer; font-size: 12px; box-sizing: border-box; border: 1.5px solid transparent; transition: box-shadow .12s ease, transform .12s ease, opacity .12s ease; }
      .stc-bar:hover { box-shadow: var(--shadow-card); z-index: 2; }
      .stc-bar:focus-visible { outline: 2px solid var(--accent); outline-offset: 1px; }
      .stc-bar-sel { outline: 2px solid var(--accent); outline-offset: 1px; z-index: 3; }
      .stc-bar-dim { opacity: .28; }
      .stc-bar-dim:hover { opacity: .7; }
      .stc-clip-l { border-top-left-radius: 0; border-bottom-left-radius: 0; border-left-style: none; }
      .stc-clip-r { border-top-right-radius: 0; border-bottom-right-radius: 0; border-right-style: none; }
      .stc-bar-in { display: flex; align-items: center; gap: 7px; padding: 0 6px 0 4px; min-width: 0; width: 100%; }
      .stc-av { flex: none; width: 22px; height: 22px; border-radius: var(--radius-pill); display: inline-flex; align-items: center; justify-content: center; font-size: 9.5px; font-weight: 700; letter-spacing: 0.02em; }
      .stc-bname { font-weight: 600; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; min-width: 0; }
      .stc-bnights { flex: none; font-size: 11px; opacity: 0.85; white-space: nowrap; }
      .stc-bar-in_house { background: var(--info); color: var(--card); }
      .stc-bar-in_house .stc-av { background: var(--card); color: var(--info); opacity: 0.95; }
      .stc-bar-upcoming { background: var(--info-soft); color: var(--info-text); border-color: var(--info); }
      .stc-bar-upcoming .stc-av { background: var(--info); color: var(--card); }
      .stc-bar-checked_out { background: var(--text-muted); color: var(--card); }
      .stc-bar-checked_out .stc-av { background: var(--card); color: var(--text-muted); opacity: 0.9; }
      .stc-bar-inq { background: var(--card); color: var(--info-text); border-style: dashed; border-color: var(--info); }
      .stc-bar-inq .stc-av { background: var(--info-soft); color: var(--info-text); }
      .stc-bar-owner { background: var(--accent-soft); color: var(--accent); border-color: var(--accent-border); }
      .stc-bar-owner .stc-av { background: var(--card); font-size: 12px; }
      /* negative experience: a danger stripe on every bar; soft red fill for upcoming, a red ring on the solid ones */
      .stc-bar-neg { box-shadow: inset 4px 0 0 var(--danger); }
      .stc-bar-neg.stc-bar-upcoming { background: var(--danger-soft); border-color: var(--danger); color: var(--danger-text); }
      .stc-bar-neg.stc-bar-upcoming .stc-av { background: var(--danger); color: var(--card); }
      .stc-bar-neg.stc-bar-in_house, .stc-bar-neg.stc-bar-checked_out { box-shadow: inset 4px 0 0 var(--danger), 0 0 0 2px var(--danger); }
      .stc-bar-neg .stc-bar-in { padding-left: 8px; }
      .stc-bar-neg:hover { box-shadow: inset 4px 0 0 var(--danger), var(--shadow-card); }
      .stc-bar-block { background: repeating-linear-gradient(135deg, var(--bg-subtle) 0 6px, var(--border) 6px 8px); color: var(--text-muted); border-color: var(--border); }
      .stc-bar-block:hover { box-shadow: var(--shadow-card); border-color: var(--border-strong); }
      .stc-bar-held { background: repeating-linear-gradient(135deg, var(--warning-soft) 0 6px, var(--border) 6px 8px); color: var(--warning-text); }
      .stc-blk-ic { flex: none; width: 22px; height: 22px; border-radius: var(--radius-pill); background: var(--card); display: inline-flex; align-items: center; justify-content: center; font-size: 13px; }
      .stc-badge { position: absolute; top: -7px; right: -6px; width: 18px; height: 18px; border-radius: var(--radius-pill); display: inline-flex; align-items: center; justify-content: center; font-size: 10px; font-weight: 800; line-height: 1; box-shadow: 0 0 0 2px var(--card); z-index: 1; }
      .stc-badge-opp { background: var(--success); color: var(--card); }
      .stc-badge-msg { background: var(--card); color: var(--text); border: 1px solid var(--border); font-size: 9px; }
      .stc-badge-neg { border-color: var(--danger); background: var(--danger-soft); }
      .stc-badge-rv { top: auto; bottom: -7px; right: -6px; width: auto; min-width: 18px; padding: 0 5px; background: var(--warning-soft); color: var(--warning-text); border: 1px solid var(--warning); font-size: 9.5px; white-space: nowrap; }
      .stc-turn { position: absolute; left: -5px; bottom: -6px; width: 14px; height: 14px; border-radius: var(--radius-pill); background: var(--card); border: 2px solid var(--warning); box-shadow: 0 0 0 1px var(--card); z-index: 1; }
      .stc-turn::after { content: ''; position: absolute; inset: 2px; border-radius: var(--radius-pill); background: var(--warning); }
      .stc-turn-cleaned, .stc-turn-verified, .stc-turn-ready { border-color: var(--success); }
      .stc-turn-cleaned::after, .stc-turn-verified::after, .stc-turn-ready::after { background: var(--success); }
      .stc-turn-assigned, .stc-turn-in_progress { border-color: var(--info); }
      .stc-turn-assigned::after, .stc-turn-in_progress::after { background: var(--info); }
      .stc-vdot { position: absolute; top: -4px; left: 10px; width: 10px; height: 10px; border-radius: var(--radius-pill); background: var(--text-faint); box-shadow: 0 0 0 2px var(--card); z-index: 1; }
      .stc-vdot-verified { background: var(--success); }
      .stc-vdot-pending { background: var(--info); }
      .stc-vdot-at_risk { background: var(--warning); }
      .stc-vdot-blocked { background: var(--danger); }
      /* ops lane: turns + work orders under the stay lanes */
      .stc-ops-sep { position: absolute; left: 0; right: 0; border-top: 1px dashed var(--border); pointer-events: none; }
      .stc-ops-chip { position: absolute; display: inline-flex; align-items: center; gap: 4px; padding: 0 6px; border-radius: var(--radius-sm); font-size: 10.5px; font-weight: 600; line-height: 1; box-sizing: border-box; border: 1px solid var(--border); background: var(--bg-subtle); color: var(--text-muted); cursor: pointer; white-space: nowrap; overflow: hidden; }
      .stc-ops-chip:hover { box-shadow: var(--shadow-card); z-index: 2; }
      .stc-ops-chip:focus-visible { outline: 2px solid var(--accent); outline-offset: 1px; }
      .stc-ops-g { flex: none; }
      .stc-ops-t { min-width: 0; overflow: hidden; text-overflow: ellipsis; }
      .stc-ops-done { background: var(--success-soft); color: var(--success-text); border-color: var(--success-border, var(--border)); }
      .stc-ops-busy { background: var(--warning-soft); color: var(--warning-text); border-color: var(--warning); }
      .stc-ops-unassigned { background: var(--bg-subtle); color: var(--text-muted); border-color: var(--border-strong); border-style: dashed; }
      .stc-ops-hl { box-shadow: 0 0 0 2px var(--warning); border-style: solid; border-color: var(--warning); color: var(--warning-text); }
      .stc-ops-wo { cursor: grab; }
      .stc-ops-due { border-style: dashed; opacity: .85; }   /* a due date, not a booked day — booking it from here gives it one */
      .stc-ops-wo:active { cursor: grabbing; }
      .stc-ops-p-routine { background: var(--info-soft); color: var(--info-text); border-color: var(--info); }
      .stc-ops-p-hot { background: var(--danger-soft); color: var(--danger-text); border-color: var(--danger); }
      .stc-ops-p-pm { background: var(--accent-soft); color: var(--accent); border-color: var(--accent-border); }
      .stc-ops-open { outline: 2px solid var(--accent); outline-offset: 1px; z-index: 3; }
      .stc-lg-chip { position: relative; height: 18px; padding: 0 5px; font-size: 10px; cursor: inherit; }
      .stc-dragging .stc-bar { pointer-events: none; }
      .stc-dropcol { position: absolute; top: 0; bottom: 0; background: var(--accent-soft); border-left: 2px dashed var(--accent); border-right: 2px dashed var(--accent); box-sizing: border-box; pointer-events: none; z-index: 1; }
      .stc-dropcol-no { background: var(--danger-soft); border-color: var(--danger); }
      .stc-legend { display: flex; flex-wrap: wrap; gap: 6px 16px; padding: 10px 16px; border-top: 1px solid var(--border); font-size: 12px; color: var(--text-muted); background: var(--bg-subtle); }
      .stc-lg { display: inline-flex; align-items: center; gap: 6px; }
      .stc-lg-toggle { font: inherit; color: inherit; background: var(--card); border: 1px solid var(--border); border-radius: var(--radius-pill); padding: 2px 10px 2px 6px; cursor: pointer; }
      .stc-lg-toggle:hover { border-color: var(--accent-border); color: var(--accent); }
      .stc-lg-toggle.off { color: var(--text-faint); text-decoration: line-through; }
      .stc-lg-toggle.off .stc-lg-sw, .stc-lg-toggle.off .stc-lg-chip { opacity: .4; }
      .stc-lg-sw { width: 22px; height: 12px; border-radius: var(--radius-pill); border: 1.5px solid transparent; box-sizing: border-box; }
      .stc-lg-inhouse { background: var(--info); }
      .stc-lg-upcoming { background: var(--info-soft); border-color: var(--info); }
      .stc-lg-out { background: var(--text-muted); }
      .stc-lg-inq { background: var(--card); border-style: dashed; border-color: var(--info); }
      .stc-lg-block { background: repeating-linear-gradient(135deg, var(--bg-subtle) 0 4px, var(--border) 4px 6px); border-color: var(--border); }
      .stc-lg-owner { background: var(--accent-soft); border-color: var(--accent-border); }
      .stc-lg-neg { background: var(--danger-soft); border-color: var(--danger); box-shadow: inset 3px 0 0 var(--danger); }
      .stc-lg-badge { position: relative; top: auto; right: auto; left: auto; bottom: auto; box-shadow: none; }   /* stays a containing block for the dot's ::after */
      /* right panel */
      .stc-panel { position: fixed; top: 0; right: 0; bottom: 0; width: 440px; max-width: 100vw; background: var(--card); border-left: 1px solid var(--border); box-shadow: var(--shadow-pop); z-index: 150; transform: translateX(105%); transition: transform .18s ease; display: flex; flex-direction: column; overflow: hidden; }
      .stc-panel.open { transform: none; }
      .stc-panel-head { display: flex; align-items: center; gap: 12px; padding: 18px 20px 14px; border-bottom: 1px solid var(--border-soft); }
      .stc-gav { flex: none; width: 46px; height: 46px; border-radius: var(--radius-pill); background: var(--info-soft); color: var(--info-text); font-weight: 700; font-size: 15px; display: inline-flex; align-items: center; justify-content: center; }
      .stc-gav-neg { background: var(--danger-soft); color: var(--danger-text); box-shadow: 0 0 0 2px var(--danger); }
      .stc-gav-blk { background: var(--bg-subtle); color: var(--text-muted); border: 1px solid var(--border); font-size: 20px; }
      .stc-gname { flex: 1; min-width: 0; font-size: 18px; font-weight: 700; color: var(--text); margin: 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
      .stc-close { flex: none; width: 32px; height: 32px; border-radius: var(--radius-pill); border: none; background: transparent; color: var(--text-muted); font-size: 16px; cursor: pointer; }
      .stc-close:hover { background: var(--bg-subtle); color: var(--text); }
      .stc-panel-body { flex: 1; overflow-y: auto; padding: 16px 20px 28px; }
      .stc-actions { display: flex; flex-direction: column; gap: 8px; margin-bottom: 16px; }
      .stc-actions .btn { width: 100%; justify-content: center; padding: 11px 14px; font-size: 13px; }
      .stc-staycard { border: 1px solid var(--border); border-radius: var(--radius); padding: 14px; background: var(--card); box-shadow: var(--shadow-card); }
      .stc-staycard-neg { border-color: var(--danger-border, var(--danger)); box-shadow: inset 4px 0 0 var(--danger), var(--shadow-card); }
      .stc-staycard-top { display: flex; align-items: center; gap: 12px; }
      .stc-staycard-txt { flex: 1; min-width: 0; }
      .stc-pills { flex: none; display: flex; flex-direction: column; align-items: flex-end; gap: 4px; max-width: 46%; }
      .stc-staycard-dates { display: flex; align-items: center; justify-content: space-between; gap: 10px; margin-top: 14px; padding-top: 12px; border-top: 1px solid var(--border-soft); }
      .stc-dl { font-size: 10.5px; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; color: var(--text-faint); }
      .stc-dv { font-size: 14px; font-weight: 600; color: var(--text); margin-top: 3px; }
      .stc-nights { flex: none; display: inline-flex; align-items: center; gap: 4px; padding: 5px 11px; border-radius: var(--radius-pill); background: var(--info-soft); color: var(--info-text); font-size: 12px; font-weight: 600; }
      .stc-turnline { display: flex; align-items: center; gap: 8px; margin-top: 10px; font-size: 12px; color: var(--text-muted); }
      .stc-turnline .stc-turn { position: relative; left: auto; bottom: auto; box-shadow: none; }
      .stc-verline { margin-top: 10px; font-size: 12px; color: var(--text-muted); line-height: 1.5; }
      .stc-negline { margin-top: 10px; padding: 8px 10px; border-radius: var(--radius-sm); background: var(--danger-soft); color: var(--danger-text); font-size: 12px; line-height: 1.45; }
      .stc-link { font: inherit; font-weight: 600; color: var(--accent); background: none; border: none; padding: 0; cursor: pointer; text-decoration: underline; text-underline-offset: 2px; }
      .stc-link:hover { color: var(--accent-hover); }
      .stc-pill { flex: none; display: inline-flex; align-items: center; gap: 6px; padding: 4px 10px; border-radius: var(--radius-pill); font-size: 11.5px; font-weight: 600; white-space: nowrap; background: var(--bg-subtle); color: var(--text-muted); border: 1px solid var(--border); }
      .stc-pill-dot { width: 6px; height: 6px; border-radius: var(--radius-pill); background: var(--text-faint); }
      .stc-pill-in_house { background: var(--info); color: var(--card); border-color: var(--info); } .stc-pill-in_house .stc-pill-dot { background: var(--card); }
      .stc-pill-upcoming { background: var(--info-soft); color: var(--info-text); border-color: var(--info-soft); } .stc-pill-upcoming .stc-pill-dot { background: var(--info); }
      .stc-pill-inq { border-style: dashed; border-color: var(--info); color: var(--info-text); background: var(--card); } .stc-pill-inq .stc-pill-dot { background: var(--info); }
      .stc-pill-owner { background: var(--accent-soft); color: var(--accent); border-color: var(--accent-border); } .stc-pill-owner .stc-pill-dot { background: var(--accent); }
      .stc-pill-block { background: var(--bg-subtle); color: var(--text-muted); border-color: var(--border-strong); }
      .stc-pill-muted { font-weight: 500; color: var(--text-faint); background: var(--card); }
      .stc-pill-rv { background: var(--warning-soft); color: var(--warning-text); border-color: var(--warning); }
      .stc-pill-v-ok { background: var(--success-soft); color: var(--success-text); border-color: var(--success-border, var(--success)); }
      .stc-pill-v-pend { background: var(--info-soft); color: var(--info-text); border-color: var(--info-soft); }
      .stc-pill-v-warn { background: var(--warning-soft); color: var(--warning-text); border-color: var(--warning); }
      .stc-pill-v-bad { background: var(--danger-soft); color: var(--danger-text); border-color: var(--danger); }
      .stc-pill-v-none { background: var(--card); color: var(--text-faint); border-style: dashed; }
      .stc-sec { font-size: 11px; font-weight: 700; letter-spacing: 0.09em; text-transform: uppercase; color: var(--text-muted); margin: 20px 0 10px; display: flex; align-items: center; gap: 8px; }
      .stc-sec-btn { width: 100%; font: inherit; font-size: 11px; font-weight: 700; letter-spacing: 0.09em; background: none; border: none; padding: 0; cursor: pointer; text-align: left; }
      .stc-sec-btn:hover { color: var(--text); }
      .stc-chev { font-size: 12px; color: var(--text-faint); }
      .stc-count { display: inline-flex; align-items: center; justify-content: center; min-width: 20px; height: 20px; padding: 0 6px; border-radius: var(--radius-pill); background: var(--bg-subtle); border: 1px solid var(--border); color: var(--text); font-size: 11px; letter-spacing: 0; }
      .stc-opp { display: flex; align-items: flex-start; gap: 10px; padding: 12px 12px 12px 12px; border-radius: var(--radius); border: 1px solid var(--success-border, var(--border)); background: var(--success-soft); margin-bottom: 10px; }
      .stc-opp-done { background: var(--bg-subtle); border-color: var(--border); }
      .stc-opp-ic { flex: none; width: 28px; height: 28px; border-radius: var(--radius-sm); background: var(--card); border: 1px solid var(--border); display: inline-flex; align-items: center; justify-content: center; font-size: 14px; }
      .stc-opp-body { flex: 1; min-width: 0; }
      .stc-opp-title { font-size: 13.5px; font-weight: 600; color: var(--text); }
      .stc-opp-text { font-size: 12.5px; color: var(--text-muted); line-height: 1.5; margin-top: 4px; }
      .stc-opp-src { font-size: 11px; color: var(--text-faint); margin-top: 4px; }
      .stc-apply { flex: none; white-space: nowrap; background: var(--card); color: var(--success-text); border-color: var(--success-border, var(--border)); }
      .stc-apply:hover:not(:disabled) { background: var(--success-soft); }
      .stc-applied { color: var(--text-muted); opacity: 1; }
      .stc-fine { font-size: 11.5px; color: var(--text-faint); line-height: 1.5; margin: 2px 2px 0; }
      .stc-note { font-size: 12px; color: var(--text); background: var(--bg-subtle); border: 1px solid var(--border); border-radius: var(--radius-sm); padding: 8px 10px; margin-top: 10px; }
      .stc-none { font-size: 12.5px; color: var(--text-muted); line-height: 1.5; padding: 10px 12px; border: 1px dashed var(--border); border-radius: var(--radius); }
      .stc-sent { display: flex; gap: 12px; padding: 12px; border-radius: var(--radius); border: 1px solid var(--border); background: var(--card); }
      .stc-sent-ic { flex: none; width: 30px; height: 30px; border-radius: var(--radius-pill); border: 1px solid var(--border); background: var(--bg-subtle); display: inline-flex; align-items: center; justify-content: center; font-size: 15px; }
      .stc-sent-title { font-size: 13.5px; font-weight: 600; color: var(--text); }
      .stc-sent-text { font-size: 12.5px; color: var(--text-muted); line-height: 1.5; margin-top: 4px; }
      .stc-sent-negative { border-color: var(--danger-border, var(--border)); background: var(--danger-soft); }
      .stc-sent-positive { border-color: var(--success-border, var(--border)); background: var(--success-soft); }
      .stc-checks { font-size: 12.5px; color: var(--text-muted); line-height: 1.5; }
      .stc-muted { font-size: 12px; color: var(--text-faint); font-weight: 400; }
      /* review */
      .stc-review { border: 1px solid var(--warning-soft); border-radius: var(--radius); padding: 12px 14px; background: var(--card); box-shadow: var(--shadow-card); }
      .stc-rv-head { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; font-size: 14px; color: var(--text); }
      .stc-stars { color: var(--warning); letter-spacing: 1px; font-size: 15px; }
      .stc-rv-text { font-size: 12.5px; color: var(--text); line-height: 1.55; margin-top: 8px; white-space: pre-wrap; }
      .stc-rv-sub { margin-top: 10px; padding-top: 10px; border-top: 1px solid var(--border-soft); }
      .stc-rv-actions { margin-top: 12px; }
      .stc-rv-actions .btn { width: 100%; justify-content: center; }
      /* key/value rows (Details drawer, WO popover, Add summary) */
      .stc-kv { border: 1px solid var(--border); border-radius: var(--radius); background: var(--card); padding: 4px 14px; }
      .stc-kv-row { display: flex; justify-content: space-between; gap: 12px; padding: 8px 0; border-bottom: 1px solid var(--border-soft); font-size: 12.5px; }
      .stc-kv-row:last-child { border-bottom: none; }
      .stc-kv-k { color: var(--text-muted); flex: none; }
      .stc-kv-v { color: var(--text); font-weight: 500; text-align: right; min-width: 0; overflow-wrap: anywhere; }
      .stc-kv-actions { padding: 10px 0 8px; }
      .stc-kv-actions .btn { width: 100%; justify-content: center; }
      .stc-ext { text-decoration: none; }
      .stc-evlist { display: flex; flex-direction: column; gap: 8px; }
      .stc-evrow { display: flex; align-items: center; gap: 10px; font-size: 12.5px; }
      .stc-evrow .stc-lg-chip { flex: none; width: 26px; justify-content: center; padding: 0; }
      .stc-evt { font-weight: 600; color: var(--text); }
      /* work-order popover */
      .stc-pop { position: fixed; z-index: 250; background: var(--card); border: 1px solid var(--border); border-radius: var(--radius); box-shadow: var(--shadow-pop); padding: 14px; box-sizing: border-box; }
      .stc-pop-head { display: flex; align-items: flex-start; gap: 10px; margin-bottom: 10px; }
      .stc-pop-head .stc-lg-chip { flex: none; margin-top: 2px; }
      .stc-pop-title { flex: 1; min-width: 0; font-size: 13.5px; font-weight: 600; color: var(--text); line-height: 1.35; overflow-wrap: anywhere; }
      .stc-pop-head .stc-close { width: 28px; height: 28px; font-size: 14px; }
      .stc-pop-kv { padding: 0 10px; }
      .stc-pop-kv .stc-kv-row { padding: 6px 0; font-size: 12px; }
      .stc-pop-date { display: flex; flex-direction: column; gap: 5px; margin-top: 12px; }
      .stc-pop-err { margin-top: 10px; padding: 8px 10px; border-radius: var(--radius-sm); background: var(--danger-soft); color: var(--danger-text); font-size: 12px; line-height: 1.45; }
      .stc-pop-actions { margin-top: 12px; }
      .stc-pop-actions .btn { width: 100%; justify-content: center; }
      /* "+ Add" modal */
      .stc-ovl { z-index: 300; padding: 16px; box-sizing: border-box; }   /* main.css .modal-overlay.active brings the backdrop + centering */
      .stc-modal { width: 520px; max-width: 100%; max-height: calc(100vh - 32px); background: var(--card); border: 1px solid var(--border); border-radius: var(--radius-lg); box-shadow: var(--shadow-pop); display: flex; flex-direction: column; overflow: hidden; }
      .stc-modal-head { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 16px 20px 12px; border-bottom: 1px solid var(--border-soft); }
      .stc-modal-head h3 { margin: 0; font-size: 16px; font-weight: 700; color: var(--text); }
      .stc-modal-body { flex: 1; overflow-y: auto; padding: 16px 20px; }
      .stc-modal-foot { display: flex; justify-content: flex-end; gap: 10px; padding: 12px 20px 16px; border-top: 1px solid var(--border-soft); }
      .stc-field { margin-bottom: 14px; }
      .stc-field label { display: flex; flex-direction: column; gap: 5px; }
      .stc-field > .stc-dl, .stc-field > label > .stc-dl { display: block; margin-bottom: 5px; }
      .stc-field-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
      .stc-add-kind { margin-bottom: 6px; }
      .stc-ta { resize: vertical; min-height: 56px; font-family: inherit; }
      .stc-combo { position: relative; }
      .stc-combo-list { margin-top: 6px; border: 1px solid var(--border); border-radius: var(--radius-sm); background: var(--card); max-height: 240px; overflow-y: auto; }
      .stc-combo-list:empty { display: none; }
      .stc-combo-hint { padding: 10px 12px; font-size: 12px; color: var(--text-faint); }
      .stc-combo-opt { display: flex; align-items: center; gap: 10px; width: 100%; padding: 8px 10px; border: none; border-bottom: 1px solid var(--border-soft); background: var(--card); color: var(--text); font: inherit; font-size: 13px; cursor: pointer; text-align: left; }
      .stc-combo-opt:last-child { border-bottom: none; }
      .stc-combo-opt:hover, .stc-combo-opt:focus-visible { background: var(--accent-soft); outline: none; }
      .stc-combo-opt .stc-pav, .stc-combo-sel .stc-pav { width: 28px; height: 28px; font-size: 10px; }
      .stc-combo-name { flex: 1; min-width: 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; font-weight: 600; }
      .stc-combo-sel { display: flex; align-items: center; gap: 10px; padding: 6px 6px 6px 10px; border: 1px solid var(--accent-border); border-radius: var(--radius-btn); background: var(--accent-soft); color: var(--text); font-size: 13px; }
      .stc-add-warn { padding: 10px 12px; border-radius: var(--radius-sm); background: var(--warning-soft); color: var(--warning-text); border: 1px solid var(--warning); font-size: 12.5px; font-weight: 600; margin-bottom: 12px; }
      .stc-add-conf { margin-top: 12px; padding: 10px 12px; border-radius: var(--radius-sm); background: var(--danger-soft); color: var(--danger-text); font-size: 12.5px; }
      .stc-add-conf ul { margin: 6px 0 0; padding-left: 18px; }
      .stc-add-conf li { margin: 2px 0; }
      /* toast */
      .stc-toast { position: fixed; left: 50%; bottom: 24px; transform: translateX(-50%) translateY(16px); opacity: 0; pointer-events: none; z-index: 400; background: var(--text); color: var(--card); padding: 11px 18px; border-radius: var(--radius-pill); font-size: 13px; font-weight: 500; box-shadow: var(--shadow-pop); max-width: calc(100vw - 32px); transition: opacity .18s ease, transform .18s ease; }
      .stc-toast.show { opacity: 1; transform: translateX(-50%); }
      .stc-toast.stc-toast-act { pointer-events: auto; display: inline-flex; align-items: center; gap: 12px; }
      .stc-toast-btn { font: inherit; font-weight: 700; color: var(--card); background: transparent; border: 1px solid var(--card); border-radius: var(--radius-pill); padding: 3px 11px; cursor: pointer; }
      .stc-toast-btn:hover { background: var(--card); color: var(--text); }
      /* R112: turn chips — initials discs, at-risk ring, corner badges, draggable */
      .stc-ops-turn { overflow: visible; }
      .stc-ops-drag { cursor: grab; }
      .stc-ops-drag:active { cursor: grabbing; }
      .stc-ops-avs { display: inline-flex; align-items: center; }
      .stc-ops-av { width: 16px; height: 16px; border-radius: var(--radius-pill); background: var(--card); color: var(--text); border: 1px solid var(--border-strong); font-size: 8px; font-weight: 800; letter-spacing: 0.02em; display: inline-flex; align-items: center; justify-content: center; box-sizing: border-box; flex: none; }
      .stc-ops-av + .stc-ops-av { margin-left: -4px; }
      .stc-ops-busy .stc-ops-av { color: var(--warning-text); border-color: var(--warning); }
      .stc-ops-done .stc-ops-av { color: var(--success-text); border-color: var(--success); }
      .stc-ops-risk { background: var(--danger-soft); color: var(--danger-text); border-color: var(--danger); border-style: solid; box-shadow: 0 0 0 2px var(--danger); }
      .stc-ops-risk .stc-ops-av { color: var(--danger-text); border-color: var(--danger); }
      .stc-ops-cb { position: absolute; top: -6px; height: 13px; min-width: 13px; padding: 0 3px; border-radius: var(--radius-pill); background: var(--card); border: 1px solid var(--border); color: var(--text-muted); font-size: 8.5px; font-weight: 700; line-height: 11px; display: inline-flex; align-items: center; justify-content: center; box-shadow: 0 0 0 1px var(--card); z-index: 1; box-sizing: border-box; }
      .stc-ops-cb-ph { right: -4px; }
      .stc-ops-cb-mv { left: -4px; color: var(--accent); border-color: var(--accent-border); background: var(--accent-soft); }
      .stc-lbl-sub-warn { color: var(--warning-text); }
      .stc-lg-n { color: var(--text-faint); font-weight: 400; }
      .stc-lg-hidden { padding: 2px 10px; color: var(--text-muted); }
      .stc-lg-hidden.on { border-color: var(--accent); background: var(--accent-soft); color: var(--accent); }
      /* R112: turn popover */
      .stc-pop-turn { max-height: calc(100vh - 16px); overflow-y: auto; }
      .stc-pop-pills { display: flex; flex-wrap: wrap; gap: 4px; margin: -2px 0 10px; }
      .stc-pop-pills .stc-pill { padding: 2px 8px; font-size: 11px; }
      .stc-pop-sel { display: flex; flex-direction: column; gap: 5px; margin-top: 10px; }
      .stc-pop-sel select.input, .stc-pop-date .input { padding: 7px 10px; font-size: 12.5px; }
      .stc-pop-loading { margin-top: 10px; }
      .stc-pop-warn { margin-top: 10px; padding: 8px 10px; border-radius: var(--radius-sm); background: var(--warning-soft); color: var(--warning-text); border: 1px solid var(--warning); font-size: 12px; line-height: 1.45; }
      .stc-pop-warn-acts { display: flex; gap: 8px; margin-top: 8px; }
      .stc-pop-warn-acts .btn { flex: 1; justify-content: center; padding: 7px 10px; }
      .stc-pop-kv .stc-kv-v a.stc-link { margin: 0 2px; }
      /* R112: inquiry panel */
      .stc-inqbox { padding: 10px 12px; border-radius: var(--radius-sm); border: 1px dashed var(--info); background: var(--card); font-size: 12.5px; line-height: 1.5; color: var(--text); }
      .stc-inqbox-bad { border: 1px solid var(--danger); background: var(--danger-soft); color: var(--danger-text); }
      .stc-inqbox-dis { border: 1px solid var(--border); background: var(--bg-subtle); color: var(--text-muted); }
      .stc-inqbox + .stc-inqbox { margin-top: 8px; }
      .stc-inq-acts { display: flex; gap: 8px; margin-top: 10px; }
      .stc-inq-acts .btn { flex: 1; justify-content: center; }
      /* R112: arrival checklist */
      .stc-cl { display: flex; flex-direction: column; gap: 6px; }
      .stc-ci { display: flex; align-items: flex-start; gap: 8px; padding: 8px 10px; border: 1px solid var(--border); border-radius: var(--radius-sm); background: var(--card); }
      .stc-ci-done { background: var(--success-soft); border-color: var(--success-border, var(--border)); }
      .stc-ci-failed { background: var(--danger-soft); border-color: var(--danger); }
      .stc-ci-over { border-color: var(--danger); box-shadow: inset 3px 0 0 var(--danger); }
      .stc-ci-waived, .stc-ci-na { opacity: .7; }
      .stc-ci-req { flex: none; width: 8px; height: 8px; border-radius: var(--radius-pill); margin-top: 6px; background: var(--accent); box-sizing: border-box; }
      .stc-ci-opt { background: transparent; border: 1.5px solid var(--border-strong); }
      .stc-ci-body { flex: 1; min-width: 0; }
      .stc-ci-top { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
      .stc-ci-label { font-size: 12.5px; font-weight: 600; color: var(--text); }
      .stc-ci-top .stc-pill { padding: 1px 7px; font-size: 10.5px; }
      .stc-ci-sub { font-size: 11.5px; color: var(--text-faint); margin-top: 2px; line-height: 1.4; overflow-wrap: anywhere; }
      .stc-ci-sub b { color: var(--danger-text); }
      .stc-ci-acts { flex: none; display: flex; gap: 3px; }
      .stc-ci-btn { width: 26px; height: 26px; border-radius: var(--radius-sm); border: 1px solid var(--border); background: var(--card); color: var(--text-muted); font: inherit; font-size: 12px; cursor: pointer; display: inline-flex; align-items: center; justify-content: center; padding: 0; box-sizing: border-box; }
      .stc-ci-btn:hover:not(:disabled) { border-color: var(--accent); color: var(--accent); background: var(--accent-soft); }
      .stc-ci-btn-ok:hover:not(:disabled) { border-color: var(--success); color: var(--success-text); background: var(--success-soft); }
      .stc-ci-btn-bad:hover:not(:disabled) { border-color: var(--danger); color: var(--danger-text); background: var(--danger-soft); }
      .stc-ci-btn:disabled { opacity: .5; cursor: default; }
      .stc-ci-photo { font-size: 11px; }
      .stc-cl-foot { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; margin-top: 10px; font-size: 12px; }
      /* R112: ECI / LCO switch rows */
      .stc-el { margin-top: 12px; padding-top: 10px; border-top: 1px solid var(--border-soft); display: flex; flex-direction: column; gap: 8px; }
      .stc-el-row { display: flex; align-items: center; gap: 10px; }
      .stc-sw { flex: none; width: 36px; height: 20px; border-radius: var(--radius-pill); border: 1px solid var(--border-strong); background: var(--bg-subtle); position: relative; cursor: pointer; padding: 0; transition: background .12s ease, border-color .12s ease; }
      .stc-sw-knob { position: absolute; top: 2px; left: 2px; width: 14px; height: 14px; border-radius: var(--radius-pill); background: var(--card); box-shadow: var(--shadow-card); transition: left .12s ease; }
      .stc-sw.on { background: var(--accent); border-color: var(--accent); }
      .stc-sw.on .stc-sw-knob { left: 18px; }
      .stc-sw.declined { background: var(--danger-soft); border-color: var(--danger); }
      .stc-sw:disabled { opacity: .5; cursor: default; }
      .stc-sw:focus-visible { outline: none; box-shadow: var(--focus-ring); }
      .stc-el-body { flex: 1; min-width: 0; }
      .stc-el-title { font-size: 12.5px; font-weight: 600; color: var(--text); display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
      .stc-el-src { font-size: 10px; font-weight: 600; padding: 1px 7px; border-radius: var(--radius-pill); background: var(--accent-soft); color: var(--accent); border: 1px solid var(--accent-border); white-space: nowrap; }
      .stc-el-src-auto { background: var(--info-soft); color: var(--info-text); border-color: var(--info-soft); }
      .stc-el-src-off { background: var(--danger-soft); color: var(--danger-text); border-color: var(--danger); }
      .stc-el-clear { font-size: 11px; font-weight: 500; }
      .stc-el-tight { font-size: 11.5px; color: var(--warning-text); margin-top: 2px; }
      .stc-el-time { width: 74px; flex: none; padding: 5px 8px; font-size: 12px; }
      @media (max-width: 900px) {
        .stc-page { --stc-label-w: 170px; }
        .stc-kpis { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; gap: 10px; }
        .stc-kpis .kpi-card { padding: 14px 16px; }
        .stc-kpis .kpi-card-value { font-size: 24px; }
        .stc-panel { width: 100vw; border-left: none; }
        .stc-scroller { max-height: calc(100dvh - 200px); min-height: 320px; }   /* R115: the grid starts near the top; it fills the screen below the toolbar (no scroll trap) */
        .stc-search { width: 100%; }
        .stc-spacer { display: none; }
        .stc-filters { flex-wrap: nowrap; overflow-x: auto; -webkit-overflow-scrolling: touch; padding-bottom: 4px; margin-left: -16px; margin-right: -16px; padding-left: 16px; padding-right: 16px; }
        .stc-filters .chip { flex: none; }
        .stc-pills { max-width: 52%; }
        .stc-modal { max-height: calc(100vh - 16px); }
        .stc-ovl { padding: 8px; align-items: flex-end; }
      }
      @media (max-width: 620px) {
        .stc-page { --stc-label-w: 118px; }
        .stc-lbl .stc-pav { display: none; }
        .stc-lbl { padding: 0 8px; }
        .stc-meter-bar { display: none; }
        .stc-lbl { padding: 0 10px; gap: 8px; }
        .stc-field-2 { grid-template-columns: 1fr; }
        .stc-ci { flex-wrap: wrap; }
        .stc-ci-acts { width: 100%; justify-content: flex-end; padding-left: 16px; }
        .stc-el-row { flex-wrap: wrap; }
        .stc-el-time { flex: 1 1 100%; margin-left: 46px; }   /* its own line under the title (basis 100%, not flex:1's 0) */
        .stc-pop-warn-acts, .stc-inq-acts { flex-direction: column; }
      }`;
  }
}
